import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { X, Calendar, Loader2, Search, ArrowLeftRight, ChevronDown } from 'lucide-react';
// Removed unused Recharts imports
// Removed unused imports
import { api } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { useProfile } from '../../contexts/ProfileContext';
import { getBrokerSummaryBlobPath, parseBrokerSummaryCSV, fetchBrokerSummaryCsv } from '../../utils/brokerSummaryBlob';
import { loadOhlcByPath } from '../../services/duckdbService';
import { getOhlcBlobPath, primeStockToSectorMap } from '../../utils/sectorMappingCache';
import { fetchSectorMapping, fetchStockSymbols, getEmitenShares } from '../../utils/stockMetadataBlob';
import { listBrokerInventoryBrokers } from '../../utils/brokerInventoryBlob';
import {
  createChart,
  ColorType,
  CrosshairMode,
  LineSeries,
  CandlestickSeries,
  HistogramSeries,
  type IChartApi,
} from 'lightweight-charts';
import { useUserChartColors } from '../../hooks/useUserChartColors';

// Removed unused interface

interface InventoryTimeSeries {
  time: string;
  [brokerCode: string]: string | number;
}

// Available tickers (now using API data)
// const AVAILABLE_TICKERS = [
//   'BBCA', 'BBRI', 'BMRI', 'BBNI', 'ARTO', 'BACA', 'TLKM', 'ISAT', 'FREN', 'EXCL',
//   'ASII', 'GOTO', 'ANTM', 'MDKA', 'ADRO', 'UNVR', 'ICBP', 'INDF', 'PGAS', 'MEDC',
//   'CPIN', 'JPFA', 'INCO', 'TPIA', 'TKIM', 'INKP', 'BRIS', 'SIDO', 'ERAA', 'ESSA'
// ];

// Import broker color utilities
// Note: BROKER_COLORS is available but not used in this component - using dynamic color generation instead

// Foreign brokers (red text)
// Foreign brokers (red text)
const FOREIGN_BROKERS = [
  "AG", "AH", "AI", "AK", "BK", "BQ", "CG", "CS", "DP", "DR", "DU", "FS", "GW", "HD", "KK",
  "KZ", "LH", "LG", "LS", "MS", "RB", "RX", "TX", "YP", "YU", "ZP"
];

// Government brokers (green text)
const GOVERNMENT_BROKERS = ['CC', 'NI', 'OD', 'DX'];

// Helper function to get broker color class based on type (for text color)
const getBrokerColorClass = (brokerCode: string): { color: string; className: string } => {
  if (GOVERNMENT_BROKERS.includes(brokerCode)) {
    return { color: '#10B981', className: 'font-semibold' }; // Green-600
  }
  if (FOREIGN_BROKERS.includes(brokerCode)) {
    return { color: '#EF4444', className: 'font-semibold' }; // Red-600
  }
  return { color: '#FFFFFF', className: 'font-semibold' }; // White
};

// Top 5 Buyer Colors: Red, Amber-Yellow, Deep Green, Blue, Fuchsia
const TOP_BUYER_COLORS = ['#FF0000', '#FACC15', '#16A34A', '#48a2f7ff', '#cc5c16ff'];
const TOP_TEKTOK_COLORS = ['#72647fff', '#6708ecff', '#0b00e4ff', '#FF00FF', '#FFFFFF']; // Purple, Pink, Brown, Lime, White

// Custom Broker Colors: Bright, distinct colors for dark mode.
// Condensed to 13 maximally distinct hues to prevent any visual overlapping.
const CUSTOM_BROKER_COLORS = [
  '#00FFFF', // 0: Cyan
  '#FF1493', // 1: Deep Pink
  '#7CFC00', // 2: Lawn Green (Yellow-Green)
  '#FF8C00', // 3: Dark Orange
  '#00BFFF', // 4: Deep Sky Blue
  '#DA70D6', // 5: Orchid (Soft Purple)
  '#FFFF00', // 6: Yellow
  '#FF6347', // 7: Tomato (Coral Red)
  '#00FA9A', // 8: Medium Spring Green (Mint)
  '#DDA0DD', // 9: Plum (Pale Purple)
  '#FFB6C1', // 10: Light Pink
  '#F0E68C', // 11: Khaki (Pale Yellow)
  '#87CEFA', // 12: Light Sky Blue
];

// Helper to validate broker codes (2 letters or "ALL")
const isValidBrokerCode = (code: string): boolean => {
  if (!code) return false;
  const upper = code.toUpperCase();
  if (upper === 'ALL') return true;
  return /^[A-Z]{2}$/.test(upper);
};

// Dynamic color generator based on loaded brokers (for chart colors, not text)
const generateBrokerColor = (broker: string | undefined | null, allBrokers: string[] = [], topBuyers: string[] = [], topSellers: string[] = [], topTektoks: string[] = []): string => {
  // Handle undefined/null broker
  if (!broker || typeof broker !== 'string') {
    return 'hsl(0, 0%, 50%)'; // Return gray color for invalid broker
  }

  // Check if broker is in Top 5 Buyers
  const topBuyerIndex = topBuyers.indexOf(broker);
  if (topBuyerIndex !== -1 && topBuyerIndex < TOP_BUYER_COLORS.length) {
    return TOP_BUYER_COLORS[topBuyerIndex] as string;
  }

  // Check if broker is in Top 5 Sellers - use the same color palette as buyers
  const topSellerIndex = topSellers.indexOf(broker);
  if (topSellerIndex !== -1 && topSellerIndex < TOP_BUYER_COLORS.length) {
    return TOP_BUYER_COLORS[topSellerIndex] as string;
  }

  // Check if broker is in Top TekToks - use a unique color palette
  const topTektokIndex = topTektoks.indexOf(broker);
  if (topTektokIndex !== -1 && topTektokIndex < TOP_TEKTOK_COLORS.length) {
    return TOP_TEKTOK_COLORS[topTektokIndex] as string;
  }

  // Get all unique brokers and sort them for consistent color assignment
  const sortedBrokers = [...new Set(allBrokers)].sort();
  const brokerIndex = sortedBrokers.indexOf(broker);

  if (brokerIndex === -1) {
    // Fallback for unknown brokers using a robust string hash
    let hash = 0;
    for (let i = 0; i < broker.length; i++) {
      hash = (hash * 31 + broker.charCodeAt(i)) >>> 0;
    }
    const index = hash % CUSTOM_BROKER_COLORS.length;
    return CUSTOM_BROKER_COLORS[index] ?? '#FFFFFF';
  }

  // Use a prime multiplier (5) coprime to the array length (13)
  // This guarantees that adjacent brokers jump wildly across the array
  // (e.g. index 0 -> Cyan, index 1 -> Orchid, index 2 -> Light Pink, index 3 -> Lawn Green)
  const colorIndex = (brokerIndex * 5) % CUSTOM_BROKER_COLORS.length;
  return CUSTOM_BROKER_COLORS[colorIndex] ?? '#FFFFFF';
};

// Format lot numbers with K/M/B/T prefixes
const formatLotNumber = (value: number): string => {
  if (value === 0) return '0';

  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (absValue >= 1000000000000) {
    return `${sign}${(absValue / 1000000000000).toFixed(1)}T`;
  } else if (absValue >= 1000000000) {
    return `${sign}${(absValue / 1000000000).toFixed(1)}B`;
  } else if (absValue >= 1000000) {
    return `${sign}${(absValue / 1000000).toFixed(1)}M`;
  } else if (absValue >= 1000) {
    return `${sign}${(absValue / 1000).toFixed(1)}K`;
  } else {
    return `${sign}${absValue.toFixed(0)}`;
  }
};

// Generate broker gross/net data for horizontal bar chart
// Removed unused data generation functions to improve performance

// Removed generateTopBrokersData - now using real data from API

// Page ID for menu preferences
const PAGE_ID = 'broker-activity-inventory';

// Interface for user preferences
interface UserPreferences {
  selectedTicker: string;
  selectedBrokers: string[];
  fdFilter: 'All' | 'Foreign' | 'Domestic';
  marketFilter: 'RG' | 'TN' | 'NG' | '';
  brokerSelectionMode: {
    top5buy: boolean;
    top5sell: boolean;
    top5tektok: boolean;
    custom: boolean;
  };
  startDate?: string;
  endDate?: string;
  brokerVisibility?: Record<string, boolean>;
  manuallySelectedBrokers?: string[];
  hasUserSelectedBrokers?: boolean;
  splitVisualization?: boolean;
  showTooltipDetails?: boolean;
  chartLogicalRange?: { from: number; to: number };
}

// Import menu preferences service
import { menuPreferencesService } from '../../services/menuPreferences';

// Utility functions for saving/loading preferences (now using cookies)
const loadPreferences = (pageId: string): Partial<UserPreferences> | null => {
  try {
    const cached = menuPreferencesService.getCachedPreferences(pageId);
    if (cached) {
      return cached as Partial<UserPreferences>;
    }
    const prefs = menuPreferencesService.loadPreferences(pageId);
    return prefs ? prefs as Partial<UserPreferences> : null;
  } catch (error) {
    console.warn('Failed to load cached preferences:', error);
  }
  return null;
};

const savePreferences = (pageId: string, prefs: Partial<UserPreferences>) => {
  menuPreferencesService.savePreferences(pageId, prefs);
};


const BrokerLegend = ({
  title,
  brokers,
  colorReferenceBrokers: _colorReferenceBrokers,
  brokerVisibility,
  onToggleVisibility,
  onRemoveBroker,
  onRemoveAll,
  topBuyers = [],
  topSellers = [],
  topTektoks: _topTektoks = [],
  brokerMetadata,
  tektokBrokerNames = [],
  brokerColorMap
}: {
  title: string;
  brokers: string[];
  colorReferenceBrokers: string[];
  brokerVisibility: Record<string, boolean>;
  onToggleVisibility: (broker: string) => void;
  onRemoveBroker?: ((broker: string) => void) | undefined;
  onRemoveAll?: (() => void) | undefined;
  topBuyers?: string[];
  topSellers?: string[];
  topTektoks?: { broker: string; vol: number; algos?: string }[];
  brokerMetadata?: Record<string, { tooltip?: string; netStatus?: string }>;
  tektokBrokerNames?: string[]; // names of tektok brokers to show badge on buy/sell panels
  brokerColorMap?: Map<string, string>;
}) => {
  const isTektok = title.toLowerCase().includes('tektok');
  const [hoveredBroker, setHoveredBroker] = useState<string | null>(null);

  // For tektok panel: exclude brokers that are already in top buyers or sellers
  const displayBrokers = isTektok
    ? brokers.filter(b => !topBuyers.includes(b) && !topSellers.includes(b))
    : brokers;

  // Check if all brokers are visible
  const allVisible = displayBrokers.length > 0 && displayBrokers.every(broker => brokerVisibility[broker] !== false);
  const someVisible = displayBrokers.some(broker => brokerVisibility[broker] !== false);

  // Toggle all visibility
  const handleToggleAll = () => {
    displayBrokers.forEach(broker => {
      const isVisible = brokerVisibility[broker] !== false;
      if (allVisible) {
        if (isVisible) onToggleVisibility(broker);
      } else {
        if (!isVisible) onToggleVisibility(broker);
      }
    });
  };

  return (
    <div className="bg-background border border-border/40 px-2 pb-2 pt-1 rounded-none relative z-10 w-full">
      <div className="flex items-center justify-between gap-2 pt-0.5 pb-0.5">
        <div className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {title}
        </div>
        <div className="flex items-center gap-2">
          {onRemoveAll && displayBrokers.length > 0 && (
            <button
              type="button"
              onClick={onRemoveAll}
              className="text-muted-foreground hover:text-destructive transition-colors"
              aria-label={`Remove all ${title}`}
              title={`Remove all ${title}`}
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      {displayBrokers.length > 0 && (
        <div className="mb-1">
          <label className="flex items-center gap-1 text-[12px] font-medium cursor-pointer select-none text-muted-foreground hover:text-foreground transition-colors">
            <input
              type="checkbox"
              className="h-3.5 w-3.5 rounded-none border-border/70 bg-transparent text-primary focus:ring-primary"
              checked={allVisible}
              ref={(input) => {
                if (input) input.indeterminate = someVisible && !allVisible;
              }}
              onChange={handleToggleAll}
            />
            <span>{allVisible ? 'Unselect All' : 'Select All'}</span>
          </label>
        </div>
      )}
      <div className={`space-y-0.5 pr-0 ${isTektok ? '' : ''}`}>
        {displayBrokers.length === 0 ? (
          <p className="text-xs text-muted-foreground leading-relaxed max-w-[160px]">
            {isTektok ? 'All tektok brokers are in the buy/sell list.' : 'No brokers in this group.'}
          </p>
        ) : (
          displayBrokers.map((broker) => {
            const isVisible = brokerVisibility[broker] !== false;
            const isTektokBroker = tektokBrokerNames.includes(broker);
            return (
                <div key={broker} className="flex items-center justify-between gap-3 group relative hover:z-[100] py-0 whitespace-nowrap">
                  <div className="flex items-center gap-1.5 shrink-0">
                    <label className="flex items-center gap-1.5 text-[12px] font-medium cursor-pointer select-none">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded-none border-border/70 bg-transparent text-primary focus:ring-primary"
                        checked={isVisible}
                        onChange={() => onToggleVisibility(broker)}
                      />
                      <div className="flex items-center gap-1.5">
                        {topSellers.includes(broker) || brokerMetadata?.[broker]?.netStatus === 'Net Sell' ? (
                          <div className="w-3 h-3 flex items-center justify-center shrink-0">
                            <div 
                              className="w-full border-t-2 border-dotted" 
                            style={{ borderColor: brokerColorMap?.get(broker) ?? '#ffffff' }} 
                            />
                          </div>
                        ) : (
                          <div
                            className="w-3 h-3 shrink-0"
                            style={{ 
                              backgroundColor: brokerColorMap?.get(broker) ?? '#ffffff'
                            }}
                          />
                        )}

                        <span className="shrink-0 tabular-nums">{broker}</span>
                        
                        {/* Tektok marker â€” positioned in a fixed column for table-like alignment */}
                        <div className="w-4 flex items-center justify-center">
                          {(isTektok || isTektokBroker) && (
                            <ArrowLeftRight 
                              className="w-3 h-3 text-yellow-400 hover:text-primary cursor-help transition-colors" 
                              onMouseEnter={() => setHoveredBroker(broker)}
                              onMouseLeave={() => setHoveredBroker(null)}
                            />
                          )}
                        </div>
                      </div>
                    </label>
                  </div>

                  {onRemoveBroker && (
                    <div className="flex items-center shrink-0">
                      <button
                        type="button"
                        onClick={() => onRemoveBroker(broker)}
                        className="text-muted-foreground hover:text-destructive transition-colors"
                        aria-label={`Remove ${broker}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>
              );
          })
        )}
      </div>

      {/* Global Tooltip Overlay (Outside scroll box to prevent clipping) */}
      {hoveredBroker && brokerMetadata?.[hoveredBroker]?.tooltip && (
        <div 
          className="absolute z-[9999] right-full mr-3 top-0 w-56 px-3 py-2 bg-popover text-popover-foreground text-[10px] rounded border border-border shadow-2xl pointer-events-none whitespace-normal leading-tight backdrop-blur-md bg-opacity-95 border-l-4 border-l-primary"
        >
          <div className="font-bold mb-1 border-b border-border pb-1 flex items-center justify-between gap-2">
            <div className="flex items-center gap-1 min-w-0">
              <ArrowLeftRight className="w-3 h-3 text-yellow-400 shrink-0" />
              <span className="truncate">Tektok Info: {hoveredBroker}</span>
            </div>
            {brokerMetadata[hoveredBroker].netStatus && (
              <span className={`shrink-0 font-semibold px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider ${
                brokerMetadata[hoveredBroker].netStatus === 'Net Buy' 
                  ? 'bg-green-500/20 text-green-500' 
                  : brokerMetadata[hoveredBroker].netStatus === 'Net Sell'
                  ? 'bg-red-500/20 text-red-500'
                  : 'bg-muted text-muted-foreground'
              }`}>
                {brokerMetadata[hoveredBroker].netStatus}
              </span>
            )}
          </div>
          <div className="opacity-90 mt-1.5">
            This broker is identified as tektok by:
            <ul className="mt-1 ml-1 list-disc list-inside space-y-0.5">
              {brokerMetadata[hoveredBroker].tooltip.split(', ').map((algo, i) => (
                <li key={i} className="inline-block w-full">
                  <span className="mr-1">â€¢</span>
                  <strong className="font-bold">{algo}</strong>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

// Calculate contrast text color (black or white) based on background hex color
const getContrastTextColor = (hexColor: string): string => {
  if (!hexColor || hexColor === '#ffffff') return '#000000';
  let hex = hexColor.replace('#', '');
  if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
  if (hex.length !== 6) return '#ffffff';
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 128 ? '#000000' : '#ffffff';
};

export interface TradingViewChartProps {
  candlestickData?: any[];
  inventoryData?: any[];
  selectedBrokers?: string[];
  displayBrokers?: string[];
  title?: string;
  className?: string;
  volumeData?: any[];
  ticker?: string;
  topBuyers?: any[];
  topSellers?: any[];
  topTektoks?: any[];
  showDetails?: boolean;
  brokerSummaryData?: any[];
  brokerColorMap?: Map<string, string>;
  logicalRangeRef?: React.MutableRefObject<{ from: number; to: number } | null>;
  tailBadgeOffset?: number;
}

// TradingView-style chart component with dual Y-axis (Standalone modular component)
export const TradingViewChart: React.FC<TradingViewChartProps> = ({
  candlestickData = [],
  inventoryData = [],
  selectedBrokers = [],
  displayBrokers,
  title,
  className,
  volumeData = [],
  ticker = '',
  topBuyers = [],
  topSellers = [],
  topTektoks = [],
  showDetails = true,
  brokerSummaryData = [],
  brokerColorMap,
  logicalRangeRef,
  tailBadgeOffset = 54
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const tailBadgeContainerRef = useRef<HTMLDivElement>(null);
  const userColors = useUserChartColors();
  const showDetailsRef = useRef(showDetails ?? true);
  const prevTickerRef = useRef<string | undefined>(ticker);
  const internalLogicalRangeRef = useRef<{ from: number; to: number } | null>(null);
  const activeLogicalRangeRef = logicalRangeRef || internalLogicalRangeRef;
  const isInitializingRef = useRef(false);

  // Keep ref in sync with state so callbacks always have the latest value
  useEffect(() => {
    showDetailsRef.current = showDetails ?? true;
    // If toggled off, immediately hide tooltip
    if (!(showDetails ?? true) && tooltipRef.current) {
      tooltipRef.current.style.display = 'none';
    }
  }, [showDetails]);

  // Get theme-aware colors
  const getThemeColors = () => {
    const isDark = document.documentElement.classList.contains('dark');
    return {
      textColor: isDark ? '#f9fafb' : '#111827',
      gridColor: isDark ? '#4b5563' : '#e5e7eb',
      borderColor: isDark ? '#6b7280' : '#d1d5db',
      axisTextColor: isDark ? '#d1d5db' : '#6b7280'
    };
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const isSameTicker = prevTickerRef.current === ticker;
    isInitializingRef.current = true;
    prevTickerRef.current = ticker;

    if (chartRef.current) {
      try {
        const liveRange = chartRef.current.timeScale().getVisibleLogicalRange();
        if (liveRange && typeof liveRange.from === 'number' && typeof liveRange.to === 'number') {
          activeLogicalRangeRef.current = liveRange;
        }
      } catch (e) {
        console.error('Error getting visible logical range:', e);
      }
      console.log(`🧹 TradingViewChart: Removing existing chart and all series before re-render`, { currentRange: activeLogicalRangeRef.current });
      try {
        chartRef.current.remove();
      } catch (e) {
        console.error('Error removing chart:', e);
      }
      chartRef.current = null;
    }

    // Always render candlestick & volume series even if brokersToDisplay is temporarily empty
    const brokersToDisplay = Array.isArray(displayBrokers) && displayBrokers.length > 0 ? displayBrokers : [];

    const rect = el.getBoundingClientRect();
    const width = Math.max(1, Math.floor(rect.width || el.clientWidth || el.parentElement?.clientWidth || 800));
    const height = Math.max(1, Math.floor(rect.height || el.clientHeight || el.parentElement?.clientHeight || 400));
    const colors = getThemeColors();

    console.log(`[DEBUG Chart] TradingViewChart useEffect triggered | ticker: "${ticker}" | isSameTicker: ${isSameTicker} | rect: ${rect.width}x${rect.height} | computed width: ${width}, height: ${height} | savedRange:`, activeLogicalRangeRef.current);

    chartRef.current = createChart(el, {
      width,
      height,
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: colors.axisTextColor
      },
      grid: {
        horzLines: { visible: false },
        vertLines: { visible: false }
      },
      rightPriceScale: {
        visible: true,
        borderColor: colors.borderColor,
        scaleMargins: { top: 0.1, bottom: 0.1 }
      },
      leftPriceScale: {
        visible: true, // Show left price scale
        borderColor: colors.borderColor,
        scaleMargins: { top: 0.1, bottom: 0.1 }
      },
      timeScale: {
        borderColor: colors.borderColor,
        timeVisible: true,
        secondsVisible: false,
        tickMarkFormatter: (time: any) => {
          let date: Date;
          if (typeof time === 'string') {
            date = new Date(time);
          } else {
            date = new Date(time * 1000);
          }
          const day = date.getDate();
          const month = date.toLocaleDateString('en-US', { month: 'short' });
          return `${day} ${month}`;
        }
      },
      crosshair: { mode: CrosshairMode.Normal },
      handleScroll: true,
      handleScale: true,
    });

    const chart = chartRef.current!;

    try {
      // brokersToDisplay already calculated in useEffect above
      // This is just for logging
      console.log(`📈 TradingViewChart: Rendering chart`, {
        displayBrokers,
        brokersToDisplay,
        brokersToDisplayLength: brokersToDisplay.length,
        selectedBrokersLength: selectedBrokers.length
      });

      // If no brokers to display, log it but continue to add candlestick and volume
      if (brokersToDisplay.length === 0 && selectedBrokers.length > 0) {
        console.log(`⚠️ TradingViewChart: No visible brokers to display (displayBrokers=${JSON.stringify(displayBrokers)}, brokersToDisplay.length=${brokersToDisplay.length}), skipping ALL broker series`);
      }



      // Pre-calculate cumulative data (Running Sum) for all valid/displayed brokers
      const cumulativeInventoryData: any[] = [];
      let runningSums: Record<string, number> = {};
      brokersToDisplay.forEach(b => runningSums[b] = 0);

      inventoryData.forEach(day => {
        const row: any = { time: day.time };
        brokersToDisplay.forEach(broker => {
          const dailyNet = (day[broker] as number) || 0;
          const currentSum = runningSums[broker] ?? 0;
          const newSum = currentSum + dailyNet;
          runningSums[broker] = newSum;
          row[broker] = newSum / 100;
        });
        cumulativeInventoryData.push(row);
      });

      const brokerDottedMap = new Map<string, boolean>();
      brokersToDisplay.forEach(broker => {
        let isNetSell = false;
        if (cumulativeInventoryData.length > 0) {
          isNetSell = ((cumulativeInventoryData[cumulativeInventoryData.length - 1][broker] as number) ?? 0) < 0;
        }
        brokerDottedMap.set(broker, isNetSell);
      });

      // 1. Add Candlestick series (Pane 0)
      const candlestickSeries = chart.addSeries(CandlestickSeries, {
        upColor: userColors.bullish,
        downColor: userColors.bearish,
        borderVisible: false,
        wickUpColor: userColors.bullish,
        wickDownColor: userColors.bearish,
        priceScaleId: 'right',
        priceFormat: {
          type: 'custom',
          formatter: (price: number) => price.toLocaleString('id-ID', { maximumFractionDigits: 0 }),
        }
      }, 0);

      // 2. Add all Broker Line series (Pane 0)
      let firstBrokerSeries: any = null;
      const brokerSeriesPairs: { broker: string; color: string; series: any; data: any[]; priceLine: any; lastVal: number }[] = [];

      if (brokersToDisplay.length > 0) {
        brokersToDisplay.forEach(broker => {
          const brokerColor = brokerColorMap?.get(broker) ?? '#ffffff';
          const isDotted = brokerDottedMap.get(broker) ?? false;
          const brokerData = cumulativeInventoryData.map(d => ({
            time: d.time,
            value: d[broker] as number,
          })).filter(d => d.value !== undefined && d.value !== null && !isNaN(d.value) && isFinite(d.value));

          if (brokerData.length > 0) {
            const lastVal = brokerData[brokerData.length - 1]?.value ?? 0;
            const lineSeries = chart.addSeries(LineSeries, {
              color: brokerColor,
              lineWidth: 2,
              lineStyle: isDotted ? 1 : 0,
              title: '', // empty title so broker code label is NOT attached to price scale badge
              priceScaleId: 'left',
              priceFormat: {
                type: 'custom',
                formatter: (price: number) => formatLotNumber(price),
              },
              lastValueVisible: false,
              priceLineVisible: false,
            }, 0);

            const priceLine = lineSeries.createPriceLine({
              price: lastVal,
              color: brokerColor,
              lineWidth: 1,
              lineVisible: false, // Hide horizontal line across chart canvas
              axisLabelVisible: true,
              title: '',
            });

            brokerSeriesPairs.push({ broker, color: brokerColor, series: lineSeries, data: brokerData, priceLine, lastVal });
            if (!firstBrokerSeries) firstBrokerSeries = lineSeries;
          }
        });

        if (firstBrokerSeries) {
          firstBrokerSeries.createPriceLine({
            price: 0,
            color: '#6b7280',
            lineWidth: 1,
            lineStyle: 0,
            axisLabelVisible: false,
            title: '',
          });
        }
      }

      // Function to render broker code badges at the exact right tail tip of each line plot
      const updateTailBadges = () => {
        const container = tailBadgeContainerRef.current;
        if (!container || !chartRef.current) return;

        const timeScale = chartRef.current.timeScale();
        const containerWidth = container.clientWidth || 800;
        const containerHeight = container.clientHeight || 600;

        // Clip container strictly within plot pane (excluding left and right price scales)
        const rightScaleWidth = chartRef.current.priceScale('right')?.width() || 55;
        const leftScaleWidth = chartRef.current.priceScale('left')?.width() || 60;
        container.style.clipPath = `inset(0px ${rightScaleWidth}px 0px ${leftScaleWidth}px)`;

        const badgesData: Array<{
          broker: string;
          color: string;
          x: number;
          y: number;
        }> = [];

        brokerSeriesPairs.forEach(pair => {
          if (!pair.data || pair.data.length === 0) return;
          const lastPoint = pair.data[pair.data.length - 1];
          const yCoord = pair.series.priceToCoordinate(lastPoint.value);
          const xCoord = timeScale.timeToCoordinate(lastPoint.time);

          if (
            yCoord !== null &&
            yCoord >= 0 &&
            yCoord <= containerHeight &&
            xCoord !== null &&
            xCoord >= 0 &&
            xCoord <= containerWidth
          ) {
            badgesData.push({
              broker: pair.broker,
              color: pair.color,
              x: xCoord,
              y: yCoord,
            });
          }
        });

        if (badgesData.length === 0) {
          container.innerHTML = '';
          return;
        }

        // Slight local adjustment only if two badges have almost identical coordinates
        badgesData.sort((a, b) => a.y - b.y);
        for (let i = 1; i < badgesData.length; i++) {
          if (Math.abs(badgesData[i].x - badgesData[i - 1].x) < 30 && badgesData[i].y - badgesData[i - 1].y < 12) {
            badgesData[i].y = badgesData[i - 1].y + 12;
          }
        }

        container.innerHTML = '';
        badgesData.forEach(b => {
          const badgeEl = document.createElement('div');
          badgeEl.className = 'absolute font-bold text-[11.8px] px-1.5 py-0.5 rounded-none shadow-sm flex items-center justify-center leading-none select-none pointer-events-none transition-transform duration-75 z-10';
          const textColor = getContrastTextColor(b.color);
          badgeEl.style.backgroundColor = b.color;
          badgeEl.style.color = textColor;
          badgeEl.innerText = b.broker;

          badgeEl.style.left = `${b.x + tailBadgeOffset}px`;
          badgeEl.style.top = `${b.y}px`;
          badgeEl.style.transform = 'translateY(-50%)';

          container.appendChild(badgeEl);
        });
      };

      // 3. Add Volume series (Pane 1)
      let volumeSeries: any = null;
      let formattedVolumeData: any[] = [];
      if (volumeData && volumeData.length > 0) {
        volumeSeries = chart.addSeries(HistogramSeries, {
          color: '#26a69a',
          priceFormat: { type: 'volume' },
          priceScaleId: 'right',
        }, 1);

        const upMap: Record<string, boolean> = {};
        candlestickData.forEach((c: any) => {
          upMap[c.time] = (c.close ?? 0) >= (c.open ?? 0);
        });

        formattedVolumeData = volumeData.map(d => {
          const up = upMap[d.time] ?? ((d.value ?? 0) >= 0);
          return {
            time: d.time,
            value: (d.value ?? 0) / 100,
            color: up ? userColors.bullish : userColors.bearish,
          };
        });
      }

      // 4. POPULATE DATA ON ALL SERIES IN BATCH BEFORE TIMESCALE CALCULATION
      candlestickSeries.setData(candlestickData.map(d => ({
        time: d.time, open: d.open, high: d.high, low: d.low, close: d.close,
      })));

      brokerSeriesPairs.forEach(pair => pair.series.setData(pair.data));

      if (volumeSeries && formattedVolumeData.length > 0) {
        volumeSeries.setData(formattedVolumeData);

        const volumePane = chart.panes()[1];
        if (volumePane) {
          const volumeHeight = Math.floor(height * 0.25);
          volumePane.setHeight(volumeHeight);
          const pricePane = chart.panes()[0];
          if (pricePane) {
            pricePane.setHeight(Math.floor(height * 0.75));
          }
        }
      }

      if (activeLogicalRangeRef.current && typeof activeLogicalRangeRef.current.from === 'number' && typeof activeLogicalRangeRef.current.to === 'number') {
        try {
          const prevRange = activeLogicalRangeRef.current;
          const rangeSpan = Math.max(10, prevRange.to - prevRange.from);
          const newLength = candlestickData ? candlestickData.length : 0;
          let targetRange = prevRange;
          if (newLength > 0 && (prevRange.to > newLength + 20 || prevRange.from < -20 || prevRange.to <= 0)) {
            targetRange = {
              from: Math.max(0, newLength - rangeSpan),
              to: newLength
            };
            activeLogicalRangeRef.current = targetRange;
          }
          console.log(`[DEBUG Chart] TradingViewChart restoring range:`, targetRange);
          chart.timeScale().setVisibleLogicalRange(targetRange);
        } catch (e) {
          console.warn('Could not restore logical range, fitting content:', e);
          chart.timeScale().fitContent();
        }
      } else {
        console.log(`[DEBUG Chart] TradingViewChart calling fitContent() (initial load or ticker changed)`);
        chart.timeScale().fitContent();
      }

      const handleRangeChange = (newRange: any) => {
        if (isInitializingRef.current) return;
        if (newRange && typeof newRange.from === 'number' && typeof newRange.to === 'number') {
          activeLogicalRangeRef.current = newRange;
        }
        updateTailBadges();
      };
      chart.timeScale().subscribeVisibleLogicalRangeChange(handleRangeChange);
      (chart as any)._rangeUnsubscribe = () => {
        try {
          chart.timeScale().unsubscribeVisibleLogicalRangeChange(handleRangeChange);
        } catch (e) {}
      };

      requestAnimationFrame(() => {
        isInitializingRef.current = false;
        updateTailBadges();
      });

      // PRE-COMPUTE cumPrice per broker per date (Progressive Cumulative Price).
      // Instead of a single total for the whole range, we accumulate day by day
      // so the chart tooltip can show the cumulative price UP TO the hovered date.
      const cumPriceMapByDate = new Map<string, Map<string, number>>();
      
      if (brokerSummaryData && brokerSummaryData.length > 0) {
        // 1. Group records by date
        const recordsByDate = new Map<string, any[]>();
        brokerSummaryData.forEach((b: any) => {
          const date = b.date ?? b.time ?? '';
          if (!date) return;
          if (!recordsByDate.has(date)) recordsByDate.set(date, []);
          recordsByDate.get(date)!.push(b);
        });

        // 2. Sort dates chronologically
        const sortedDates = Array.from(recordsByDate.keys()).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

        // 3. Accumulate and compute cumPrice progressively
        const brokerTotals: Record<string, { nbvol: number; nbval: number; nsvol: number; nsval: number }> = {};

        sortedDates.forEach(date => {
          const dailyRecords = recordsByDate.get(date)!;
          
          dailyRecords.forEach(b => {
            const brokerCode: string = b.broker ?? b.BrokerCode ?? '';
            if (!brokerCode) return;

            const netBuyVol  = Number(b.NetBuyVol    ?? b.netBuyVol    ?? 0);
            const netBuyVal  = Number(b.NetBuyValue  ?? b.netBuyValue  ?? 0);
            const netSellVol = Number(b.NetSellVol   ?? b.netSellVol   ?? 0);
            const netSellVal = Number(b.NetSellValue ?? b.netSellValue ?? 0);

            if (!brokerTotals[brokerCode]) brokerTotals[brokerCode] = { nbvol: 0, nbval: 0, nsvol: 0, nsval: 0 };
            const entry = brokerTotals[brokerCode]!;

            if (netBuyVol > 0 || netBuyVal > 0) {
              entry.nbvol += netBuyVol;
              entry.nbval += netBuyVal;
            }
            if (netSellVol > 0 || netSellVal > 0) {
              entry.nsvol += netSellVol;
              entry.nsval += netSellVal;
            }
          });

          // Compute cumPrice for all brokers up to this date
          const dailyCumPriceMap = new Map<string, number>();
          Object.entries(brokerTotals).forEach(([brokerCode, totals]) => {
            const netValue = totals.nbval - totals.nsval;
            const netLot   = totals.nbvol - totals.nsvol;
            if (Math.abs(netLot) > 0) {
              dailyCumPriceMap.set(brokerCode, Math.abs(netValue) / Math.abs(netLot));
            }
          });
          
          cumPriceMapByDate.set(date, dailyCumPriceMap);
        });
      }

      // Subscribe to crosshair move for hover annotation & left axis updates
      const unsubscribeCrosshair = chart.subscribeCrosshairMove((param) => {
        // Requirement 1: Update left axis price lines to follow hovered date values
        if (param.point !== undefined && param.time && cumulativeInventoryData.length > 0) {
          let timeStr: string = '';
          if (typeof param.time === 'string') {
            timeStr = param.time;
          } else if (typeof param.time === 'number') {
            const date = new Date(param.time * 1000);
            timeStr = date.toISOString().split('T')[0] || '';
          }

          const hoverRow = cumulativeInventoryData.find((d: any) => {
            const dTime = typeof d.time === 'string' ? d.time : new Date(d.time * 1000).toISOString().split('T')[0];
            return dTime === timeStr;
          });

          if (hoverRow) {
            brokerSeriesPairs.forEach(pair => {
              const val = hoverRow[pair.broker];
              if (typeof val === 'number' && !isNaN(val)) {
                pair.priceLine.applyOptions({ price: val });
              }
            });
          }
        } else {
          // Revert to last values when crosshair leaves chart
          brokerSeriesPairs.forEach(pair => {
            pair.priceLine.applyOptions({ price: pair.lastVal });
          });
        }

        // Requirement 2: Keep tail badges position synced
        updateTailBadges();

        const tooltip = tooltipRef.current;
        if (!tooltip) return;

        if (param.point === undefined || !param.time || param.point.x < 0 || param.point.y < 0) {
          tooltip.style.display = 'none';
          return;
        }

        // If Show Details is off, hide tooltip and exit
        if (!showDetailsRef.current) {
          tooltip.style.display = 'none';
          return;
        }

        // Convert time to string format for matching
        let timeStr: string = '';
        if (typeof param.time === 'string') {
          timeStr = param.time;
        } else if (typeof param.time === 'number') {
          const date = new Date(param.time * 1000);
          const isoStr = date.toISOString().split('T')[0];
          timeStr = isoStr ? isoStr : '';
        }

        // Get candlestick data at hover time
        const candleData = candlestickData.find((d: any) => {
          const dTime = typeof d.time === 'string' ? d.time : new Date(d.time * 1000).toISOString().split('T')[0];
          return dTime === timeStr;
        });

        // Get broker data at hover time - USE cumulativeInventoryData for consistency
        const brokerValues: Array<{ 
          broker: string; 
          value: number; 
          color: string; 
          summaryColor: string; 
          changeAbsolute: number | null; 
          avgPrice: number | null; 
          cumPrice: number | null;
          isDotted: boolean;
        }> = [];
        if (cumulativeInventoryData && displayBrokers && displayBrokers.length > 0 && timeStr) {
          const currentIndex = cumulativeInventoryData.findIndex((d: any) => {
            const dTime = typeof d.time === 'string' ? d.time : new Date(d.time * 1000).toISOString().split('T')[0];
            return dTime === timeStr;
          });
          
          const brokerDataPoint = currentIndex >= 0 ? cumulativeInventoryData[currentIndex] : null;
          const prevBrokerDataPoint = currentIndex > 0 ? cumulativeInventoryData[currentIndex - 1] : null;

          if (brokerDataPoint) {
            displayBrokers.forEach(broker => {
              if (brokerDataPoint[broker] !== undefined && brokerDataPoint[broker] !== null) {
                 const brokerColor = brokerColorMap?.get(broker) ?? '#ffffff';
                 
                 // the file's FOREIGN_BROKERS / GOVERNMENT_BROKERS (same lists as Broker Summary 5D)
                 const summaryColor = (() => {
                   if (GOVERNMENT_BROKERS.includes(broker)) return '#16a34a'; // text-green-600
                   if (FOREIGN_BROKERS.includes(broker)) return '#dc2626'; // text-red-600
                   return '#ffffff'; // text-white
                 })();

                 const currentValue = brokerDataPoint[broker] as number;
                 
                 let changeAbsolute: number | null = null;
                 if (prevBrokerDataPoint && prevBrokerDataPoint[broker] !== undefined && prevBrokerDataPoint[broker] !== null) {
                   const prevValue = prevBrokerDataPoint[broker] as number;
                   changeAbsolute = currentValue - prevValue;
                 }
                 
                 let cumPrice: number | null = null;
                 let avgPrice: number | null = null;

                 // 1. Cum Price: progressive lookup from pre-computed map by date
                 const dailyCumPriceMap = cumPriceMapByDate.get(timeStr);
                 if (dailyCumPriceMap) {
                   const preComputedCumPrice = dailyCumPriceMap.get(broker);
                   if (preComputedCumPrice !== undefined && preComputedCumPrice > 0) {
                     cumPrice = preComputedCumPrice;
                   }
                 }

                 // 2. Avg Price: per-date NET table BAvg/SAvg (same as sortedNetDataByDateMemo rendering)
                 //   Net buyer  (netBuyValue >= netSellValue): show netBuyerAvg
                 //   Net seller (netSellValue > netBuyValue):  show netSellerAvg
                 if (brokerSummaryData) {
                    const dailyRec = brokerSummaryData.find((r: any) =>
                      (r.broker === broker || r.BrokerCode === broker) &&
                      (r.date === timeStr || r.time === timeStr)
                    );
                    if (dailyRec) {
                      const nbVal = Number(dailyRec.NetBuyValue  ?? dailyRec.netBuyValue  ?? 0);
                      const nsVal = Number(dailyRec.NetSellValue ?? dailyRec.netSellValue ?? 0);
                      const nbVol = Number(dailyRec.NetBuyVol    ?? dailyRec.netBuyVol    ?? 0);
                      const nsVol = Number(dailyRec.NetSellVol   ?? dailyRec.netSellVol   ?? 0);
                      let nba     = Number(dailyRec.NetBuyerAvg  ?? dailyRec.netBuyerAvg  ?? 0);
                      let nsa     = Number(dailyRec.NetSellerAvg ?? dailyRec.netSellerAvg ?? 0);
                      // Fallback: compute from val/vol if avg field is 0
                      if (nba === 0 && nbVol > 0) nba = nbVal / nbVol;
                      if (nsa === 0 && nsVol > 0) nsa = Math.abs(nsVal) / nsVol;

                      if (nbVal >= nsVal && nba > 0) {
                        avgPrice = nba; // Net buyer on this date
                      } else if (nsVal > nbVal && nsa > 0) {
                        avgPrice = nsa; // Net seller on this date
                      }
                    }
                 }

                 brokerValues.push({
                   broker,
                   value: currentValue,
                   color: brokerColor,
                   summaryColor,
                   changeAbsolute,
                   avgPrice,
                   cumPrice,
                   isDotted: brokerDottedMap.get(broker) ?? false
                 });
              }
            });
          }
        }

        // Show tooltip cursor
        tooltip.style.display = 'block';
        // Follow cursor position
        const cursorX = param.point.x;
        // In multi-pane charts, point.y is relative to the pane. 
        // We need to add the offset of previous panes to get the absolute Y in the container.
        let cursorY = param.point.y as number;
        
        // We know from line 625+ that Price pane is 75% and Volume pane is 25% of total height.
        // param.paneIndex tells us which pane the mouse is in (0 for Price, 1 for Volume)
        const activePaneIndex = (param as any).paneIndex ?? 0;
        if (activePaneIndex === 1) {
          const chartHeight = el.clientHeight || 400;
          cursorY += Math.floor(chartHeight * 0.75);
        }

        tooltip.style.cssText = `
          position: absolute !important;
          display: block !important;
          pointer-events: none !important;
          z-index: 9999 !important;
          left: 0 !important;
          top: 0 !important;
          transform: translate(${cursorX + 30}px, ${cursorY + 30}px) !important;
          width: 0 !important;
          height: 0 !important;
        `;

        // Format date for display
        const formatDisplayDate = (dateStr: string): string => {
          if (!dateStr) return '';
          try {
            const date = new Date(dateStr);
            return date.toLocaleDateString('id-ID', {
              day: '2-digit',
              month: 'short',
              year: 'numeric'
            });
          } catch {
            return dateStr;
          }
        };

        const displayDate = formatDisplayDate(timeStr);

        const fmtPrice = (val: number | undefined) => val !== undefined && val !== null ? val.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '-';
        const fmtPrice0 = (val: number | undefined) => val !== undefined && val !== null ? Math.round(val).toLocaleString('en-US') : '-';

        // Separate into Net Buy and Net Sell based on their Cumulative Net Flow (val)
        // Logic: TEKTOK section has priority. If a broker is tektok, it should not appear in Net Buy/Sell.
        const tektokBrokers = brokerValues.filter(b => 
          topTektoks && 
          topTektoks.includes(b.broker) && 
          !(topBuyers && topBuyers.includes(b.broker)) && 
          !(topSellers && topSellers.includes(b.broker))
        ).sort((a, b) => b.value - a.value);
        
        const tektokSet = new Set(tektokBrokers.map(b => b.broker));

        const buyBrokers = brokerValues.filter(b => b.value > 0 && !tektokSet.has(b.broker)).sort((a, b) => b.value - a.value);
        const sellBrokers = brokerValues.filter(b => b.value < 0 && !tektokSet.has(b.broker)).sort((a, b) => a.value - b.value).reverse();

        // Use auto columns with specific spacers; 16px columns act as the visual "gap"
        // Added 8px spacer between Avg and Cum Price
        const gridCols = 'auto 8px auto 8px auto 16px auto 8px auto';
        const cellR = 'text-align: right; padding: 2px 0;';
        const vLine = '<div style="width: 2px; height: 100%; background-color: rgba(255,255,255,0.15); margin: 0 auto;"></div>';
        const vLineH = '<div style="width: 2px; height: 100%; background-color: rgba(255,255,255,0.15); margin: 0 auto;"></div>';
        const spacer8 = '<div></div>';
        const spacer16 = '<div></div>';

        const formatBrokerCells = (b: any) => {
          const chgColor = b.changeAbsolute === null ? '#9ca3af'
            : b.changeAbsolute > 0 ? '#10b981'
            : b.changeAbsolute < 0 ? '#f97316' : '#9ca3af';
          const chgText = b.changeAbsolute === null ? '-'
            : `${b.changeAbsolute > 0 ? '+' : ''}${formatLotNumber(b.changeAbsolute)}`;
          const cumText = formatLotNumber(b.value);
          const avgText = b.avgPrice !== null ? fmtPrice(b.avgPrice) : '-';
          const cumPText = b.cumPrice !== null ? fmtPrice(b.cumPrice) : '-';
          
          const avgColor = '#ffffff';
          
          let cumPColor = '#e5e7eb';
          if (b.cumPrice !== null && candleData && candleData.close !== undefined) {
            if (b.value > 0) { // Net Buy
              cumPColor = b.cumPrice < candleData.close ? '#10b981' : (b.cumPrice > candleData.close ? '#ef4444' : '#e5e7eb');
            } else { // Net Sell
              cumPColor = b.cumPrice < candleData.close ? '#ef4444' : (b.cumPrice > candleData.close ? '#10b981' : '#e5e7eb');
            }
          }

          return `
            <div style="display: flex; align-items: center; gap: 4px; padding: 2px 0;">
              <div style="width: 12px; height: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; box-sizing: border-box;">
                ${b.isDotted ? `
                  <div style="width: 100%; border-top: 2px dotted ${b.color};"></div>
                ` : `
                  <div style="width: 100%; height: 100%; background-color: ${b.color};"></div>
                `}
              </div>
              <span style="color: ${b.summaryColor}; font-weight: 600;">${b.broker}</span>
            </div>
            ${spacer16}
            <span style="${cellR} color: ${chgColor};">${chgText}</span>
            ${spacer8}
            <span style="${cellR} color: #f3f4f6;">${cumText}</span>
            ${vLine}
            <span style="${cellR} color: ${avgColor};">${avgText}</span>
            ${spacer8}
            <span style="${cellR} color: ${cumPColor};">${cumPText}</span>
          `;
        };

        const buyInfo = buyBrokers.length > 0
          ? buyBrokers.map(formatBrokerCells).join('')
          : `<div style="color: #9ca3af; grid-column: 1/-1; padding-left: 4px;">No buyer data</div>`;
        const sellInfo = sellBrokers.length > 0
          ? sellBrokers.map(formatBrokerCells).join('')
          : `<div style="color: #9ca3af; grid-column: 1/-1; padding-left: 4px;">No seller data</div>`;
        const tektokInfo = tektokBrokers.length > 0
          ? tektokBrokers.map(formatBrokerCells).join('')
          : '';

        // Update tooltip content
        const priceInfo = candleData ?
          `<div style="display: flex; gap: 8px; font-variant-numeric: tabular-nums;">
            <span>O: ${fmtPrice0(candleData.open)}</span>
            <span>H: ${fmtPrice0(candleData.high)}</span>
            <span>L: ${fmtPrice0(candleData.low)}</span>
            <span>C: ${fmtPrice0(candleData.close)}</span>
           </div>` :
          'No price data';

        tooltip.innerHTML = `
          <div style="padding: 10px; background: rgba(15, 23, 42, 0.95); border: 1px solid #1e293b; border-radius: 6px; font-size: 11px; color: #cbd5e1; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.5); width: max-content; margin-left: 57px !important; text-align: left !important;">
            <div style="margin-bottom: 8px; font-weight: bold; color: #f8fafc; font-size: 14px; border-bottom: 1px solid #334155; padding-bottom: 6px;">
              ${ticker ? `${ticker} - ` : ''}${displayDate}
            </div>
            
            <div style="margin-bottom: 4px; font-weight: 600; color: #94a3b8; font-size: 12px; text-transform: uppercase; letter-spacing: 0.05em;">Price</div>
            <div style="margin-bottom: 12px; color: #e2e8f0; font-family: Inter, monospace; font-variant-numeric: tabular-nums;">${priceInfo}</div>
            
            <div style="display: grid; grid-template-columns: ${gridCols}; column-gap: 4px; row-gap: 0; align-items: stretch; font-family: Inter, monospace; font-variant-numeric: tabular-nums; font-size: 11px;">
              <!-- Header row -->
              <span style="padding: 4px 0;"></span>
              ${spacer16}
              <span style="text-align: center; color: #94a3b8; font-size: 10px; padding: 4px 0;">Chg</span>
              ${spacer8}
              <span style="text-align: center; color: #94a3b8; font-size: 10px; padding: 4px 0;">Cum</span>
              ${vLineH}
              <span style="text-align: center; color: #94a3b8; font-size: 10px; line-height: 1; padding: 4px 0;">Avg.<br/>Price</span>
              ${spacer8}
              <span style="text-align: center; color: #94a3b8; font-size: 10px; line-height: 1; padding: 4px 0;">Cum.<br/>Price</span>

              <!-- NET BUY label row -->
              <span style="padding-top: 8px; padding-bottom: 4px; font-weight: 600; color: #10b981; font-family: sans-serif;">NET BUY</span>
              ${spacer16}
              <span style="grid-column: 3 / 6;"></span>
              ${vLineH}
              <span style="grid-column: 7 / 10;"></span>
              
              ${buyInfo}

              <!-- NET SELL label row -->
              <span style="padding-top: 10px; padding-bottom: 4px; font-weight: 600; color: #ef4444; font-family: sans-serif;">NET SELL</span>
              ${spacer16}
              <span style="grid-column: 3 / 6;"></span>
              ${vLineH}
              <span style="grid-column: 7 / 10;"></span>

              ${sellInfo}

              <!-- TEKTOK label row -->
              ${tektokBrokers.length > 0 ? `
                <span style="padding-top: 10px; padding-bottom: 4px; font-weight: 600; color: #facc15; font-family: sans-serif;">TEKTOK</span>
                ${spacer16}
                <span style="grid-column: 3 / 6;"></span>
                ${vLineH}
                <span style="grid-column: 7 / 10;"></span>
                
                ${tektokInfo}
              ` : ''}
            </div>
          </div>
        `;
      });

      // Store unsubscribe function for cleanup
      (chart as any)._crosshairUnsubscribe = unsubscribeCrosshair;
    } catch (e) {
      console.error('Chart render error:', e);
    }

    // Cleanup function - ensure chart is removed when component unmounts or dependencies change
    return () => {
      if (chartRef.current) {
        // Unsubscribe from crosshair
        if ((chartRef.current as any)._crosshairUnsubscribe) {
          (chartRef.current as any)._crosshairUnsubscribe();
        }

        console.log(`🧹 TradingViewChart: Cleanup - removing chart`);
        try {
          chartRef.current.remove();
        } catch (e) {
          console.error('Error removing chart in cleanup:', e);
        }
        chartRef.current = null;
      }
    };
  }, [candlestickData, inventoryData, selectedBrokers, displayBrokers, volumeData, userColors, brokerSummaryData, ticker, topBuyers, topSellers, topTektoks, brokerColorMap, logicalRangeRef]);

  // Resize responsif with 80:20 ratio preservation
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    let prevW = 0;
    let prevH = 0;
    const ro = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect;
      if (!cr || !chartRef.current) return;
      const currentWidth = Math.max(1, Math.floor(cr.width));
      const currentHeight = Math.max(1, Math.floor(cr.height));

      if (Math.abs(currentWidth - prevW) > 2 || Math.abs(currentHeight - prevH) > 2) {
        console.log(`[DEBUG Resize] TradingViewChart ResizeObserver TRIGGERED RESIZE | from ${prevW}x${prevH} to ${currentWidth}x${currentHeight}`);
        prevW = currentWidth;
        prevH = currentHeight;
        chartRef.current.resize(currentWidth, currentHeight);

        // Re-apply 75:25 ratio during resize to ensure consistency
        const vPane = chartRef.current.panes()[1];
        if (vPane) {
          vPane.setHeight(Math.floor(currentHeight * 0.25));
          const pPane = chartRef.current.panes()[0];
          if (pPane) {
            pPane.setHeight(Math.floor(currentHeight * 0.75));
          }
        }
      } else {
        console.log(`[DEBUG Resize] TradingViewChart ResizeObserver SKIPPED (dimension delta <= 2px) | current: ${currentWidth}x${currentHeight}, prev: ${prevW}x${prevH}`);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }
    };
  }, []);

  return (
    <div className={className || "h-[600px] w-full relative"}>
      <style>{`
        #tv-attr-logo {
          display: none !important;
        }
        .tv-attr-logo {
          display: none !important;
        }
        [data-tv-attr-logo] {
          display: none !important;
        }
      `}</style>

      {/* Hover Tooltip */}
      <div
        ref={tooltipRef}
        style={{
          position: 'absolute',
          display: 'none',
          pointerEvents: 'none',
          zIndex: 1000,
          left: 0,
          top: 0,
          width: 0,
          height: 0,
        }}
      />

      {/* Header Label Bar */}
      <div
        className="absolute top-0 left-0 right-0 h-6 bg-background flex items-center justify-between z-10 border border-border/50"
      >
        <span className="w-[60px] text-[12px] text-muted-foreground font-bold uppercase tracking-wider text-center">Lot</span>
        <span className="w-[60px] text-[12px] text-muted-foreground font-bold uppercase tracking-wider text-center">Price</span>
      </div>

      <div className="relative h-full w-full">
        <div ref={containerRef} className="h-full w-full border border-border/50" />
        <div ref={tailBadgeContainerRef} className="absolute inset-0 pointer-events-none z-20 overflow-hidden" />
      </div>
    </div>
  );
};

// Volume Chart Component
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const VolumeChart = ({ volumeData, candlestickData, showLabel = true, className, logicalRangeRef }: { volumeData: any[], candlestickData?: any[], showLabel?: boolean, className?: string, logicalRangeRef?: React.MutableRefObject<{ from: number; to: number } | null> }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const userColors = useUserChartColors();
  const internalLogicalRangeRef = useRef<{ from: number; to: number } | null>(null);
  const activeLogicalRangeRef = logicalRangeRef || internalLogicalRangeRef;

  // Get theme-aware colors
  const getThemeColors = () => {
    const isDark = document.documentElement.classList.contains('dark');
    return {
      textColor: isDark ? '#f9fafb' : '#111827',
      gridColor: isDark ? '#4b5563' : '#e5e7eb',
      borderColor: isDark ? '#6b7280' : '#d1d5db',
      axisTextColor: isDark ? '#d1d5db' : '#6b7280'
    };
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    if (chartRef.current) {
      chartRef.current.remove();
      chartRef.current = null;
    }

    const width = el.clientWidth || 800;
    const height = el.clientHeight || 150;
    const colors = getThemeColors();

    chartRef.current = createChart(el, {
      width,
      height,
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: colors.axisTextColor
      },
      grid: {
        horzLines: { visible: false },
        vertLines: { visible: false }
      },
      rightPriceScale: {
        borderColor: colors.borderColor,
        scaleMargins: { top: 0.1, bottom: 0.1 }
      },
      timeScale: {
        borderColor: colors.borderColor,
        timeVisible: true,
        secondsVisible: false,
        tickMarkFormatter: (time: any) => {
          let date: Date;
          if (typeof time === 'string') {
            date = new Date(time);
          } else {
            date = new Date(time * 1000);
          }
          const day = date.getDate();
          const month = date.toLocaleDateString('en-US', { month: 'short' });
          return `${day} ${month}`;
        }
      },
      crosshair: { mode: CrosshairMode.Normal },
      handleScroll: true,
      handleScale: true,
    });

    const chart = chartRef.current!;

    try {
      // Add volume histogram series
      const volumeSeries = chart.addSeries(HistogramSeries, {
        color: '#26a69a',
        priceFormat: {
          type: 'volume',
        },
        priceScaleId: '',
      });

      // Build price up/down map if candlestickData provided
      const upMap: Record<string, boolean> = {};
      if (candlestickData && Array.isArray(candlestickData)) {
        candlestickData.forEach((c: any) => {
          const t = c.time;
          upMap[t] = (c.close ?? 0) >= (c.open ?? 0);
        });
      }

      volumeSeries.setData(volumeData.map(d => {
        const up = upMap[d.time] ?? (d.value ?? 0) >= 0;
        return {
          time: d.time,
          value: (d.value ?? 0) / 100, // Convert to Lot (/100)
          color: up ? userColors.bullish : userColors.bearish,
        };
      }));

      const handleRangeChange = (newRange: any) => {
        if (newRange && typeof newRange.from === 'number' && typeof newRange.to === 'number') {
          activeLogicalRangeRef.current = newRange;
        }
      };
      chart.timeScale().subscribeVisibleLogicalRangeChange(handleRangeChange);

      if (activeLogicalRangeRef.current && typeof activeLogicalRangeRef.current.from === 'number' && typeof activeLogicalRangeRef.current.to === 'number') {
        try {
          const prevRange = activeLogicalRangeRef.current;
          const rangeSpan = Math.max(10, prevRange.to - prevRange.from);
          const newLength = volumeData ? volumeData.length : 0;
          let targetRange = prevRange;
          if (newLength > 0 && (prevRange.to > newLength + 20 || prevRange.from < -20 || prevRange.to <= 0)) {
            targetRange = {
              from: Math.max(0, newLength - rangeSpan),
              to: newLength
            };
            activeLogicalRangeRef.current = targetRange;
          }
          chart.timeScale().setVisibleLogicalRange(targetRange);
        } catch (e) {
          chart.timeScale().fitContent();
        }
      } else {
        chart.timeScale().fitContent();
      }
    } catch (e) {
      console.error('Volume chart render error:', e);
    }
  }, [volumeData, candlestickData, userColors, logicalRangeRef]);

  // Resize responsif
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect;
      if (!cr || !chartRef.current) return;
      chartRef.current.applyOptions({
        width: Math.max(1, Math.floor(cr.width)),
        height: Math.max(1, Math.floor(cr.height)),
      });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }
    };
  }, []);

  return (
    <div className={className || "h-32 w-full relative"}>
      <style>{`
        #tv-attr-logo {
          display: none !important;
        }
        .tv-attr-logo {
          display: none !important;
        }
        [data-tv-attr-logo] {
          display: none !important;
        }
      `}</style>

      {/* Header Label Bar */}
      {showLabel && (
        <div
          className="absolute top-0 left-0 right-0 h-6 bg-background flex items-center justify-end z-10 border border-border/50"
        >
          <span className="w-[60px] text-[12px] text-muted-foreground font-bold uppercase tracking-wider text-center">Lot</span>
        </div>
      )}

      <div ref={containerRef} className="h-full w-full border border-border/50" />
    </div>
  );
};

// Removed unused chart components to improve performance

// Price Chart Component for Split View
const PriceChart = ({ candlestickData, className, logicalRangeRef }: { candlestickData: any[], className?: string, logicalRangeRef?: React.MutableRefObject<{ from: number; to: number } | null> }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const userColors = useUserChartColors();
  const internalLogicalRangeRef = useRef<{ from: number; to: number } | null>(null);
  const activeLogicalRangeRef = logicalRangeRef || internalLogicalRangeRef;

  const getThemeColors = () => {
    const isDark = document.documentElement.classList.contains('dark');
    return {
      textColor: isDark ? '#f9fafb' : '#111827',
      gridColor: isDark ? '#4b5563' : '#e5e7eb',
      borderColor: isDark ? '#6b7280' : '#d1d5db',
      axisTextColor: isDark ? '#d1d5db' : '#6b7280'
    };
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    if (chartRef.current) {
      chartRef.current.remove();
      chartRef.current = null;
    }

    const width = el.clientWidth || 800;
    const height = el.clientHeight || 300;
    const colors = getThemeColors();

    chartRef.current = createChart(el, {
      width,
      height,
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: colors.axisTextColor
      },
      grid: {
        horzLines: { visible: false },
        vertLines: { visible: false }
      },
      rightPriceScale: {
        borderColor: colors.borderColor,
        scaleMargins: { top: 0.1, bottom: 0.1 }
      },
      timeScale: {
        borderColor: colors.borderColor,
        timeVisible: true,
        secondsVisible: false,
        tickMarkFormatter: (time: any) => {
          let date: Date;
          if (typeof time === 'string') {
            date = new Date(time);
          } else {
            date = new Date(time * 1000);
          }
          const day = date.getDate();
          const month = date.toLocaleDateString('en-US', { month: 'short' });
          return `${day} ${month}`;
        }
      },
      crosshair: { mode: CrosshairMode.Normal },
      handleScroll: true,
      handleScale: true,
    });

    const chart = chartRef.current!;

    try {
      const candlestickSeries = chart.addSeries(CandlestickSeries, {
        upColor: userColors.bullish,
        downColor: userColors.bearish,
        borderVisible: false,
        wickUpColor: userColors.bullish,
        wickDownColor: userColors.bearish,
        priceScaleId: 'right',
        priceFormat: {
          type: 'custom',
          formatter: (price: number) => {
            return price.toLocaleString('id-ID', { maximumFractionDigits: 0 });
          }
        }
      });

      candlestickSeries.setData(candlestickData.map(d => ({
        time: d.time,
        open: d.open,
        high: d.high,
        low: d.low,
        close: d.close,
      })));

      const handleRangeChange = (newRange: any) => {
        if (newRange && typeof newRange.from === 'number' && typeof newRange.to === 'number') {
          activeLogicalRangeRef.current = newRange;
        }
      };
      chart.timeScale().subscribeVisibleLogicalRangeChange(handleRangeChange);

      if (activeLogicalRangeRef.current && typeof activeLogicalRangeRef.current.from === 'number' && typeof activeLogicalRangeRef.current.to === 'number') {
        try {
          const prevRange = activeLogicalRangeRef.current;
          const rangeSpan = Math.max(10, prevRange.to - prevRange.from);
          const newLength = candlestickData ? candlestickData.length : 0;
          let targetRange = prevRange;
          if (newLength > 0 && (prevRange.to > newLength + 20 || prevRange.from < -20 || prevRange.to <= 0)) {
            targetRange = {
              from: Math.max(0, newLength - rangeSpan),
              to: newLength
            };
            activeLogicalRangeRef.current = targetRange;
          }
          chart.timeScale().setVisibleLogicalRange(targetRange);
        } catch (e) {
          chart.timeScale().fitContent();
        }
      } else {
        chart.timeScale().fitContent();
      }
    } catch (e) {
      console.error('Price chart render error:', e);
    }
  }, [candlestickData, userColors, logicalRangeRef]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect;
      if (!cr || !chartRef.current) return;
      chartRef.current.applyOptions({
        width: Math.max(1, Math.floor(cr.width)),
        height: Math.max(1, Math.floor(cr.height)),
      });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    return () => {
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }
    };
  }, []);

  return (
    <div className={className || "h-80 w-full relative"}>
      <style>{`
        #tv-attr-logo {
          display: none !important;
        }
        .tv-attr-logo {
          display: none !important;
        }
        [data-tv-attr-logo] {
          display: none !important;
        }
      `}</style>

      {/* Header Label Bar */}
      <div
        className="absolute top-0 left-0 right-0 h-6 bg-background flex items-center justify-end z-10 border border-border/50"
      >
        <span className="w-[60px] text-[12px] text-muted-foreground font-bold uppercase tracking-wider text-center">Price</span>
      </div>

      <div ref={containerRef} className="h-full w-full border border-border/50" />
    </div>
  );
};

export interface InventoryChartProps {
  inventoryData?: any[];
  selectedBrokers?: string[];
  displayBrokers?: string[];
  className?: string;
  topBuyers?: string[];
  topSellers?: string[];
  topTektoks?: string[];
  brokerSummaryData?: any[];
  brokerColorMap?: Map<string, string>;
  ticker?: string;
  logicalRangeRef?: React.MutableRefObject<{ from: number; to: number } | null>;
  tailBadgeOffset?: number;
}

export const InventoryChart: React.FC<InventoryChartProps> = ({
  inventoryData = [],
  selectedBrokers = [],
  displayBrokers,
  className,
  topBuyers = [],
  topSellers = [],
  topTektoks = [],
  brokerSummaryData = [],
  brokerColorMap,
  ticker = '',
  logicalRangeRef,
  tailBadgeOffset = 54
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const tailBadgeContainerRef = useRef<HTMLDivElement>(null);
  const prevTickerRef = useRef<string | undefined>(ticker);
  const internalLogicalRangeRef = useRef<{ from: number; to: number } | null>(null);
  const activeLogicalRangeRef = logicalRangeRef || internalLogicalRangeRef;
  const isInitializingRef = useRef(false);

  const getThemeColors = () => {
    const isDark = document.documentElement.classList.contains('dark');
    return {
      textColor: isDark ? '#f9fafb' : '#111827',
      gridColor: isDark ? '#4b5563' : '#e5e7eb',
      borderColor: isDark ? '#6b7280' : '#d1d5db',
      axisTextColor: isDark ? '#d1d5db' : '#6b7280'
    };
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const isSameTicker = prevTickerRef.current === ticker;
    isInitializingRef.current = true;
    prevTickerRef.current = ticker;

    if (chartRef.current) {
      try {
        const liveRange = chartRef.current.timeScale().getVisibleLogicalRange();
        if (liveRange && typeof liveRange.from === 'number' && typeof liveRange.to === 'number') {
          activeLogicalRangeRef.current = liveRange;
        }
      } catch (e) {
        console.error('Error getting visible logical range:', e);
      }
      try {
        chartRef.current.remove();
      } catch (e) {
        console.error('Error removing chart in cleanup:', e);
      }
      chartRef.current = null;
    }

    const rect = el.getBoundingClientRect();
    const width = Math.max(1, Math.floor(rect.width || el.clientWidth || el.parentElement?.clientWidth || 800));
    const height = Math.max(1, Math.floor(rect.height || el.clientHeight || el.parentElement?.clientHeight || 300));
    const colors = getThemeColors();

    console.log(`[DEBUG Chart] InventoryChart useEffect triggered | ticker: "${ticker}" | isSameTicker: ${isSameTicker} | rect: ${rect.width}x${rect.height} | computed width: ${width}, height: ${height} | savedRange:`, activeLogicalRangeRef.current);

    chartRef.current = createChart(el, {
      width,
      height,
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: colors.axisTextColor
      },
      grid: {
        horzLines: { visible: false },
        vertLines: { visible: false }
      },
      leftPriceScale: {
        visible: true, // Show left price scale
        borderColor: colors.borderColor,
        scaleMargins: { top: 0.1, bottom: 0.1 }
      },
      rightPriceScale: {
        visible: true, // Show right price scale with labels
        borderColor: colors.borderColor,
        scaleMargins: { top: 0.1, bottom: 0.1 },
      },
      timeScale: {
        borderColor: colors.borderColor,
        timeVisible: true,
        secondsVisible: false,
        tickMarkFormatter: (time: any) => {
          let date: Date;
          if (typeof time === 'string') {
            date = new Date(time);
          } else {
            date = new Date(time * 1000);
          }
          const day = date.getDate();
          const month = date.toLocaleDateString('en-US', { month: 'short' });
          return `${day} ${month}`;
        }
      },
      crosshair: { mode: CrosshairMode.Normal },
      handleScroll: true,
      handleScale: true,
    });

    const chart = chartRef.current!;

    try {
      // Only display brokers that are visible (from displayBrokers/visibleBrokers)
      // If displayBrokers is provided, use it; otherwise use empty array to show nothing
      const brokersToDisplay = displayBrokers && displayBrokers.length > 0 ? displayBrokers : [];

      const firstInventoryItem = inventoryData[0];
      console.log(`ðŸ“Š InventoryChart render:`, {
        inventoryDataLength: inventoryData.length,
        selectedBrokers,
        sampleInventoryData: inventoryData.slice(0, 3),
        inventoryDataKeys: firstInventoryItem ? Object.keys(firstInventoryItem) : []
      });

      if (inventoryData.length === 0) {
        console.warn(`âš ï¸ InventoryChart: No inventory data provided`);
        return;
      }

      if (brokersToDisplay.length === 0) {
        console.warn(`âš ï¸ InventoryChart: No selected brokers`);
        return;
      }

      // Pre-calculate cumulative data (Running Sum) for all valid/displayed brokers to match Combined View logic
      // Structure: { time: string, [broker]: number }[]
      const cumulativeInventoryData: any[] = [];
      let runningSums: Record<string, number> = {};

      // Initialize running sums for all displayed brokers to 0
      brokersToDisplay.forEach(b => runningSums[b] = 0);

      inventoryData.forEach(day => {
        const row: any = { time: day.time };
        brokersToDisplay.forEach(broker => {
          const dailyNet = (day[broker] as number) || 0;
          const currentSum = runningSums[broker] ?? 0;
          const newSum = currentSum + dailyNet;
          runningSums[broker] = newSum;
          // Convert to Lot (/100)
          row[broker] = newSum / 100;
        });
        cumulativeInventoryData.push(row);
      });

      // Use left price scale for broker series
      let firstBrokerSeries: any = null;
      const brokerSeriesPairs: { broker: string; color: string; series: any; data: any[]; priceLine: any; lastVal: number }[] = [];

      brokersToDisplay.forEach(broker => {
        const brokerColor = brokerColorMap?.get(broker) ?? '#ffffff';
        const isTopSeller = topSellers && topSellers.includes(broker);
        const isTopTektok = topTektoks && topTektoks.includes(broker);
        
        let isNetSell = false;
        if (brokerSummaryData) {
           const brokerRecords = brokerSummaryData.filter(r => (r.broker || r.BrokerCode) === broker);
           let nBuy = 0;
           let nSell = 0;
           brokerRecords.forEach(r => {
              nBuy += Number(r.NetBuyValue || 0);
              nSell += Number(r.NetSellValue || 0);
           });
           isNetSell = nSell > nBuy;
        } else if (cumulativeInventoryData.length > 0) {
          isNetSell = (cumulativeInventoryData[cumulativeInventoryData.length - 1][broker] as number) < 0;
        }
        const isDotted = isTopSeller || (isTopTektok && isNetSell);

        // Extract Cumulative Inventory data from pre-calculated array
        const brokerData = cumulativeInventoryData
          .map(d => {
            const netValue = d[broker];
            let timeValue: string = String(d.time || '');

            const rawTime: any = d.time;
            if (typeof rawTime === 'string') {
              timeValue = rawTime;
              if (!/^\d{4}-\d{2}-\d{2}$/.test(timeValue)) {
                const date = new Date(timeValue);
                if (!isNaN(date.getTime())) {
                  const isoStr = date.toISOString().split('T')[0];
                  timeValue = isoStr || timeValue;
                }
              }
            } else if (rawTime && typeof rawTime === 'object' && 'toISOString' in rawTime) {
              const dateObj = rawTime as Date;
              const isoStr = dateObj.toISOString().split('T')[0];
              timeValue = isoStr || '';
            } else if (typeof rawTime === 'number') {
              const date = new Date(rawTime * 1000);
              const isoStr = date.toISOString().split('T')[0];
              timeValue = isoStr || '';
            }

            return {
              time: timeValue,
              value: typeof netValue === 'number' ? netValue : 0
            };
          })
          .filter(d => {
            return d.time && !isNaN(d.value) && isFinite(d.value);
          })
          .sort((a, b) => {
            return a.time.localeCompare(b.time);
          });

        if (brokerData.length > 0) {
          const lastVal = brokerData[brokerData.length - 1]?.value ?? 0;
          const lineSeries = chart.addSeries(LineSeries, {
            color: brokerColor,
            lineWidth: 2,
            lineStyle: isDotted ? 1 : 0,
            title: '', // empty title so broker code label is NOT attached to price scale badge
            priceScaleId: 'left',
            priceFormat: {
              type: 'custom',
              formatter: (price: number) => formatLotNumber(price),
            },
            lastValueVisible: false,
            priceLineVisible: false,
          });

          lineSeries.setData(brokerData);

          const priceLine = lineSeries.createPriceLine({
            price: lastVal,
            color: brokerColor,
            lineWidth: 1,
            lineVisible: false, // Hide horizontal line across chart canvas
            axisLabelVisible: true,
            title: '',
          });

          brokerSeriesPairs.push({ broker, color: brokerColor, series: lineSeries, data: brokerData, priceLine, lastVal });

          if (!firstBrokerSeries) {
            firstBrokerSeries = lineSeries;
          }
        }
      });

      // Function to render broker code badges at the exact right tail tip of each line plot
      const updateTailBadges = () => {
        const container = tailBadgeContainerRef.current;
        if (!container || !chartRef.current) return;

        const timeScale = chartRef.current.timeScale();
        const containerWidth = container.clientWidth || 800;
        const containerHeight = container.clientHeight || 600;

        // Clip container strictly within plot pane (excluding left and right price scales)
        const rightScaleWidth = chartRef.current.priceScale('right')?.width() || 55;
        const leftScaleWidth = chartRef.current.priceScale('left')?.width() || 60;
        container.style.clipPath = `inset(0px ${rightScaleWidth}px 0px ${leftScaleWidth}px)`;

        const badgesData: Array<{
          broker: string;
          color: string;
          x: number;
          y: number;
        }> = [];

        brokerSeriesPairs.forEach(pair => {
          if (!pair.data || pair.data.length === 0) return;
          const lastPoint = pair.data[pair.data.length - 1];
          const yCoord = pair.series.priceToCoordinate(lastPoint.value);
          const xCoord = timeScale.timeToCoordinate(lastPoint.time);

          if (
            yCoord !== null &&
            yCoord >= 0 &&
            yCoord <= containerHeight &&
            xCoord !== null &&
            xCoord >= 0 &&
            xCoord <= containerWidth
          ) {
            badgesData.push({
              broker: pair.broker,
              color: pair.color,
              x: xCoord,
              y: yCoord,
            });
          }
        });

        if (badgesData.length === 0) {
          container.innerHTML = '';
          return;
        }

        // Slight local adjustment only if two badges have almost identical coordinates
        badgesData.sort((a, b) => a.y - b.y);
        for (let i = 1; i < badgesData.length; i++) {
          if (Math.abs(badgesData[i].x - badgesData[i - 1].x) < 30 && badgesData[i].y - badgesData[i - 1].y < 12) {
            badgesData[i].y = badgesData[i - 1].y + 12;
          }
        }

        container.innerHTML = '';
        badgesData.forEach(b => {
          const badgeEl = document.createElement('div');
          badgeEl.className = 'absolute font-bold text-[11.8px] px-1.5 py-0.5 rounded-none shadow-sm flex items-center justify-center leading-none select-none pointer-events-none transition-transform duration-75 z-10';
          const textColor = getContrastTextColor(b.color);
          badgeEl.style.backgroundColor = b.color;
          badgeEl.style.color = textColor;
          badgeEl.innerText = b.broker;

          badgeEl.style.left = `${b.x + tailBadgeOffset}px`;
          badgeEl.style.top = `${b.y}px`;
          badgeEl.style.transform = 'translateY(-50%)';

          container.appendChild(badgeEl);
        });
      };

      // Add zero line (0 lot) horizontal line on left price scale
      if (firstBrokerSeries) {
        firstBrokerSeries.createPriceLine({
          price: 0,
          color: '#6b7280',
          lineWidth: 1,
          lineStyle: 0,
          axisLabelVisible: false,
          title: '',
        });
      }

      // Crosshair hover listener to update left axis price lines to follow hovered date
      const unsubscribeCrosshair = chart.subscribeCrosshairMove((param) => {
        if (param.point !== undefined && param.time && cumulativeInventoryData.length > 0) {
          let timeStr: string = '';
          if (typeof param.time === 'string') {
            timeStr = param.time;
          } else if (typeof param.time === 'number') {
            const date = new Date(param.time * 1000);
            timeStr = date.toISOString().split('T')[0] || '';
          }

          const hoverRow = cumulativeInventoryData.find((d: any) => {
            const dTime = typeof d.time === 'string' ? d.time : new Date(d.time * 1000).toISOString().split('T')[0];
            return dTime === timeStr;
          });

          if (hoverRow) {
            brokerSeriesPairs.forEach(pair => {
              const val = hoverRow[pair.broker];
              if (typeof val === 'number' && !isNaN(val)) {
                pair.priceLine.applyOptions({ price: val });
              }
            });
          }
        } else {
          brokerSeriesPairs.forEach(pair => {
            pair.priceLine.applyOptions({ price: pair.lastVal });
          });
        }
        updateTailBadges();
      });

      // Ensure left price scale is visible
      chart.applyOptions({
        leftPriceScale: {
          visible: true,
        },
        rightPriceScale: {
          visible: true,
        },
      });

      // Fit content or restore previous zoom range after adding all series
      if (activeLogicalRangeRef.current && typeof activeLogicalRangeRef.current.from === 'number' && typeof activeLogicalRangeRef.current.to === 'number') {
        try {
          const prevRange = activeLogicalRangeRef.current;
          const rangeSpan = Math.max(10, prevRange.to - prevRange.from);
          const newLength = inventoryData ? inventoryData.length : 0;
          let targetRange = prevRange;
          if (newLength > 0 && (prevRange.to > newLength + 20 || prevRange.from < -20 || prevRange.to <= 0)) {
            targetRange = {
              from: Math.max(0, newLength - rangeSpan),
              to: newLength
            };
            activeLogicalRangeRef.current = targetRange;
          }
          chart.timeScale().setVisibleLogicalRange(targetRange);
        } catch (e) {
          chart.timeScale().fitContent();
        }
      } else {
        chart.timeScale().fitContent();
      }

      const handleRangeChange = (newRange: any) => {
        if (isInitializingRef.current) return;
        if (newRange && typeof newRange.from === 'number' && typeof newRange.to === 'number') {
          activeLogicalRangeRef.current = newRange;
        }
        updateTailBadges();
      };
      chart.timeScale().subscribeVisibleLogicalRangeChange(handleRangeChange);
      (chart as any)._rangeUnsubscribe = () => {
        try {
          chart.timeScale().unsubscribeVisibleLogicalRangeChange(handleRangeChange);
          unsubscribeCrosshair();
        } catch (e) {}
      };

      requestAnimationFrame(() => {
        isInitializingRef.current = false;
        updateTailBadges();
      });
      console.log(`✅ InventoryChart: Chart rendered with ${brokersToDisplay.length} series, labels on right side`);
    } catch (e) {
      console.error('Inventory chart render error:', e);
    }
  }, [inventoryData, selectedBrokers, displayBrokers]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect;
      if (!cr || !chartRef.current) return;
      chartRef.current.applyOptions({
        width: Math.max(1, Math.floor(cr.width)),
        height: Math.max(1, Math.floor(cr.height)),
      });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    return () => {
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }
    };
  }, []);

  return (
    <div className={`w-full relative ${className || 'h-80'}`}>
      <style>{`
        #tv-attr-logo {
          display: none !important;
        }
        .tv-attr-logo {
          display: none !important;
        }
        [data-tv-attr-logo] {
          display: none !important;
        }
      `}</style>

      {/* Header Label Bar */}
      <div
        className="absolute top-0 left-0 right-0 h-6 bg-background flex items-center justify-start z-10 border border-border/50"
      >
        <span className="w-[60px] text-[12px] text-muted-foreground font-bold uppercase tracking-wider text-center">Net Flow</span>
      </div>

      <div className="relative h-full w-full">
        <div ref={containerRef} className="h-full w-full border border-border/50" />
        <div ref={tailBadgeContainerRef} className="absolute inset-0 pointer-events-none z-20 overflow-hidden" />
      </div>
    </div>
  );
};

// Removed unused chart components to improve performance


// Cache mechanism
const cache = {
  brokers: new Map<string, { data: string[]; timestamp: number }>(),
  inventory: new Map<string, { data: any[]; timestamp: number }>(),
  stockData: new Map<string, { data: any[]; timestamp: number }>(),
  topBrokers: new Map<string, { data: any[]; timestamp: number }>(),

  get(key: string, cacheMap: Map<string, any>, ttl: number = 5 * 60 * 1000): any | null {
    const cached = cacheMap.get(key);
    if (cached && Date.now() - cached.timestamp < ttl) {
      return cached.data;
    }
    return null;
  },

  set(key: string, data: any, cacheMap: Map<string, any>): void {
    cacheMap.set(key, { data, timestamp: Date.now() });
  },

  clear(): void {
    cache.brokers.clear();
    cache.inventory.clear();
    cache.stockData.clear();
    cache.topBrokers.clear();
  }
};

interface BrokerInventoryPageProps {
  selectedStock?: string;
  defaultSplitView?: boolean;
  hideControls?: boolean;
  onlyShowInventoryChart?: boolean;
  disableTickerSelection?: boolean;
  isDashboard?: boolean;
  isActive?: boolean;
}

export const BrokerInventoryPage = React.memo(function BrokerInventoryPage({
  selectedStock: propSelectedStock,
  defaultSplitView = false,
  hideControls = false,
  onlyShowInventoryChart = false,
  disableTickerSelection = false,
  isDashboard = false,
  isActive = true
}: BrokerInventoryPageProps) {
  const { showToast } = useToast();
  const { profile } = useProfile();
  const isTrial = profile?.subscriptionStatus === 'trial';

  // Load preferences from cookies on mount
  const effectivePageId = isDashboard ? `dashboard-${PAGE_ID}` : PAGE_ID;
  const [savedPrefs, setSavedPrefs] = useState(() => loadPreferences(effectivePageId));

  // Preferences are now initialized directly in useState to prevent flash of defaults
  // and redundant updates on mount.

  // State management
  // Always default to BBCA if no propSelectedStock or if it's empty
  const [selectedTicker, setSelectedTicker] = useState<string>(() => {
    if (isTrial) {
      return ''; // Force "All" for trial users
    }
    // If disableTickerSelection is true, always use propSelectedStock
    if (disableTickerSelection && propSelectedStock && propSelectedStock.trim() !== '') {
      return propSelectedStock;
    }
    // Try to load from preferences first
    if (savedPrefs?.selectedTicker && !disableTickerSelection) {
      return savedPrefs.selectedTicker;
    }
    // Fallback to prop or default
    return propSelectedStock && propSelectedStock.trim() !== '' ? propSelectedStock : 'BBCA';
  });
  // Displayed ticker - only changes when new data is successfully loaded (after Show button clicked)
  const [displayedTicker, setDisplayedTicker] = useState<string>(() => {
    if (isTrial) {
      return ''; // Force "All" for trial users
    }
    return propSelectedStock && propSelectedStock.trim() !== '' ? propSelectedStock : 'BBCA';
  });
  const [selectedBrokers, setSelectedBrokers] = useState<string[]>(() => {
    if (isTrial) {
      return []; // Force "All" for trial users
    }
    // Try to load from preferences first
    if (savedPrefs?.selectedBrokers && savedPrefs.selectedBrokers.length > 0) {
      return savedPrefs.selectedBrokers;
    }
    // Fallback to empty
    return [];
  });
  const isSameStock = savedPrefs?.selectedTicker === propSelectedStock;

  const [startDate, setStartDate] = useState<string>(() => {
    // Try to load from preferences first
    if (savedPrefs?.startDate && (!isDashboard || isSameStock)) {
      return savedPrefs.startDate;
    }
    // Fallback to empty (will be set from API)
    return '';
  });
  const [endDate, setEndDate] = useState<string>(() => {
    // Try to load from preferences first
    if (savedPrefs?.endDate && (!isDashboard || isSameStock)) {
      return savedPrefs.endDate;
    }
    // Fallback to empty (will be set from API)
    return '';
  });
  const [brokerSearch, setBrokerSearch] = useState('');
  const [debouncedBrokerSearch, setDebouncedBrokerSearch] = useState('');
  const [showBrokerSuggestions, setShowBrokerSuggestions] = useState(false);
  const [highlightedBrokerIndex, setHighlightedBrokerIndex] = useState(-1);
  const chartLogicalRangeRef = useRef<{ from: number; to: number } | null>(savedPrefs?.chartLogicalRange || null);
  const [brokerSelectionMode, setBrokerSelectionMode] = useState<{
    top5buy: boolean;
    top5sell: boolean;
    top5tektok: boolean;
    custom: boolean;
  }>(() => {
    if (isTrial) {
      return { top5buy: true, top5sell: true, top5tektok: false, custom: false };
    }
    // Try to load from preferences first
    if (savedPrefs?.brokerSelectionMode) {
      // Ensure backward compatibility if top5tektok is missing in prefs
      return {
        top5buy: savedPrefs.brokerSelectionMode.top5buy,
        top5sell: savedPrefs.brokerSelectionMode.top5sell,
        top5tektok: (savedPrefs.brokerSelectionMode as any).top5tektok || false,
        custom: savedPrefs.brokerSelectionMode.custom
      };
    }
    // Fallback to default
    return { top5buy: true, top5sell: true, top5tektok: false, custom: false };
  });
  const [tickerInput, setTickerInput] = useState<string>('');
  const [manuallySelectedBrokers, setManuallySelectedBrokers] = useState<Set<string>>(() => {
    if (savedPrefs?.manuallySelectedBrokers) {
      return new Set(savedPrefs.manuallySelectedBrokers);
    }
    return new Set();
  });

  const [activeColumn, setActiveColumn] = useState<'stocks' | 'sectors'>('stocks');
  const [debouncedTickerInput, setDebouncedTickerInput] = useState<string>('');
  const [showStockSuggestions, setShowStockSuggestions] = useState(false);
  const [splitVisualization, setSplitVisualization] = useState<boolean>(() => savedPrefs?.splitVisualization !== undefined ? savedPrefs.splitVisualization : defaultSplitView);
  const [showTooltipDetails, setShowTooltipDetails] = useState<boolean>(() => savedPrefs?.showTooltipDetails !== undefined ? savedPrefs.showTooltipDetails : true);
  const [highlightedStockIndex, setHighlightedStockIndex] = useState<number>(-1);
  const [availableStocks, setAvailableStocks] = useState<string[]>([]);
  const [sectorMapping, setSectorMapping] = useState<{ [sector: string]: string[] }>({});
  const [stockSearchTimeout, setStockSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  // Windowing state for virtual scrolling
  const [stockScrollOffset, setStockScrollOffset] = useState(0);
  const [sectorScrollOffset, setSectorScrollOffset] = useState(0);
  const [brokerScrollOffset, setBrokerScrollOffset] = useState(0);
  const ITEMS_PER_PAGE = 50; // Render 50 items at a time
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isDataReady, setIsDataReady] = useState<boolean>(false); // Control when to show charts/tables
  const [shouldFetchData, setShouldFetchData] = useState<boolean>(false); // Control when to fetch data (only when Show button clicked)
  const [isLoadingBrokerData, setIsLoadingBrokerData] = useState(false);
  const [dataError, setDataError] = useState<string | null>(null);
  const [brokerDataError, setBrokerDataError] = useState<string | null>(null);
  const [ohlcData, setOhlcData] = useState<any[]>([]);
  const [volumeData, setVolumeData] = useState<any[]>([]);
  const [brokerSummaryData, setBrokerSummaryData] = useState<any[]>([]); // Still used for top brokers table
  const [stockShares, setStockShares] = useState<number>(0);
  const [availableBrokersForStock, setAvailableBrokersForStock] = useState<string[]>([]);
  const [isLoadingBrokersForStock, setIsLoadingBrokersForStock] = useState(false);
  const startDateRef = useRef<HTMLInputElement>(null);
  const endDateRef = useRef<HTMLInputElement>(null);
  const [brokerVisibility, setBrokerVisibility] = useState<Record<string, boolean>>(() => {
    return savedPrefs?.brokerVisibility || {};
  });
  const [defaultBrokers, setDefaultBrokers] = useState<string[]>([]);
  const [hasUserSelectedBrokers, setHasUserSelectedBrokers] = useState<boolean>(() => {
    // If we have saved brokers or explicitly saved flag, don't overwrite with defaults
    return !!(savedPrefs?.selectedBrokers && savedPrefs.selectedBrokers.length > 0) || !!savedPrefs?.hasUserSelectedBrokers;
  });


  const [fdFilter, setFdFilter] = useState<'All' | 'Foreign' | 'Domestic'>(() => savedPrefs?.fdFilter || 'All'); // Temporary filter
  const [displayedFdFilter, setDisplayedFdFilter] = useState<'All' | 'Foreign' | 'Domestic'>(() => savedPrefs?.fdFilter || 'All'); // Actual filter used for data display
  const [marketFilter, setMarketFilter] = useState<'RG' | 'TN' | 'NG' | ''>(() => savedPrefs?.marketFilter || 'RG'); // Default to RG MARKET
  const [displayedMarket, setDisplayedMarket] = useState<'RG' | 'TN' | 'NG' | ''>(() => savedPrefs?.marketFilter || 'RG'); // Default to RG MARKET

  // Minimum date allowed: 02/01/2004 (earliest stock data)
  const MIN_DATE = '2004-01-02';

  // Helper function to get actual ticker from selectedTicker (handle sector selection)
  const getActualTicker = useMemo(() => {
    if (!selectedTicker) return '';
    // If it's a sector, get first stock from that sector
    if (selectedTicker.startsWith('[SECTOR] ')) {
      const sectorName = selectedTicker.replace('[SECTOR] ', '');
      const stocksInSector = sectorMapping[sectorName] || [];
      return stocksInSector[0] || '';
    }
    return selectedTicker;
  }, [selectedTicker, sectorMapping]);

  // Track previous selectedTicker to detect changes
  const prevSelectedTickerRef = useRef<string>('');

  // Force selectedTicker to match propSelectedStock when disableTickerSelection is true
  useEffect(() => {
    if (disableTickerSelection && propSelectedStock && propSelectedStock.trim() !== '') {
      if (selectedTicker !== propSelectedStock) {
        setSelectedTicker(propSelectedStock);
      }
    }
  }, [propSelectedStock, disableTickerSelection, selectedTicker]);

  // Auto-trigger data fetch when isDashboard is true (on mount and when propSelectedStock changes)
  const dashboardAutoFetchRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isDashboard || !propSelectedStock || !isActive) return;
    if (dashboardAutoFetchRef.current === propSelectedStock) return;
    
    const timer = setTimeout(() => {
      if (startDate && endDate && selectedTicker) {
        dashboardAutoFetchRef.current = propSelectedStock;
        setDisplayedFdFilter(fdFilter);
        setDisplayedMarket(marketFilter);
        setShouldFetchData(true);
        setIsDataReady(false);
        setIsLoadingData(true);
        setIsLoadingBrokerData(true);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [isDashboard, propSelectedStock, startDate, endDate, selectedTicker, isActive]);

  // Track ticker changes but keep existing chart & broker states untouched until Show button is clicked
  useEffect(() => {
    const currentTicker: string = selectedTicker || '';
    console.log(`[BrokerInventory] selectedTicker effect (ticker changed to "${currentTicker}") - keeping chart frozen until Show is clicked`);
    if (currentTicker) {
      prevSelectedTickerRef.current = currentTicker;
    }
  }, [selectedTicker]);

  const syncSelectedBrokersWithAvailable = useCallback((availableList: string[]) => {
    if (!availableList || availableList.length === 0) {
      setSelectedBrokers([]);
      return;
    }
    setSelectedBrokers(prev => {
      const filtered = prev.filter(broker => availableList.includes(broker));

      if (filtered.length > 0 || hasUserSelectedBrokers) {
        return filtered;
      }

      const defaultsInList = defaultBrokers.filter(broker => availableList.includes(broker));
      if (defaultsInList.length > 0) {
        return defaultsInList;
      }

      return availableList.slice(0, Math.min(5, availableList.length));
    });
  }, [defaultBrokers, hasUserSelectedBrokers]);

  useEffect(() => {
    setBrokerVisibility((prev) => {
      const updated = { ...prev };
      let changed = false;

      selectedBrokers.forEach((broker) => {
        if (updated[broker] === undefined) {
          updated[broker] = true;
          changed = true;
        }
      });

      Object.keys(updated).forEach((broker) => {
        if (!selectedBrokers.includes(broker)) {
          delete updated[broker];
          changed = true;
        }
      });

      return changed ? updated : prev;
    });
  }, [selectedBrokers]);



  const visibleBrokers = useMemo(
    () => {
      const visible = selectedBrokers.filter((broker) => brokerVisibility[broker] !== false);
      console.log(`ðŸ‘ï¸ visibleBrokers updated:`, {
        selectedBrokers,
        brokerVisibility,
        visible,
        visibleCount: visible.length
      });
      return visible;
    },
    [selectedBrokers, brokerVisibility]
  );

  // Auto-save preferences when state changes
  useEffect(() => {
    const timeout = setTimeout(() => {
      const prefs: Partial<UserPreferences> = {
        selectedTicker: propSelectedStock || selectedTicker,
        selectedBrokers,
        fdFilter,
        marketFilter,
        brokerSelectionMode,
        startDate,
        endDate,
        brokerVisibility,
        splitVisualization,
        showTooltipDetails,
        chartLogicalRange: chartLogicalRangeRef.current || undefined,
        manuallySelectedBrokers: Array.from(manuallySelectedBrokers),
        hasUserSelectedBrokers
      };
      savePreferences(effectivePageId, prefs);
      setSavedPrefs(prev => ({ ...prev, ...prefs }));
    }, 500);

    return () => clearTimeout(timeout);
  }, [selectedTicker, selectedBrokers, fdFilter, marketFilter, brokerSelectionMode, startDate, endDate, brokerVisibility, splitVisualization, showTooltipDetails, manuallySelectedBrokers, hasUserSelectedBrokers, propSelectedStock, effectivePageId]);

  const handleToggleBrokerVisibility = (broker: string) => {
    setBrokerVisibility((prev) => ({
      ...prev,
      [broker]: !(prev[broker] !== false),
    }));
  };

  // Auto-load data from saved preferences on mount (after initial data is loaded)
  useEffect(() => {
    // Only auto-load if we have saved preferences with dates and ticker
    if (!savedPrefs?.startDate || !savedPrefs?.endDate || !savedPrefs?.selectedTicker) {
      return; // No saved preferences, don't auto-load
    }

    const savedStartDate = savedPrefs.startDate;
    const savedEndDate = savedPrefs.endDate;

    // Wait a bit to ensure initial data (stocks, dates) are loaded
    const timer = setTimeout(() => {
      // Generate date array from saved startDate and endDate (only trading days)
      const startParts = savedStartDate.split('-').map(Number);
      const endParts = savedEndDate.split('-').map(Number);

      if (startParts.length === 3 && endParts.length === 3) {
        const startYear = startParts[0];
        const startMonth = startParts[1];
        const startDay = startParts[2];
        const endYear = endParts[0];
        const endMonth = endParts[1];
        const endDay = endParts[2];

        if (startYear !== undefined && startMonth !== undefined && startDay !== undefined &&
          endYear !== undefined && endMonth !== undefined && endDay !== undefined) {
          const start = new Date(startYear, startMonth - 1, startDay);
          const end = new Date(endYear, endMonth - 1, endDay);

          // Check if range is valid
          if (start <= end) {
            // Generate date array (only trading days)
            const dateArray: string[] = [];
            const currentDate = new Date(start);

            while (currentDate <= end) {
              const dayOfWeek = currentDate.getDay();
              // Skip weekends (Saturday = 6, Sunday = 0)
              if (dayOfWeek !== 0 && dayOfWeek !== 6) {
                // Format as YYYY-MM-DD in local timezone
                const year = currentDate.getFullYear();
                const month = String(currentDate.getMonth() + 1).padStart(2, '0');
                const day = String(currentDate.getDate()).padStart(2, '0');
                const dateString = `${year}-${month}-${day}`;
                dateArray.push(dateString);
              }
              // Move to next day
              currentDate.setDate(currentDate.getDate() + 1);
            }

            // Sort by date (oldest first) for display
            dateArray.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

            // Trigger auto-load by setting shouldFetchData to true
            console.log(`ðŸš€ Auto-loading data for ${savedPrefs.selectedTicker} from saved preferences`);
            setShouldFetchData(true);
          }
        }
      }
    }, 800); // Increased delay slightly to 800ms to ensure all states are initialized from prefs

    return () => clearTimeout(timer);
  }, []); // Only run once on mount

  // Track previous ticker to detect changes
  const prevTickerForAutoFetchRef = useRef<string>('');

  // Update selectedTicker when propSelectedStock changes
  useEffect(() => {
    // Only update if propSelectedStock is provided and valid, and different from current
    if (propSelectedStock && propSelectedStock.trim() !== '' && propSelectedStock !== selectedTicker) {
      console.log(`ðŸ“Š Dashboard stock changed to ${propSelectedStock}`);
      setSelectedTicker(propSelectedStock);
      setTickerInput('');

      // Only reset broker selection and UI states - keep chart data until Show is clicked
      setSelectedBrokers([]);
      setBrokerDataError(null);
      setAvailableBrokersForStock([]);
      setIsLoadingBrokersForStock(false);
      setBrokerSearch('');
      setShowBrokerSuggestions(false);
      setHighlightedBrokerIndex(-1);
      setHasUserSelectedBrokers(false);
      setDefaultBrokers([]);
      // Don't reset chart data (ohlcData, volumeData, brokerSummaryData) - keep showing old data
      // Don't reset isDataReady to prevent flicker - keep showing old data until new data is ready
      // Reset shouldFetchData to prevent auto-fetch
      setShouldFetchData(false);
      // Update prevTickerForAutoFetchRef when ticker changes (for tracking purposes only)
      prevTickerForAutoFetchRef.current = propSelectedStock;
      // No auto-fetch - user must click Show button to fetch new data when ticker changes
    }
  }, [propSelectedStock, selectedTicker]);


  // Helper function to trigger date picker
  const triggerDatePicker = (inputRef: React.RefObject<HTMLInputElement>) => {
    if (inputRef.current) {
      inputRef.current.showPicker();
    }
  };

  // Helper function to format date for input (YYYY-MM-DD)
  const formatDateForInput = (date: string | undefined) => {
    return date || ''; // Already in YYYY-MM-DD format, return empty string if undefined
  };

  // Load initial data (stocks list and sector mapping) on component mount
  useEffect(() => {
    let isMounted = true;
    const loadInitialData = async () => {
      if (availableStocks.length > 0) return; // Already loaded

      try {
        // Load both stock list and sector mapping in parallel
        const [sectorResult, stocks] = await Promise.all([
          fetchSectorMapping(),
          fetchStockSymbols()
        ]);

        if (!isMounted) return;
        let sectors: string[] = [];
        let mapping: { [sector: string]: string[] } = {};

        if (sectorResult.success && sectorResult.data) {
          sectors = sectorResult.data.sectors || [];
          mapping = sectorResult.data.sectorMapping || {};
          setSectorMapping(mapping);
          primeStockToSectorMap(sectorResult.data.stockToSector || {});
        }

        // Remove IDX from ticker list (IDX is now a sector, not a ticker)
        const stocksWithoutIdx = stocks.filter(stock => stock !== 'IDX');

        // Add sectors to the list (with prefix to distinguish from stocks)
        const sectorsWithPrefix = sectors.map(sector => `[SECTOR] ${sector}`);

        // Combine stocks and sectors, then sort alphabetically
        // Ensure IDX is always first
        const allItems = [...stocksWithoutIdx, ...sectorsWithPrefix].sort((a: string, b: string) => {
          // IDX always comes first
          if (a === '[SECTOR] IDX') return -1;
          if (b === '[SECTOR] IDX') return 1;
          return a.localeCompare(b);
        });

        setAvailableStocks(allItems);
        console.log(`ðŸ“Š Loaded ${stocksWithoutIdx.length} stocks and ${sectors.length} sectors from stockList.ts`);
      } catch (error) {
        console.error('Error loading initial data:', error);
        showToast({
          type: 'error',
          title: 'Error Memuat Data',
          message: 'Gagal memuat data awal.'
        });
        // Even if API fails, ensure IDX is available as sector
        setAvailableStocks(['[SECTOR] IDX']);
      }
    };

    loadInitialData();

    return () => {
      isMounted = false;
    };
  }, [showToast, availableStocks.length]);

  // Set default date range and load emiten shares when stock is selected
  useEffect(() => {
    let isMounted = true;
    const fetchStockDetail = async () => {
      const actualTicker = getActualTicker;
      if (!actualTicker) return;

      try {
        const shares = await getEmitenShares(actualTicker);
        if (!isMounted) return;
        if (shares) {
          setStockShares(shares);
        }
      } catch (err) {
        console.error('Error fetching stock detail:', err);
      }
    };

    const applyDefaultDateRange = () => {
      const actualTicker = getActualTicker;
      if (!actualTicker) return;

      const isSameStock = savedPrefs?.selectedTicker === propSelectedStock;
      // Default date range: 5 trading days -- only if no saved preferences OR if in dashboard (watchlist) and stock changed
      if (!savedPrefs?.startDate || !savedPrefs?.endDate || (isDashboard && !isSameStock)) {
        const dates: string[] = [];
        const today = new Date();
        const currentHour = today.getHours();
        today.setHours(0, 0, 0, 0);
        let daysBack = currentHour < 20 ? 1 : 0; // Skip today if before 20:00

        while (dates.length < 5) { // 5 hari terakhir
          const date = new Date(today);
          date.setDate(date.getDate() - daysBack);
          const dayOfWeek = date.getDay();
          
          if (dayOfWeek !== 0 && dayOfWeek !== 6) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            dates.push(`${year}-${month}-${day}`);
          }
          daysBack++;
        }
        
        dates.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
        if (dates.length > 0) {
          setStartDate(dates[0]);
          setEndDate(dates[dates.length - 1]);
        }
      }
    };

    const init = async () => {
      applyDefaultDateRange();
      await fetchStockDetail();
    };
    init();

    return () => { isMounted = false; };
  }, [getActualTicker]);

  // Sync endDate when startDate changes to ensure endDate >= startDate
  useEffect(() => {
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      console.log(`âš ï¸ Start date ${startDate} is after end date ${endDate}, updating end date to ${startDate}`);
      setEndDate(startDate);
    }
  }, [startDate, endDate]);

  // Load OHLC and volume data only when Show button is clicked
  useEffect(() => {
    if (!shouldFetchData) {
      return; // Don't load data until Show button is clicked
    }

    let isMounted = true;
    const loadStockData = async () => {
      const actualTicker = getActualTicker;
      if (!actualTicker || !startDate || !endDate) {
        console.log(`[BrokerInventory] Skipping loadStockData:`, { actualTicker, startDate, endDate });
        setShouldFetchData(false);
        setIsDataReady(false);
        return;
      }
      // Check if ticker is a sector (starts with [SECTOR] prefix)
      const isSector = selectedTicker.startsWith('[SECTOR] ');
      const sectorName = isSector ? selectedTicker.replace('[SECTOR] ', '') : null;

      console.log(`[BrokerInventory] Loading ${isSector ? 'sector' : 'stock'} data for:`, {
        selectedTicker,
        actualTicker,
        isSector,
        sectorName,
        startDate,
        endDate
      });

      setIsLoadingData(true);
      setDataError(null);

      // Extend start date 10 calendar days back to ensure we can show 5 prior trading-day candles
      const extendedStartDate = (() => {
        const d = new Date(startDate + 'T00:00:00');
        d.setDate(d.getDate() - 10);
        return d.toISOString().split('T')[0] ?? startDate;
      })();

      try {
        // Check cache first (5 minutes TTL) - use selectedTicker for cache key to differentiate sector vs stock
        const cacheKey = `${selectedTicker}-${startDate}-${endDate}-ext5`;
        const cachedData = cache.get(cacheKey, cache.stockData, 5 * 60 * 1000);

        if (cachedData) {
          console.log(`ðŸ“Š Using cached ${isSector ? 'sector' : 'stock'} data for ${isSector ? sectorName : actualTicker}`);
          // Ensure cached data is sorted and deduplicated
          const sortedCandlestick = (cachedData.candlestick || [])
            .filter((item: any, index: number, arr: any[]) => {
              // Remove duplicates (keep first occurrence)
              return index === 0 || arr[index - 1].time !== item.time;
            })
            .sort((a: any, b: any) => {
              if (a.time < b.time) return -1;
              if (a.time > b.time) return 1;
              return 0;
            });

          const sortedVolume = (cachedData.volume || [])
            .filter((item: any, index: number, arr: any[]) => {
              // Remove duplicates (keep first occurrence)
              return index === 0 || arr[index - 1].time !== item.time;
            })
            .sort((a: any, b: any) => {
              if (a.time < b.time) return -1;
              if (a.time > b.time) return 1;
              return 0;
            });

          setOhlcData(sortedCandlestick);
          setVolumeData(sortedVolume);
          setIsLoadingData(false);
          return;
        }

        console.log(`ðŸ“Š Loading ${isSector ? 'sector' : 'stock'} data for ${isSector ? sectorName : actualTicker} from ${startDate} to ${endDate}`);

        let response: any;

        if (isSector && sectorName) {
          // For sectors, use sector OHLC price API
          console.log(`ðŸ“Š Fetching sector OHLC data for: "${sectorName}"`);
          console.log(`ðŸ“Š Selected ticker: "${selectedTicker}", Extracted sector name: "${sectorName}"`);
          response = await api.getSectorOhlcPrice(sectorName, extendedStartDate, endDate, 1000);

          if (!response.success) {
            console.error(`âŒ Failed to fetch sector OHLC data:`, response.error);
            console.error(`âŒ Sector name sent: "${sectorName}"`);
            console.error(`âŒ Selected ticker: "${selectedTicker}"`);
          }
        } else {
          // For regular stocks, fetch OHLC parquet directly from Azure Blob
          // (trading-data-v2/ohlc/stocks/{sector}/{ticker}.parquet, output of scheduler task 0c),
          // same method as OrderbookReplay.tsx / TechnicalAnalysisTradingView.tsx
          console.log(`ðŸ“Š Fetching stock OHLC blob for: ${actualTicker}`);
          const tokenRes = await api.getOrderbookReplaySasToken();
          if (!tokenRes.success || !tokenRes.data) {
            response = { success: false, error: tokenRes.error || 'Failed to get blob access token' };
          } else {
            const { containerUrl, sasToken } = tokenRes.data;
            const ohlcPath = await getOhlcBlobPath(actualTicker);
            if (!ohlcPath) {
              response = { success: false, error: `No OHLC data found for ${actualTicker}` };
            } else {
              const ohlcRows = await loadOhlcByPath(ohlcPath, containerUrl, sasToken);
              response = { success: true, data: { data: ohlcRows } };
            }
          }
        }

        if (!isMounted) return;

        if (response.success && response.data?.data) {
          const stockData = response.data.data;
          console.log(`ðŸ“Š Received ${stockData.length} records for ${isSector ? `sector "${sectorName}"` : `stock ${actualTicker}`}`);

          // Get all data in the extended range
          const allExtendedData = stockData.filter((row: any) => {
            const rowDate = row.Date;
            return rowDate >= extendedStartDate && rowDate <= endDate;
          });

          // Take exactly 5 trading days before startDate as prior candles
          const priorDates = new Set(
            allExtendedData
              .filter((row: any) => row.Date < startDate)
              .map((row: any) => row.Date as string)
              .sort()
              .slice(-5)
          );

          // Include prior 5 candles + original date range
          const filteredData = allExtendedData.filter((row: any) => {
            const rowDate = row.Date;
            return priorDates.has(rowDate) || (rowDate >= startDate && rowDate <= endDate);
          });

          // Convert to candlestick format for charts
          // First, create a map to handle duplicates (keep last occurrence)
          const candlestickMap = new Map<string, any>();
          filteredData.forEach((row: any) => {
            const time = row.Date;
            candlestickMap.set(time, {
              time: time,
              open: row.Open || 0,
              high: row.High || 0,
              low: row.Low || 0,
              close: row.Close || 0,
              volume: row.Volume || 0
            });
          });

          // Convert map to array, sort by time, and remove duplicates
          const candlestickData = Array.from(candlestickMap.values())
            .sort((a, b) => {
              // Sort by time (ascending)
              if (a.time < b.time) return -1;
              if (a.time > b.time) return 1;
              return 0;
            });

          // Convert to volume format for charts (using same deduplication)
          const volumeMap = new Map<string, any>();
          filteredData.forEach((row: any) => {
            const time = row.Date;
            volumeMap.set(time, {
              time: time,
              value: row.Volume || 0,
              color: (row.Close || 0) >= (row.Open || 0) ? '#16a34a' : '#dc2626'
            });
          });

          const volumeChartData = Array.from(volumeMap.values())
            .sort((a, b) => {
              // Sort by time (ascending)
              if (a.time < b.time) return -1;
              if (a.time > b.time) return 1;
              return 0;
            });

          // Final validation: ensure no duplicates and proper sorting
          const finalCandlestick = candlestickData.filter((item: any, index: number, arr: any[]) => {
            if (index === 0) return true;
            const prevTime = arr[index - 1].time;
            if (prevTime === item.time) {
              console.warn(`âš ï¸ Duplicate time found in candlestick data: ${item.time} at index ${index}`);
              return false; // Remove duplicate
            }
            if (prevTime > item.time) {
              console.warn(`âš ï¸ Data not sorted: prev=${prevTime}, current=${item.time} at index ${index}`);
            }
            return true;
          });

          const finalVolume = volumeChartData.filter((item: any, index: number, arr: any[]) => {
            if (index === 0) return true;
            const prevTime = arr[index - 1].time;
            if (prevTime === item.time) {
              console.warn(`âš ï¸ Duplicate time found in volume data: ${item.time} at index ${index}`);
              return false; // Remove duplicate
            }
            return true;
          });

          console.log(`ðŸ“Š Processed data: ${finalCandlestick.length} OHLC records (${candlestickData.length - finalCandlestick.length} duplicates removed), ${finalVolume.length} volume records (${volumeChartData.length - finalVolume.length} duplicates removed)`);

          // Cache the cleaned data
          cache.set(cacheKey, { candlestick: finalCandlestick, volume: finalVolume }, cache.stockData);

          setOhlcData(finalCandlestick);
          setVolumeData(finalVolume);

          // Don't set isDataReady here - wait for broker inventory data to be loaded
          // Don't reset shouldFetchData here - let loadBrokerInventory handle it

        } else {
          const errorMsg = response.error || `Failed to load ${isSector ? 'sector' : 'stock'} data`;
          throw new Error(errorMsg);
        }

      } catch (error) {
        console.error(`Error loading ${isSector ? 'sector' : 'stock'} data:`, error);
        const errorMessage = error instanceof Error ? error.message : `Failed to load ${isSector ? 'sector' : 'stock'} data`;
        setDataError(errorMessage);
        setShouldFetchData(false);
        // Don't set isDataReady to false on error - keep showing old data if available
        // Only set to false if there's no data at all
        if (ohlcData.length === 0 && volumeData.length === 0) {
          setIsDataReady(false);
        }
        showToast({
          type: 'error',
          title: 'Error Memuat Data',
          message: 'Gagal memuat data OHLC dan volume.'
        });
      } finally {
        setIsLoadingData(false);
        // Don't reset shouldFetchData here - let loadBrokerInventory handle it
      }
    };

    loadStockData();

    return () => {
      isMounted = false;
    };
  }, [shouldFetchData, getActualTicker, startDate, endDate, showToast]);

  // Track previous ticker/date for comparison (but don't clear data immediately)
  // Data will be replaced only when new data is successfully loaded
  const prevTickerRef = useRef<string>('');
  const prevStartDateRef = useRef<string>('');
  const prevEndDateRef = useRef<string>('');
  const prevShouldFetchRef = useRef<boolean>(false);

  useEffect(() => {
    // Track when user clicks Show button
    if (shouldFetchData && !prevShouldFetchRef.current) {
      // User just clicked Show button, new data will be loaded
      // Don't clear old data yet - keep showing it until new data is ready
      console.log(`[BrokerInventory] Show button clicked, loading new data...`);
    }

    // Update refs
    prevTickerRef.current = getActualTicker || '';
    prevStartDateRef.current = startDate || '';
    prevEndDateRef.current = endDate || '';
    prevShouldFetchRef.current = shouldFetchData;
  }, [getActualTicker, startDate, endDate, shouldFetchData]);

  // REMOVED: Auto-fetch on initial load - user MUST click Show button to fetch data
  // This ensures no data is displayed automatically without user clicking Show
  // Tampilan awal akan kosong, sama seperti BrokerSummaryPage

  // Track ticker changes for logging purposes only (no auto-fetch)
  useEffect(() => {
    const currentTicker = getActualTicker || '';

    // Just update ticker ref for tracking, but never auto-fetch
    if (currentTicker && prevTickerForAutoFetchRef.current !== currentTicker) {
      const oldTicker = prevTickerForAutoFetchRef.current;
      prevTickerForAutoFetchRef.current = currentTicker;
      if (oldTicker !== '') {
        console.log(`[BrokerInventory] Ticker changed from ${oldTicker} to ${currentTicker} - NOT auto-fetching (waiting for Show button)`);
      }
    } else if (currentTicker && prevTickerForAutoFetchRef.current === '') {
      // Initial mount - just set the ticker ref
      prevTickerForAutoFetchRef.current = currentTicker;
    }
  }, [getActualTicker]);

  // Load top brokers data for table using broker-summary API when Show button is clicked
  useEffect(() => {
    if (!shouldFetchData) {
      return; // Don't load data until Show button is clicked
    }

    let isMounted = true;
    const loadTopBrokersData = async () => {
      // Load top brokers data for table (not for chart)
      // Use startDate and endDate from input field (not dependent on ohlcData)
      const actualTicker = getActualTicker;
      if (!actualTicker || !startDate || !endDate) {
        setBrokerSummaryData([]);
        setShouldFetchData(false);
        setIsDataReady(false);
        return;
      }

      setIsLoadingBrokerData(true);
      setBrokerDataError(null);

      try {
        // Calculate trading days from startDate to endDate (same as BrokerSummaryPage)
        // This ensures date range matches the input field exactly
        const tradingDays: string[] = [];
        if (startDate && endDate) {
          const start = new Date(startDate);
          const end = new Date(endDate);
          const current = new Date(start);

          while (current <= end) {
            const dayOfWeek = current.getDay();
            // Only include trading days (Monday to Friday, excluding weekends)
            if (dayOfWeek !== 0 && dayOfWeek !== 6) {
              const dateString = current.toISOString().split('T')[0];
              if (dateString) {
                tradingDays.push(dateString);
              }
            }
            current.setDate(current.getDate() + 1);
          }
        }

        if (tradingDays.length === 0) {
          console.log('âš ï¸ No trading days in date range');
          setBrokerSummaryData([]);
          return;
        }

        // For sectors, use sector name directly (not first stock) - same as BrokerSummaryPage
        const tickerToFetch = selectedTicker.startsWith('[SECTOR] ')
          ? selectedTicker.replace('[SECTOR] ', '')
          : actualTicker;
        const isSectorTicker = selectedTicker.startsWith('[SECTOR] ');

        // Use displayedMarket (the market filter that was applied when Show button was clicked)
        const market = displayedMarket || '';
        // Sectors (excluding IDX) with a specific board use the dedicated sector folder
        // (broker_summary_sector_[board]), same mapping as BrokerSummaryPage
        let marketToUse = market;
        if (isSectorTicker && tickerToFetch !== 'IDX' && (market === 'RG' || market === 'TN' || market === 'NG')) {
          marketToUse = `sector_${market}`;
        }

        // Get a SAS token to fetch broker_summary CSVs directly from Azure Blob (same as BrokerSummaryPage)
        const tokenRes = await api.getBrokerSummarySasToken();
        if (!tokenRes.success || !tokenRes.data) {
          throw new Error(tokenRes.error || 'Failed to get blob access token');
        }
        const { containerUrl, sasToken } = tokenRes.data;

        // Load broker summary data for each date directly from Azure Blob (same as BrokerSummaryPage)
        // This provides NetBuyValue and NetSellValue needed for top buyer/seller calculation (NET table)
        // OPTIMIZED: Use Promise.all for parallel loading instead of sequential for loop
        // Load all dates in parallel for much faster performance
        const datePromises = tradingDays.map(async (dateStr) => {
          try {
            // Check cache first (5 minutes TTL) - use tickerToFetch and market for cache key
            const cacheKey = `broker-summary-${tickerToFetch}-${dateStr}-${market}`;
            let brokerData: any[] | null = cache.get(cacheKey, cache.topBrokers, 5 * 60 * 1000);

            if (!brokerData) {
              // Fetch and parse the CSV directly from Azure Blob Storage (same as BrokerSummaryPage)
              const blobPaths = getBrokerSummaryBlobPath(tickerToFetch, dateStr, marketToUse, '');
              const csvText = await fetchBrokerSummaryCsv(blobPaths, containerUrl, sasToken);

              const brokers = csvText ? parseBrokerSummaryCSV(csvText) : [];

              if (brokers.length > 0) {
                // Map broker summary format to our format (same structure as BrokerSummaryPage)
                brokerData = brokers.map((broker: any) => {
                  // Standard 1:1 Mapping - Check both UpperCase and lowercase for API compatibility
                  const buyerVol = Number(broker.BuyerVol ?? broker.buyerVol ?? 0);
                  const sellerVol = Number(broker.SellerVol ?? broker.sellerVol ?? 0);
                  const buyerValue = Number(broker.BuyerValue ?? broker.buyerValue ?? 0);
                  const sellerValue = Number(broker.SellerValue ?? broker.sellerValue ?? 0);

                  const netBuyVol = Number(broker.NetBuyVol ?? broker.netBuyVol ?? 0);
                  const netSellVol = Number(broker.NetSellVol ?? broker.netSellVol ?? 0);
                  const netBuyValue = Number(broker.NetBuyValue ?? broker.netBuyValue ?? 0);
                  const netSellValue = Number(broker.NetSellValue ?? broker.netSellValue ?? 0);

                  let bavg = Number(broker.BuyerAvg ?? broker.bavg ?? 0);
                  if (bavg === 0 && Math.abs(buyerVol) > 0) bavg = buyerValue / buyerVol;

                  let savg = Number(broker.SellerAvg ?? broker.savg ?? 0);
                  if (savg === 0 && Math.abs(sellerVol) > 0) savg = sellerValue / sellerVol;

                  const netBuyerAvg = Number(broker.NetBuyerAvg ?? broker.netBuyerAvg ?? 0);
                  const netSellerAvg = Number(broker.NetSellerAvg ?? broker.netSellerAvg ?? 0);

                  return {
                    broker: broker.BrokerCode ?? broker.broker ?? broker.BROKER ?? broker.code ?? '',
                    BrokerCode: broker.BrokerCode ?? broker.broker ?? broker.BROKER ?? broker.code ?? '',
                    // Net fields (swapped for sectors, same as BrokerSummaryPage NET table)
                    NetBuyVol: netBuyVol,
                    NetSellVol: netSellVol,
                    NetBuyValue: netBuyValue,
                    NetSellValue: netSellValue,
                    // Volume fields (for backward compatibility)
                    TotalVol: buyerVol + sellerVol,
                    SellVol: sellerVol,
                    BuyVol: buyerVol,
                    // Value fields (for top buyer/seller calculation - same as BrokerSummaryPage)
                    BuyerValue: buyerValue,
                    SellerValue: sellerValue,
                    buyerValue: buyerValue,
                    sellerValue: sellerValue,
                    bavg,
                    savg,
                    NetBuyerAvg: netBuyerAvg,
                    NetSellerAvg: netSellerAvg,
                    date: dateStr,
                    time: dateStr
                  };
                });

                // Cache the data
                cache.set(cacheKey, brokerData, cache.topBrokers);
              } else {
                brokerData = [];
              }
            } else {
              // Update dates in cached data to match current date
              brokerData = brokerData.map((b: any) => ({ ...b, date: dateStr, time: dateStr }));
            }

            return brokerData || [];
          } catch (error) {
            // Silently return empty array on error - don't log to improve performance
            return [];
          }
        });

        // Wait for all promises to resolve in parallel
        const allDateResults = await Promise.all(datePromises);
        if (!isMounted) return;
        const allBrokerData = allDateResults.flat();

        // Filter final data by date range (additional safety check)
        const filteredBrokerData = allBrokerData.filter((record: any) => {
          const recordDate = record.date || record.time;
          return recordDate >= startDate && recordDate <= endDate;
        });

        if (filteredBrokerData.length === 0) {
          setBrokerDataError(`No broker summary data found for ${actualTicker} in date range ${startDate} to ${endDate}.`);
        } else {
          setBrokerDataError(null);
        }

        // Apply F/D filter client-side (same as BrokerSummaryPage)
        let finalBrokerData = filteredBrokerData;
        if (displayedFdFilter === 'Foreign') {
          finalBrokerData = filteredBrokerData.filter(row =>
            FOREIGN_BROKERS.includes(row.broker || row.BrokerCode || '') &&
            !GOVERNMENT_BROKERS.includes(row.broker || row.BrokerCode || '')
          );
        } else if (displayedFdFilter === 'Domestic') {
          finalBrokerData = filteredBrokerData.filter(row => {
            const broker = row.broker || row.BrokerCode || '';
            return !FOREIGN_BROKERS.includes(broker) || GOVERNMENT_BROKERS.includes(broker);
          });
        }

        setBrokerSummaryData(finalBrokerData);

        // Extract unique brokers from brokerSummaryData and use as fallback/merge for availableBrokersForStock
        // This ensures dropdown shows brokers even if listBrokerInventoryBrokers doesn't return data
        if (filteredBrokerData.length > 0) {
          const uniqueBrokersFromSummary = [...new Set(
            filteredBrokerData
              .map((r: any) => r.broker || r.BrokerCode)
              .filter(Boolean)
              .filter(isValidBrokerCode) // Filter for 2-letter codes or ALL
          )].sort() as string[];

          // Always merge brokers from brokerSummaryData with existing availableBrokersForStock
          // This ensures all brokers are available in dropdown
          setAvailableBrokersForStock(prev => {
            if (uniqueBrokersFromSummary.length > 0) {
              const merged = [...new Set([...prev, ...uniqueBrokersFromSummary])].sort();
              // Ensure "ALL" is first
              if (merged.includes('ALL')) {
                const allIndex = merged.indexOf('ALL');
                merged.splice(allIndex, 1);
                merged.unshift('ALL');
              } else {
                merged.unshift('ALL');
              }
              if (merged.length > prev.length) {
                console.log(`ðŸ“Š Merged brokers from brokerSummaryData: ${prev.length} -> ${merged.length} brokers`, {
                  prev: prev.length,
                  fromSummary: uniqueBrokersFromSummary.length,
                  merged: merged.length,
                  newBrokers: uniqueBrokersFromSummary.filter(b => !prev.includes(b))
                });
              }
              return merged;
            }
            return prev;
          });
        }

        // Reset shouldFetchData after successful load (will be reset again in loadBrokerInventory)

      } catch (error) {
        console.error('Error loading broker summary data:', error);
        setBrokerDataError(error instanceof Error ? error.message : 'Failed to load broker summary data');
        setShouldFetchData(false);
        // Don't set isDataReady to false on error - keep showing old data if available
        // Only set to false if there's no data at all
        if (brokerSummaryData.length === 0) {
          setIsDataReady(false);
        }
        showToast({
          type: 'error',
          title: 'Error Memuat Data Broker Summary',
          message: 'Gagal memuat data broker summary.'
        });
      } finally {
        setIsLoadingBrokerData(false);
        // Don't reset shouldFetchData here - reset it only after ALL data is loaded
      }
    };

    loadTopBrokersData();

    return () => {
      isMounted = false;
    };
    // Depend on shouldFetchData - only load when Show button is clicked
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetchData, getActualTicker, startDate, endDate, displayedMarket, displayedFdFilter, showToast]);

  // Load available brokers for selected stock code from broker_inventory folder
  useEffect(() => {
    let isMounted = true;
    const loadBrokersForStock = async () => {
      const actualTicker = getActualTicker;
      if (!actualTicker || !shouldFetchData) {
        return;
      }

      try {
        // Check cache first (10 minutes TTL)
        const cacheKey = `brokers-${actualTicker}`;
        const cachedBrokers = cache.get(cacheKey, cache.brokers, 10 * 60 * 1000);

        if (cachedBrokers) {
          console.log(`ðŸ“Š Using cached brokers for ${actualTicker}:`, cachedBrokers.length);
          // Ensure "ALL" is first in cached brokers
          const brokersWithAll = cachedBrokers.includes('ALL')
            ? (() => {
              const sorted = [...cachedBrokers];
              const allIndex = sorted.indexOf('ALL');
              sorted.splice(allIndex, 1);
              sorted.unshift('ALL');
              return sorted;
            })()
            : ['ALL', ...cachedBrokers];
          setAvailableBrokersForStock(brokersWithAll);
          syncSelectedBrokersWithAvailable(brokersWithAll);
          return;
        }

        console.log(`ðŸ“Š Loading available brokers for stock: ${actualTicker} from broker_inventory`);
        setIsLoadingBrokersForStock(true);

        // Get brokers from broker_inventory folder (direct Azure blob listing)
        const uniqueBrokers = (await listBrokerInventoryBrokers(actualTicker))
          .filter(isValidBrokerCode); // Filter for 2-letter codes or ALL

        if (!isMounted) return;

        // Ensure "ALL" is in the list and at the beginning
        const brokersWithAll = uniqueBrokers.includes('ALL')
          ? uniqueBrokers
          : ['ALL', ...uniqueBrokers];

        // Cache the brokers
        cache.set(cacheKey, brokersWithAll, cache.brokers);

        if (brokersWithAll.length > 0) {
          console.log(`âœ… Found ${brokersWithAll.length} brokers for ${actualTicker} from broker_inventory:`, brokersWithAll);
          setAvailableBrokersForStock(brokersWithAll);
          syncSelectedBrokersWithAvailable(brokersWithAll);
        } else {
          console.log(`âš ï¸ No brokers found for ${actualTicker} in broker_inventory - will use fallback from brokerSummaryData`);
          // Don't clear availableBrokersForStock here - let brokerSummaryData populate it as fallback
        }
      } catch (error) {
        console.error('Error loading brokers for stock:', error);
        // Don't clear availableBrokersForStock here - let brokerSummaryData populate it as fallback
        // setAvailableBrokersForStock([]);
        // setSelectedBrokers([]);
      } finally {
        setIsLoadingBrokersForStock(false);
      }
    };

    loadBrokersForStock();

    return () => {
      isMounted = false;
    };
    // Depend on shouldFetchData - only load when Show button is clicked
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetchData, getActualTicker]);

  // Broker search handlers
  const handleBrokerSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const actualTicker = getActualTicker;
    if (!actualTicker || isLoadingBrokersForStock) return; // Don't allow search if no stock selected or loading

    const value = e.target.value.toUpperCase();
    setBrokerSearch(value);
    setBrokerScrollOffset(0);
    setShowBrokerSuggestions(true);
    setHighlightedBrokerIndex(-1); // Reset highlighted index when search changes
  };

  const handleBrokerSelect = (broker: string) => {
    if (isTrial) {
      showToast({
        type: 'warning',
        title: 'Fitur Terbatas',
        message: 'Dalam masa free trial, Anda tidak dapat memilih broker spesifik.'
      });
      return;
    }

    // Only allow brokers that are available for the selected stock
    if (!availableBrokersForStock.includes(broker)) {
      console.warn(`âš ï¸ Broker ${broker} is not available for stock ${selectedTicker}`);
      return;
    }

    if (!selectedBrokers.includes(broker)) {
      const newSelectedBrokers = [...selectedBrokers, broker];
      setSelectedBrokers(newSelectedBrokers);
      
      setManuallySelectedBrokers(prev => {
        const next = new Set(prev);
        next.add(broker);
        return next;
      });
      
      setHasUserSelectedBrokers(true);
      
      // Open the relevant legend card based on broker category
      setBrokerSelectionMode(prev => {
        const top5BuySet = new Set(brokerNetStats.topBuyers.slice(0, 5).map(item => item.broker));
        const top5SellSet = new Set(brokerNetStats.topSellers.slice(0, 5).map(item => item.broker));
        const topTekTokSet = new Set(brokerNetStats.topTekToks.slice(0, 5).map(item => item.broker));
        
        if (top5BuySet.has(broker)) return { ...prev, top5buy: true };
        if (top5SellSet.has(broker)) return { ...prev, top5sell: true };
        if (topTekTokSet.has(broker)) return { ...prev, top5tektok: true };
        return { ...prev, custom: true };
      });
      
      setShouldFetchData(false);
      console.log(`ðŸ“Š Added broker ${broker} to selection. Total selected: ${newSelectedBrokers.length}`);
    }
    // Don't clear search or close suggestions to allow multiple selections
    // setBrokerSearch('');
    // setShowBrokerSuggestions(false);
    setHighlightedBrokerIndex(-1);
  };

  const removeBroker = (broker: string) => {
    // Only remove brokers that are currently selected
    if (selectedBrokers.includes(broker)) {
      console.log(`ðŸ—‘ï¸ Removing broker: ${broker} from selectedBrokers`);
      const newSelectedBrokers = selectedBrokers.filter(b => b !== broker);
      setSelectedBrokers(newSelectedBrokers);
      setManuallySelectedBrokers(prev => {
        const next = new Set(prev);
        next.delete(broker);
        return next;
      });
      setHasUserSelectedBrokers(true);
      // Reset shouldFetchData to prevent auto-reload until Show is clicked
      setShouldFetchData(false);
      console.log(`âœ… Broker removed. New selectedBrokers:`, newSelectedBrokers);

      // Check if we need to disable modes
      const top5BuySet = new Set(brokerNetStats.topBuyers.slice(0, 5).map(item => item.broker));
      const top5SellSet = new Set(brokerNetStats.topSellers.slice(0, 5).map(item => item.broker));

      // If removed broker was from top 5 buy, check if all top 5 buy brokers are gone
      if (top5BuySet.has(broker)) {
        const remainingTop5Buy = newSelectedBrokers.filter(b => top5BuySet.has(b));
        if (remainingTop5Buy.length === 0) {
          setBrokerSelectionMode(prev => ({ ...prev, top5buy: false }));
        }
      }

      // If removed broker was from top 5 sell, check if all top 5 sell brokers are gone
      if (top5SellSet.has(broker)) {
        const remainingTop5Sell = newSelectedBrokers.filter(b => top5SellSet.has(b));
        if (remainingTop5Sell.length === 0) {
          setBrokerSelectionMode(prev => ({ ...prev, top5sell: false }));
        }
      }

      // If removed broker was from top 5 tektok, check if all top 5 tektok brokers are gone
      const topTekTokSet = new Set(brokerNetStats.topTekToks.map(item => item.broker));
      if (topTekTokSet.has(broker)) {
        const remainingTopTekTok = newSelectedBrokers.filter(b => topTekTokSet.has(b));
        if (remainingTopTekTok.length === 0) {
          setBrokerSelectionMode(prev => ({ ...prev, top5tektok: false }));
        }
      }

      // If removed broker was custom, check if all custom brokers are gone
      if (!top5BuySet.has(broker) && !top5SellSet.has(broker) && !topTekTokSet.has(broker)) {
        const remainingCustom = newSelectedBrokers.filter(b => !top5BuySet.has(b) && !top5SellSet.has(b) && !topTekTokSet.has(b));
        if (remainingCustom.length === 0) {
          setBrokerSelectionMode(prev => ({ ...prev, custom: false }));
        }
      }

      setBrokerVisibility((prev) => {
        const updated = { ...prev };
        delete updated[broker];
        return updated;
      });
      console.log(`ðŸ“Š Removed broker ${broker} from selection - series will be updated automatically`);
    }
  };

  const removeAllTop5Buy = () => {
    const top5BuySet = new Set(brokerNetStats.topBuyers.slice(0, 5).map(item => item.broker));
    const top5SellSet = brokerSelectionMode.top5sell ? new Set(brokerNetStats.topSellers.slice(0, 5).map(item => item.broker)) : new Set();
    const topTekTokSet = brokerSelectionMode.top5tektok ? new Set(brokerNetStats.topTekToks.slice(0, 5).map(item => item.broker)) : new Set();
    
    // Only remove brokers that are NOT shared with other active modes or manually selected
    const brokersToRemove = selectedBrokers.filter(b => 
      top5BuySet.has(b) && 
      !top5SellSet.has(b) && 
      !topTekTokSet.has(b) && 
      !manuallySelectedBrokers.has(b)
    );

    if (brokersToRemove.length > 0) {
      const newSelectedBrokers = selectedBrokers.filter(b => !brokersToRemove.includes(b));
      setSelectedBrokers(newSelectedBrokers);
      setHasUserSelectedBrokers(true);
      setBrokerSelectionMode(prev => ({ ...prev, top5buy: false }));
      // Reset shouldFetchData to prevent auto-reload until Show is clicked
      setShouldFetchData(false);

      setBrokerVisibility((prev) => {
        const updated = { ...prev };
        brokersToRemove.forEach(broker => delete updated[broker]);
        return updated;
      });
      console.log(`ðŸ“Š Removed all Top 5 Buy brokers - series will be updated automatically`);
    } else {
      // No brokers to remove (all are shared), but still close the legend
      setBrokerSelectionMode(prev => ({ ...prev, top5buy: false }));
      setShouldFetchData(false);
    }
  };

  const removeAllTop5Sell = () => {
    const top5SellSet = new Set(brokerNetStats.topSellers.slice(0, 5).map(item => item.broker));
    const top5BuySet = brokerSelectionMode.top5buy ? new Set(brokerNetStats.topBuyers.slice(0, 5).map(item => item.broker)) : new Set();
    const topTekTokSet = brokerSelectionMode.top5tektok ? new Set(brokerNetStats.topTekToks.slice(0, 5).map(item => item.broker)) : new Set();
    
    // Only remove brokers that are NOT shared with other active modes or manually selected
    const brokersToRemove = selectedBrokers.filter(b => 
      top5SellSet.has(b) && 
      !top5BuySet.has(b) && 
      !topTekTokSet.has(b) && 
      !manuallySelectedBrokers.has(b)
    );

    if (brokersToRemove.length > 0) {
      const newSelectedBrokers = selectedBrokers.filter(b => !brokersToRemove.includes(b));
      setSelectedBrokers(newSelectedBrokers);
      setHasUserSelectedBrokers(true);
      setBrokerSelectionMode(prev => ({ ...prev, top5sell: false }));
      // Reset shouldFetchData to prevent auto-reload until Show is clicked
      setShouldFetchData(false);

      setBrokerVisibility((prev) => {
        const updated = { ...prev };
        brokersToRemove.forEach(broker => delete updated[broker]);
        return updated;
      });
      console.log(`ðŸ“Š Removed all Top 5 Sell brokers - series will be updated automatically`);
    } else {
      // No brokers to remove (all are shared), but still close the legend
      setBrokerSelectionMode(prev => ({ ...prev, top5sell: false }));
      setShouldFetchData(false);
    }
  };

  const removeAllTopTekTok = () => {
    const topTekTokSet = new Set(brokerNetStats.topTekToks.map(item => item.broker));
    // Preserve brokers that are also in top5buy or top5sell sets (if those modes are active)
    const top5BuySet = brokerSelectionMode.top5buy ? new Set(brokerNetStats.topBuyers.slice(0, 5).map(item => item.broker)) : new Set();
    const top5SellSet = brokerSelectionMode.top5sell ? new Set(brokerNetStats.topSellers.slice(0, 5).map(item => item.broker)) : new Set();
    
    // Only remove tektok brokers that are NOT also in top5buy, top5sell, or manually selected
    const brokersToRemove = selectedBrokers.filter(b =>
      topTekTokSet.has(b) && 
      !top5BuySet.has(b) && 
      !top5SellSet.has(b) && 
      !manuallySelectedBrokers.has(b)
    );

    if (brokersToRemove.length > 0) {
      const newSelectedBrokers = selectedBrokers.filter(b => !brokersToRemove.includes(b));
      setSelectedBrokers(newSelectedBrokers);
      setHasUserSelectedBrokers(true);
      setBrokerSelectionMode(prev => ({ ...prev, top5tektok: false }));
      // Reset shouldFetchData to prevent auto-reload until Show is clicked
      setShouldFetchData(false);

      setBrokerVisibility((prev) => {
        const updated = { ...prev };
        brokersToRemove.forEach(broker => delete updated[broker]);
        return updated;
      });
      console.log(`ðŸ“Š Removed all Top TekTok brokers - series will be updated automatically`);
    } else {
      // No brokers to remove (all tektok brokers are shared with top5buy/top5sell)
      setBrokerSelectionMode(prev => ({ ...prev, top5tektok: false }));
      setShouldFetchData(false);
    }
  };

  const removeAllCustom = () => {
    // 1. Remove them from the main selectedBrokers list
    setSelectedBrokers(prev => prev.filter(b => !customBrokers.includes(b)));
    
    // 2. Clear them from the manually selected tracking set
    setManuallySelectedBrokers(prev => {
      const next = new Set(prev);
      customBrokers.forEach(b => next.delete(b));
      return next;
    });
    
    // 3. Disable the custom legend card
    setBrokerSelectionMode(prev => ({ ...prev, custom: false }));
    
    // 4. Reset data fetching state to prevent auto-reload until Show is clicked
    setShouldFetchData(false);

    // 5. Clean up visibility preferences
    setBrokerVisibility((prev) => {
      const updated = { ...prev };
      customBrokers.forEach(broker => delete updated[broker]);
      return updated;
    });

    console.log(`ðŸ“Š Closed Selected Brokers card and cleared ${manuallySelectedBrokers.size} manual selections.`);
  };

  const handleBrokerKeyDown = (e: React.KeyboardEvent) => {
    if (isTrial) {
      if (['Enter', ' '].includes(e.key)) {
        e.preventDefault();
        showToast({
          type: 'warning',
          title: 'Fitur Terbatas',
          message: 'Dalam masa free trial, Anda tidak dapat memilih broker spesifik.'
        });
      }
      return;
    }
    if (!showBrokerSuggestions) return;

    // Calculate total items including "Top 5 Buy", "Top 5 Sell", and "Top Tektok" if they are visible
    const hasQuickSelect = brokerNetStats.topBuyers.length > 0 && brokerSearch === '';
    const quickSelectCount = hasQuickSelect ? 3 : 0; // "Top 5 Buy", "Top 5 Sell", "Top Tektok"
    const totalItems = quickSelectCount + filteredBrokers.length;

    if (totalItems === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedBrokerIndex(prev =>
          prev < totalItems - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedBrokerIndex(prev =>
          prev > 0 ? prev - 1 : totalItems - 1
        );
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (highlightedBrokerIndex >= 0) {
          // Handle "Top 5 Buy" (index 0) and "Top 5 Sell" (index 1)
          if (hasQuickSelect) {
            if (highlightedBrokerIndex === 0) {
              // Top 5 Buy
              const top5Buy = brokerNetStats.topBuyers.slice(0, 5).map(item => item.broker);
              const isCurrentlyActive = brokerSelectionMode.top5buy;
              if (isCurrentlyActive) {
                setSelectedBrokers(prev => prev.filter(b => !top5Buy.includes(b)));
                setBrokerSelectionMode(prev => ({ ...prev, top5buy: false }));
              } else {
                setSelectedBrokers(prev => {
                  const newSet = new Set([...prev, ...top5Buy]);
                  return Array.from(newSet);
                });
                setBrokerSelectionMode(prev => ({ ...prev, top5buy: true }));
              }
              setHasUserSelectedBrokers(false);
              setBrokerSearch('');
              // Reset shouldFetchData to prevent auto-reload until Show is clicked
              setShouldFetchData(false);
              return;
            } else if (highlightedBrokerIndex === 1) {
              // Top 5 Sell
              const top5Sell = brokerNetStats.topSellers.slice(0, 5).map(item => item.broker);
              const isCurrentlyActive = brokerSelectionMode.top5sell;
              if (isCurrentlyActive) {
                setSelectedBrokers(prev => prev.filter(b => !top5Sell.includes(b)));
                setBrokerSelectionMode(prev => ({ ...prev, top5sell: false }));
              } else {
                setSelectedBrokers(prev => {
                  const newSet = new Set([...prev, ...top5Sell]);
                  return Array.from(newSet);
                });
                setBrokerSelectionMode(prev => ({ ...prev, top5sell: true }));
              }
              setHasUserSelectedBrokers(false);
              setBrokerSearch('');
              // Reset shouldFetchData to prevent auto-reload until Show is clicked
              setShouldFetchData(false);
              console.log(`âœ… Top 5 Sell selected via keyboard:`, {
                top5Sell,
                count: top5Sell.length,
                willAppearInLegend: true,
                mode: 'top5sell'
              });
              return;
            } else if (highlightedBrokerIndex === 2) {
              // Top TekTok
              const topTekTok = brokerNetStats.topTekToks.map(item => item.broker);
              const isCurrentlyActive = brokerSelectionMode.top5tektok;
              if (isCurrentlyActive) {
                setSelectedBrokers(prev => prev.filter(b => !topTekTok.includes(b)));
                setBrokerSelectionMode(prev => ({ ...prev, top5tektok: false }));
              } else {
                setSelectedBrokers(prev => {
                  const newSet = new Set([...prev, ...topTekTok]);
                  return Array.from(newSet);
                });
                setBrokerSelectionMode(prev => ({ ...prev, top5tektok: true }));
              }
              setHasUserSelectedBrokers(false);
              setBrokerSearch('');
              // Reset shouldFetchData to prevent auto-reload until Show is clicked
              setShouldFetchData(false);
              console.log(`âœ… Top TekTok selected via keyboard:`, {
                topTekTok,
                count: topTekTok.length,
                willAppearInLegend: true,
                mode: 'toptektok'
              });
              return;
            }
          }

          // Handle regular brokers (adjust index if quick select is present)
          const brokerIndex = highlightedBrokerIndex - quickSelectCount;
          if (brokerIndex >= 0 && brokerIndex < filteredBrokers.length) {
            const selectedBroker = filteredBrokers[brokerIndex];
            if (selectedBroker && selectedBrokers.includes(selectedBroker)) {
              removeBroker(selectedBroker);
            } else if (selectedBroker) {
              handleBrokerSelect(selectedBroker);
            }
          }
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowBrokerSuggestions(false);
        setHighlightedBrokerIndex(-1);
        break;
      case 'Tab':
        // Allow default tab behavior but close dropdown
        setShowBrokerSuggestions(false);
        setHighlightedBrokerIndex(-1);
        break;
      case 'Home':
        e.preventDefault();
        setHighlightedBrokerIndex(0);
        break;
      case 'End':
        e.preventDefault();
        setHighlightedBrokerIndex(totalItems - 1);
        break;
      case 'PageUp':
        e.preventDefault();
        setHighlightedBrokerIndex(prev => Math.max(0, prev - 5));
        break;
      case 'PageDown':
        e.preventDefault();
        setHighlightedBrokerIndex(prev => Math.min(totalItems - 1, prev + 5));
        break;
      case 'Delete':
        e.preventDefault();
        if (highlightedBrokerIndex >= 0) {
          const brokerIndex = highlightedBrokerIndex - quickSelectCount;
          if (brokerIndex >= 0 && brokerIndex < filteredBrokers.length) {
            const selectedBroker = filteredBrokers[brokerIndex];
            if (selectedBroker && selectedBrokers.includes(selectedBroker)) {
              removeBroker(selectedBroker);
            }
          }
        }
        break;
      case 'Backspace':
        // Allow default backspace behavior for input field
        break;
      case 'ArrowLeft':
      case 'ArrowRight':
        // Allow default arrow behavior for input field
        break;
    }
  };

  const handleBrokerMouseEnter = (index: number) => {
    setHighlightedBrokerIndex(index);
  };


  const handleBrokerDropdownClose = () => {
    setShowBrokerSuggestions(false);
    setHighlightedBrokerIndex(-1);
  };

  // Handle click outside to close broker dropdown
  useEffect(() => {
    if (!showBrokerSuggestions) {
      return undefined;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check both class names to ensure dropdown closes properly
      if (!target.closest('.broker-dropdown-container') && !target.closest('input[role="combobox"]')) {
        handleBrokerDropdownClose();
      }
    };

    // Use a small delay to prevent immediate closing when opening
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showBrokerSuggestions]);

  // Handle click outside to close ticker dropdown
  useEffect(() => {
    if (!showStockSuggestions) {
      return undefined;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowStockSuggestions(false);
        setHighlightedStockIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showStockSuggestions]);


  // Ticker selection handler (single-select)
  const handleStockSelect = (stock: string) => {
    // Ensure stock is valid, default to BBCA if empty
    const validStock = stock && stock.trim() !== '' ? stock : 'BBCA';

    console.log(`[DEBUG Ticker] handleStockSelect called with "${stock}" -> validStock: "${validStock}" | current selectedTicker: "${selectedTicker}"`);

    // Don't update if same ticker
    if (validStock === selectedTicker) {
      console.log(`[DEBUG Ticker] Selected same ticker "${validStock}" - ignoring`);
      setTickerInput('');
      setShowStockSuggestions(false);
      return;
    }

    // Single select: just set the selected ticker
    setSelectedTicker(validStock);
    setTickerInput('');
    setShowStockSuggestions(false);

    // Keep chart & broker selection 100% frozen - no process happens on chart until Show is clicked
    setShouldFetchData(false);
    prevTickerForAutoFetchRef.current = validStock;
  };

  const handleStockInputChange = (value: string) => {
    const upperValue = value.toUpperCase();
    setTickerInput(upperValue);
    setShowStockSuggestions(true);

    // Clear previous timeout
    if (stockSearchTimeout) {
      clearTimeout(stockSearchTimeout);
      setStockSearchTimeout(null);
    }

    // If stocks not loaded yet, load them (no debounce needed - stocks already loaded on mount)
    // But if for some reason stocks are not loaded, try to load them
    if (availableStocks.length === 0) {
      const timeout = setTimeout(async () => {
        try {
          console.log('[BrokerInventory] Loading stock list on demand...');
          const [stockResult, sectorResult] = await Promise.all([
            api.getStockList(),
            fetchSectorMapping()
          ]);

          let stocks: string[] = [];
          let sectors: string[] = [];
          let mapping: { [sector: string]: string[] } = {};

          if (stockResult.success && stockResult.data?.stocks && Array.isArray(stockResult.data.stocks)) {
            stocks = stockResult.data.stocks;
          }

          if (sectorResult.success && sectorResult.data) {
            sectors = sectorResult.data.sectors || [];
            mapping = sectorResult.data.sectorMapping || {};
            setSectorMapping(mapping);
          }

          // Remove IDX from ticker list (IDX is now a sector, not a ticker)
          const stocksWithoutIdx = stocks.filter(stock => stock !== 'IDX');

          // Add sectors to the list (with prefix to distinguish from stocks)
          const sectorsWithPrefix = sectors.map(sector => `[SECTOR] ${sector}`);

          // Combine stocks and sectors, then sort alphabetically
          // Ensure IDX is always first
          const allItems = [...stocksWithoutIdx, ...sectorsWithPrefix].sort((a: string, b: string) => {
            // IDX always comes first
            if (a === '[SECTOR] IDX') return -1;
            if (b === '[SECTOR] IDX') return 1;
            return a.localeCompare(b);
          });

          setAvailableStocks(allItems);
        } catch (err) {
          console.error('Error loading stocks:', err);
          // Even if API fails, ensure IDX is available as sector
          setAvailableStocks(['[SECTOR] IDX']);
        }
      }, 100); // Short delay only if needed
      setStockSearchTimeout(timeout);
    }

    // (Auto-select exact match logic removed per user request, user must click explicitly)
  };

  // Debounce ticker input for better performance
  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedTickerInput(tickerInput);
      // Reset scroll offsets when search changes
      setStockScrollOffset(0);
      setSectorScrollOffset(0);
    }, 150);
    return () => clearTimeout(timeout);
  }, [tickerInput]);

  // Debounce broker search for better performance
  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedBrokerSearch(brokerSearch);
      // Reset scroll offset when search changes
      setBrokerScrollOffset(0);
    }, 150);
    return () => clearTimeout(timeout);
  }, [brokerSearch]);

  // Memoized filtered stocks list for better performance
  const filteredStocksList = useMemo(() => {
    const searchTerm = debouncedTickerInput.toLowerCase();
    return (availableStocks || []).filter(stock => {
      // Only include stocks (not sectors)
      if (stock.startsWith('[SECTOR] ')) {
        return false;
      }
      return stock.toLowerCase().includes(searchTerm);
    });
  }, [availableStocks, debouncedTickerInput]);

  // Memoized filtered sectors list for better performance
  const filteredSectorsList = useMemo(() => {
    const searchTerm = debouncedTickerInput.toLowerCase();
    return (availableStocks || []).filter(stock => {
      // Only include sectors
      if (stock.startsWith('[SECTOR] ')) {
        const sectorName = stock.replace('[SECTOR] ', '').toLowerCase();
        return sectorName.includes(searchTerm);
      }
      return false;
    });
  }, [availableStocks, debouncedTickerInput]);

  // Windowed stocks list (only show visible items)
  const visibleStocksList = useMemo(() => {
    const start = stockScrollOffset;
    const end = start + ITEMS_PER_PAGE;
    return filteredStocksList.slice(start, end);
  }, [filteredStocksList, stockScrollOffset]);

  // Windowed sectors list (only show visible items)
  const visibleSectorsList = useMemo(() => {
    const start = sectorScrollOffset;
    const end = start + ITEMS_PER_PAGE;
    return filteredSectorsList.slice(start, end);
  }, [filteredSectorsList, sectorScrollOffset]);

  // All stocks without filter (for initial display)
  const allStocksList = useMemo(() => {
    return (availableStocks || []).filter(s => !s.startsWith('[SECTOR] '));
  }, [availableStocks]);

  // All sectors without filter (for initial display)
  const allSectorsList = useMemo(() => {
    return (availableStocks || []).filter(s => s.startsWith('[SECTOR] '));
  }, [availableStocks]);

  // Windowed all stocks list (for initial display)
  const visibleAllStocksList = useMemo(() => {
    const start = stockScrollOffset;
    const end = start + ITEMS_PER_PAGE;
    return allStocksList.slice(start, end);
  }, [allStocksList, stockScrollOffset]);

  // Windowed all sectors list (for initial display)
  const visibleAllSectorsList = useMemo(() => {
    const start = sectorScrollOffset;
    const end = start + ITEMS_PER_PAGE;
    return allSectorsList.slice(start, end);
  }, [allSectorsList, sectorScrollOffset]);

  // Helper function to format display name (remove [SECTOR] prefix for display)
  const formatStockDisplayName = (stock: string): string => {
    if (stock.startsWith('[SECTOR] ')) {
      const sectorName = stock.replace('[SECTOR] ', '');
      // Replace IDX with IDX Composite for display
      return sectorName === 'IDX' ? 'IDX Composite' : sectorName;
    }
    return stock;
  };

  // Close dropdown when clicking outside (removed duplicate - already handled in separate useEffect above)

  // Use real data from API instead of mock data
  // Ensure data is sorted and deduplicated before passing to chart
  const candlestickData = useMemo(() => {
    if (!ohlcData || ohlcData.length === 0) return [];

    // Remove duplicates and sort by time
    const seen = new Set<string>();
    const uniqueData = ohlcData.filter((item: any) => {
      const timeKey = String(item.time);
      if (seen.has(timeKey)) {
        console.warn(`âš ï¸ Removing duplicate time in candlestickData: ${timeKey}`);
        return false;
      }
      seen.add(timeKey);
      return true;
    });

    // Sort by time (ascending)
    const sorted = uniqueData.sort((a: any, b: any) => {
      if (a.time < b.time) return -1;
      if (a.time > b.time) return 1;
      return 0;
    });

    return sorted;
  }, [ohlcData]);

  // OPTIMIZED: Progressive rendering - set isDataReady as soon as minimal data is available
  // This allows charts to render immediately instead of waiting for all data
  useEffect(() => {
    // Set isDataReady to true as soon as we have:
    // 1. OHLC data (for price chart)
    // 2. Broker summary data (for inventory chart and top brokers table)
    const minimalDataReady = !isLoadingData && !isLoadingBrokerData &&
      ohlcData.length > 0;

    if (minimalDataReady && !isDataReady) {
      setIsDataReady(true);
      // Update displayed ticker only when data is ready
      const currentTicker = getActualTicker || '';
      if (currentTicker && currentTicker !== displayedTicker) {
        setDisplayedTicker(currentTicker);
      }
      // Reset shouldFetchData only after data is ready
      setShouldFetchData(false);
    }
    // Don't set isDataReady to false during loading to prevent flicker - keep showing old data until new data is ready
  }, [isLoadingData, isLoadingBrokerData, ohlcData.length, brokerSummaryData.length, selectedBrokers.length, isDataReady, getActualTicker, displayedTicker]);

  // Auto-refresh chart data when window size changes (debounced) - as requested by user
  // "maksudnya ketika ukuran layar berubah pastikan langsung di klik show lagi"
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const handleResize = () => {
      // Clear existing timeout
      if (timeoutId) clearTimeout(timeoutId);

      // Only attempt refresh if we have valid data conditions (similar to Show button disabled state)
      // and if we are not currently loading
      if (isLoadingData || isLoadingBrokerData || selectedBrokers.length === 0 || !startDate || !endDate || !selectedTicker) {
        return;
      }

      // 500ms debounce
      timeoutId = setTimeout(() => {
        console.log('ðŸ”„ Window resized, triggering auto-refresh (simulating Show click)...');
        // Trigger fetch logic identical to Show button
        setDisplayedFdFilter(fdFilter);
        setDisplayedMarket(marketFilter);
        setShouldFetchData(true);
        setIsDataReady(false);
        setIsLoadingData(true);
        setIsLoadingBrokerData(true);
      }, 500);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isLoadingData, isLoadingBrokerData, selectedBrokers.length, startDate, endDate, selectedTicker, fdFilter, marketFilter]);

  // Convert broker summary data to time series format for chart (same as BrokerSummaryPage NET table)
  // Uses NetBuyVol and NetSellVol from brokerSummaryData (same data source as NET table)
  // OPTIMIZED: Use Map-based lookup instead of nested loops for O(1) performance
  const inventoryData = useMemo(() => {
    if (selectedBrokers.length === 0 || !brokerSummaryData || brokerSummaryData.length === 0) {
      return [];
    }

    // OPTIMIZED: Create a Map for O(1) lookup: key = `${date}-${broker}`, value = row data
    const dataMap = new Map<string, any>();
    const allDates = new Set<string>();

    brokerSummaryData.forEach((row: any) => {
      const date = row.date || row.time || row.Date;
      const broker = row.broker || row.BrokerCode;
      if (date && broker) {
        allDates.add(date);
        const key = `${date}-${broker}`;
        dataMap.set(key, row);
      }
    });

    const sortedDates = Array.from(allDates).sort();
    const inventorySeries: InventoryTimeSeries[] = [];

    sortedDates.forEach(date => {
      const dayData: InventoryTimeSeries = { time: date };

      // OPTIMIZED: Use Map lookup instead of filter for O(1) instead of O(n)
      selectedBrokers.forEach(broker => {
        const key = `${date}-${broker}`;
        const row = dataMap.get(key);

        if (row) {
          // Get NetBuyVol and NetSellVol (same as NET table)
          const netBuyVol = typeof row.NetBuyVol === 'number' ? row.NetBuyVol : parseFloat(String(row.NetBuyVol || 0)) || 0;
          const netSellVol = typeof row.NetSellVol === 'number' ? row.NetSellVol : parseFloat(String(row.NetSellVol || 0)) || 0;
          const netBuyValue = typeof row.NetBuyValue === 'number' ? row.NetBuyValue : parseFloat(String(row.NetBuyValue || 0)) || 0;
          const netSellValue = typeof row.NetSellValue === 'number' ? row.NetSellValue : parseFloat(String(row.NetSellValue || 0)) || 0;

          // Determine if broker is NetBuy or NetSell (same logic as NET table per-date)
          let netVolume = 0;
          if (netBuyValue >= netSellValue && (netBuyVol > 0 || netBuyValue > 0)) {
            netVolume = netBuyVol;
          } else if (netSellValue > netBuyValue && (netSellVol > 0 || netSellValue > 0)) {
            netVolume = -netSellVol;
          }

          dayData[broker] = netVolume;
        } else {
          dayData[broker] = 0;
        }
      });

      inventorySeries.push(dayData);
    });

    return inventorySeries;
  }, [brokerSummaryData, selectedBrokers]);

  const volumeDataForCharts = useMemo(() => {
    if (!volumeData || volumeData.length === 0) return [];

    // Remove duplicates and sort by time
    const seen = new Set<string>();
    const uniqueData = volumeData.filter((item: any) => {
      const timeKey = String(item.time);
      if (seen.has(timeKey)) {
        console.warn(`âš ï¸ Removing duplicate time in volumeData: ${timeKey}`);
        return false;
      }
      seen.add(timeKey);
      return true;
    });

    // Sort by time (ascending)
    const sorted = uniqueData.sort((a: any, b: any) => {
      if (a.time < b.time) return -1;
      if (a.time > b.time) return 1;
      return 0;
    });

    return sorted;
  }, [volumeData]);

  const brokerNetStats = useMemo(() => {
    if (!brokerSummaryData || brokerSummaryData.length === 0) {
      return {
        netByBroker: new Map<string, number>(),
        buyVolByBroker: new Map<string, number>(),
        sellVolByBroker: new Map<string, number>(),
        topBuyers: [] as Array<{ broker: string; buy: number }>,
        topSellers: [] as Array<{ broker: string; sell: number }>,
        topTekToks: [] as Array<{ broker: string; vol: number; algos?: string; netLot?: number }>,
        topBuyerSet: new Set<string>(),
        topSellerSet: new Set<string>(),
        topTekTokSet: new Set<string>(),
      };
    }

    const netByBroker = new Map<string, number>();
    const buyVolByBroker = new Map<string, number>();
    const sellVolByBroker = new Map<string, number>();
    const tektokByBroker = new Map<string, number>();
    const brokerNetBuyTotals: { [broker: string]: { nblot: number; nbval: number } } = {};
    const brokerNetSellTotals: { [broker: string]: { nslot: number; nsval: number } } = {};

    brokerSummaryData.forEach((record) => {
      const broker = record.broker || record.BrokerCode;
      if (!broker) return;

      const buyVol = typeof record.BuyVol === 'number' ? record.BuyVol : parseFloat(String(record.BuyVol ?? 0)) || 0;
      const sellVol = typeof record.SellVol === 'number' ? record.SellVol : parseFloat(String(record.SellVol ?? 0)) || 0;
      
      const netBuyVol = typeof record.NetBuyVol === 'number' ? record.NetBuyVol : parseFloat(String(record.NetBuyVol ?? 0)) || 0;
      const netSellVol = typeof record.NetSellVol === 'number' ? record.NetSellVol : parseFloat(String(record.NetSellVol ?? 0)) || 0;
      const netBuyVal = typeof record.NetBuyValue === 'number' ? record.NetBuyValue : parseFloat(String(record.NetBuyValue ?? 0)) || 0;
      const netSellVal = typeof record.NetSellValue === 'number' ? record.NetSellValue : parseFloat(String(record.NetSellValue ?? 0)) || 0;

      buyVolByBroker.set(broker, (buyVolByBroker.get(broker) || 0) + buyVol);
      sellVolByBroker.set(broker, (sellVolByBroker.get(broker) || 0) + sellVol);
      
      // TekTok calculation: min(BuyVol, SellVol)
      const tektokVol = Math.min(buyVol, sellVol);
      tektokByBroker.set(broker, (tektokByBroker.get(broker) || 0) + tektokVol);

      if (!brokerNetBuyTotals[broker]) brokerNetBuyTotals[broker] = { nblot: 0, nbval: 0 };
      if (!brokerNetSellTotals[broker]) brokerNetSellTotals[broker] = { nslot: 0, nsval: 0 };

      brokerNetBuyTotals[broker].nblot += netBuyVol;
      brokerNetBuyTotals[broker].nbval += netBuyVal;
      brokerNetSellTotals[broker].nslot += netSellVol;
      brokerNetSellTotals[broker].nsval += netSellVal;
    });

    // Determine final side for each broker based on which total is larger (same as BrokerSummaryPage NET table)
    const totalNetBuyData: { [broker: string]: { nblot: number; nbval: number } } = {};
    const totalNetSellData: { [broker: string]: { nslot: number; nsval: number } } = {};

    const allBrokers = new Set([...Object.keys(brokerNetBuyTotals), ...Object.keys(brokerNetSellTotals)]);
    allBrokers.forEach(broker => {
      const bTotal = brokerNetBuyTotals[broker] ?? { nblot: 0, nbval: 0 };
      const sTotal = brokerNetSellTotals[broker] ?? { nslot: 0, nsval: 0 };

      const netLot = bTotal.nblot - sTotal.nslot;
      const netVal = bTotal.nbval - sTotal.nsval;

      // Ensure netByBroker gets populated for accumulation ratio logic
      netByBroker.set(broker, netLot);

      // Side determination based on Net Lot (Volume)
      if (netLot > 0) {
        // Net Buy
        totalNetBuyData[broker] = { nblot: netLot, nbval: Math.max(0, netVal) };
      } else if (netLot < 0) {
        // Net Sell
        totalNetSellData[broker] = { nslot: -netLot, nsval: Math.max(0, -netVal) };
      } else if (netVal > 0) {
        // Tie-break: Net Buy by Value
        totalNetBuyData[broker] = { nblot: 0, nbval: netVal };
      } else if (netVal < 0) {
        // Tie-break: Net Sell by Value
        totalNetSellData[broker] = { nslot: 0, nsval: -netVal };
      }
    });

    const sortedTotalNetBuy = Object.entries(totalNetBuyData)
      .filter(([, data]) => data.nblot > 0 || data.nbval > 0) // Include if has either Lot or Val (Lot takes priority)
      .map(([broker, data]) => ({
        broker,
        nblot: data.nblot,
        nbval: data.nbval,
      }))
      .sort((a, b) => (b.nblot - a.nblot) || (b.nbval - a.nbval));

    const sortedTotalNetSell = Object.entries(totalNetSellData)
      .filter(([, data]) => data.nslot > 0 || data.nsval > 0) // Include if has either Lot or Val
      .map(([broker, data]) => ({
        broker,
        nslot: data.nslot,
        nsval: data.nsval,
      }))
      .sort((a, b) => (b.nslot - a.nslot) || (b.nsval - a.nsval));

    // Standard 1:1 Mapping
    // - Top 5 Buyers = Top 5 from sortedTotalNetBuy
    // - Top 5 Sellers = Top 5 from sortedTotalNetSell
    const topBuyers = sortedTotalNetBuy
      .slice(0, 5)
      .map(({ broker, nbval, nblot }) => ({ broker, buy: nbval, lot: nblot }));

    const topSellers = sortedTotalNetSell
      .slice(0, 5)
      .map(({ broker, nsval, nslot }) => ({ broker, sell: nsval, lot: nslot }));

    const topBuyerBrokers = new Set(topBuyers.map(b => b.broker));
    const topSellerBrokers = new Set(topSellers.map(b => b.broker));

    // Sisa Barang calculation: 100% - Accumulation Ratio
    const totalNetVol = Array.from(netByBroker.values()).reduce((sum, val) => sum + val, 0);
    const accumulationRatio = stockShares > 0 ? (totalNetVol * 100) / (stockShares / 100) : 0;
    const inventoryRatio = 100 - accumulationRatio;
    const isInventoryEligible = stockShares === 0 || inventoryRatio < 35;

    // NEW Top Tektok Logic:
    const topTekToks = (() => {
      const brokerStats = new Map<string, { 
        totalValue: number; 
        totalLot: number; 
        netLot: number; 
        algos: string[];
      }>();
      
      // Pre-calculate total value per broker
      const totalValueMap = new Map<string, number>();
      brokerSummaryData.forEach(r => {
        const b = r.broker || r.BrokerCode;
        if (!b) return;
        const val = Number(r.BuyerValue || r.buyerValue || 0) + Number(r.SellerValue || r.sellerValue || 0);
        totalValueMap.set(b, (totalValueMap.get(b) || 0) + val);
      });

      const allUniqueBrokers = new Set([...buyVolByBroker.keys(), ...sellVolByBroker.keys()]);
      
      allUniqueBrokers.forEach(broker => {
        const bLot = buyVolByBroker.get(broker) || 0;
        const sLot = sellVolByBroker.get(broker) || 0;
        const tLot = bLot + sLot;
        const nLot = Math.abs(bLot - sLot);
        const tVal = totalValueMap.get(broker) || 0;
        
        brokerStats.set(broker, {
          totalValue: tVal,
          totalLot: tLot,
          netLot: nLot,
          algos: []
        });
      });

      // Sorted by Total Value for Algo 1
      const sortedByTotalValue = Array.from(brokerStats.entries())
        .map(([broker, data]) => ({ broker, ...data }))
        .sort((a, b) => b.totalValue - a.totalValue);

      const algo1Set = new Set<string>();
      const algo2Set = new Set<string>();

      // Algo 1 - Buyer side (High Gross - Low Net)
      if (sortedTotalNetBuy.length > 0) {
        const compareIdx = Math.min(4, sortedTotalNetBuy.length - 1);
        const rankComparisonBroker = sortedTotalNetBuy[compareIdx]?.broker;
        if (rankComparisonBroker) {
          const rankComparisonInValueIdx = sortedByTotalValue.findIndex(b => b.broker === rankComparisonBroker);
          
          if (rankComparisonInValueIdx !== -1) {
            for (let i = 0; i <= rankComparisonInValueIdx; i++) {
              const item = sortedByTotalValue[i];
              if (!item) continue;
              const b = item.broker;
              // Algo 1 EXCLUDES top 5 buyers and sellers
              if (topBuyerBrokers.has(b) || topSellerBrokers.has(b)) continue;
              
              algo1Set.add(b);
              if (!brokerStats.get(b)?.algos.includes("High Gross-Low Net")) {
                brokerStats.get(b)?.algos.push("High Gross-Low Net");
              }
            }
          }
        }
      }

      // Algo 1 - Seller side
      if (sortedTotalNetSell.length > 0) {
        const compareIdx = Math.min(4, sortedTotalNetSell.length - 1);
        const rankComparisonBroker = sortedTotalNetSell[compareIdx]?.broker;
        if (rankComparisonBroker) {
          const rankComparisonInValueIdx = sortedByTotalValue.findIndex(b => b.broker === rankComparisonBroker);
          
          if (rankComparisonInValueIdx !== -1) {
            for (let i = 0; i <= rankComparisonInValueIdx; i++) {
              const item = sortedByTotalValue[i];
              if (!item) continue;
              const b = item.broker;
              // Algo 1 EXCLUDES top 5 buyers and sellers
              if (topBuyerBrokers.has(b) || topSellerBrokers.has(b)) continue;

              algo1Set.add(b);
              if (!brokerStats.get(b)?.algos.includes("High Gross-Low Net")) {
                brokerStats.get(b)?.algos.push("High Gross-Low Net");
              }
            }
          }
        }
      }

      // Algo 2 - Buy & Sell Ratio < 35% (using Max/Min-1 from Lot)
      brokerStats.forEach((_data, broker) => {
        const bLot = buyVolByBroker.get(broker) || 0;
        const sLot = sellVolByBroker.get(broker) || 0;
        const maxLot = Math.max(bLot, sLot);
        const minLot = Math.min(bLot, sLot);

        // Ratio = (Max/Min) - 1. Tektok if < 0.35 (35%)
        if (minLot > 0 && (maxLot / minLot - 1) < 0.35) {
          algo2Set.add(broker);
          if (!brokerStats.get(broker)?.algos.includes("Buy & Sell Ratio < 35%")) {
            brokerStats.get(broker)?.algos.push("Buy & Sell Ratio < 35%");
          }
        }
      });

      const detected = Array.from(brokerStats.entries())
        .filter(([broker]) => algo1Set.has(broker) || algo2Set.has(broker))
        .map(([broker, data]) => {
          const bLot = buyVolByBroker.get(broker) || 0;
          const sLot = sellVolByBroker.get(broker) || 0;
          const maxLot = Math.max(bLot, sLot);
          const minLot = Math.min(bLot, sLot);
          const ratio = minLot > 0 ? (maxLot / minLot - 1) : 999;
          
          return {
            broker,
            vol: data.totalLot,
            value: data.totalValue,
            algos: data.algos.join(", "),
            ratio: ratio,
            netLot: (totalNetBuyData[broker]?.nblot || 0) - (totalNetSellData[broker]?.nslot || 0),
            netValue: (totalNetBuyData[broker]?.nbval || 0) - (totalNetSellData[broker]?.nsval || 0)
          };
        });

      // If we have 5 or more detected tektoks, just sort and return by Volume
      if (detected.length >= 5) {
        return detected.sort((a, b) => b.vol - a.vol);
      }

      // If less than 5, fill with brokers having highest total volume
      const detectedSet = new Set(detected.map(d => d.broker));
      const fillers = Array.from(brokerStats.entries())
        .filter(([broker]) => !detectedSet.has(broker) && !topBuyerBrokers.has(broker) && !topSellerBrokers.has(broker))
        .sort((a, b) => b[1].totalValue - a[1].totalValue)
        .slice(0, 5 - detected.length)
        .map(([broker, data]) => {
          const bLot = buyVolByBroker.get(broker) || 0;
          const sLot = sellVolByBroker.get(broker) || 0;
          const maxLot = Math.max(bLot, sLot);
          const minLot = Math.min(bLot, sLot);
          const ratio = minLot > 0 ? (maxLot / minLot - 1) : 999;
          
          return {
            broker,
            vol: data.totalLot,
            value: data.totalValue,
            algos: "Value Terbesar",
            ratio: ratio,
            netLot: (totalNetBuyData[broker]?.nblot || 0) - (totalNetSellData[broker]?.nslot || 0),
            netValue: (totalNetBuyData[broker]?.nbval || 0) - (totalNetSellData[broker]?.nsval || 0)
          };
        });

      return [...detected, ...fillers].sort((a, b) => b.vol - a.vol);
    })();

    return {
      netByBroker,
      buyVolByBroker,
      sellVolByBroker,
      topBuyers,
      topSellers,
      topTekToks,
      topBuyerSet: new Set(topBuyers.map(item => item.broker)),
      topSellerSet: new Set(topSellers.map(item => item.broker)),
      topTekTokSet: new Set(topTekToks.map(item => item.broker)),
      isInventoryEligible,
      inventoryRatio
    };
  }, [brokerSummaryData, stockShares]);

  // Separate brokers into different groups
  const top5BuyBrokers = useMemo(() => {
    if (!brokerSelectionMode.top5buy) return [];
    return brokerNetStats.topBuyers
      .slice(0, 5)
      .map(item => item.broker)
      .filter(broker => Boolean(broker) && !manuallySelectedBrokers.has(broker));
  }, [brokerSelectionMode.top5buy, brokerNetStats.topBuyers, manuallySelectedBrokers]);

  const top5SellBrokers = useMemo(() => {
    if (!brokerSelectionMode.top5sell) return [];
    return brokerNetStats.topSellers
      .slice(0, 5)
      .map(item => item.broker)
      .filter(broker => Boolean(broker) && !manuallySelectedBrokers.has(broker));
  }, [brokerSelectionMode.top5sell, brokerNetStats.topSellers, manuallySelectedBrokers]);

  const topTekTokBrokers = useMemo(() => {
    if (!brokerSelectionMode.top5tektok) return [];
    
    // Only exclude from Tektok legend if they are ALREADY being shown in active Buy/Sell legends
    const shownInBuy = brokerSelectionMode.top5buy ? new Set(brokerNetStats.topBuyers.slice(0, 5).map(item => item.broker)) : new Set<string>();
    const shownInSell = brokerSelectionMode.top5sell ? new Set(brokerNetStats.topSellers.slice(0, 5).map(item => item.broker)) : new Set<string>();
    
    return brokerNetStats.topTekToks
      .slice(0, 5)
      .map(item => item.broker)
      .filter(broker => 
        Boolean(broker) &&
        !shownInBuy.has(broker) && 
        !shownInSell.has(broker) &&
        !manuallySelectedBrokers.has(broker)
      );
  }, [brokerSelectionMode.top5tektok, brokerSelectionMode.top5buy, brokerSelectionMode.top5sell, brokerNetStats.topTekToks, brokerNetStats.topBuyers, brokerNetStats.topSellers, manuallySelectedBrokers]);

  const customBrokers = useMemo(() => {
    // Determine which brokers are already being displayed in other active legend sections
    const shownInBuy = brokerSelectionMode.top5buy ? new Set(brokerNetStats.topBuyers.slice(0, 5).map(item => item.broker)) : new Set<string>();
    const shownInSell = brokerSelectionMode.top5sell ? new Set(brokerNetStats.topSellers.slice(0, 5).map(item => item.broker)) : new Set<string>();
    
    // For Tektok, we need to know which ones are actually rendered in the tektok section (Top 5)
    const shownInTektok = brokerSelectionMode.top5tektok 
      ? new Set(
          brokerNetStats.topTekToks
            .map(item => item.broker)
            .filter(b => !shownInBuy.has(b) && !shownInSell.has(b))
            .slice(0, 5)
        )
      : new Set<string>();
    
    // Custom brokers are those selected that are NOT shown in any other active section,
    // OR any broker that was explicitly manually selected by the user.
    return selectedBrokers.filter(broker => {
      if (manuallySelectedBrokers.has(broker)) return true;
      return !shownInBuy.has(broker) && !shownInSell.has(broker) && !shownInTektok.has(broker);
    });
  }, [selectedBrokers, brokerSelectionMode, brokerNetStats, manuallySelectedBrokers]);

  // Centralized color map — single source of truth for all broker plot colors.
  // Both TradingViewChart and BrokerLegend read from this map so colors always match.
  const brokerColorMap = useMemo(() => {
    const map = new Map<string, string>();
    const tektokNames = brokerNetStats.topTekToks.map(item => item.broker);

    selectedBrokers.forEach(broker => {
      map.set(broker, generateBrokerColor(broker, selectedBrokers, top5BuyBrokers, top5SellBrokers, tektokNames));
    });

    return map;
  }, [selectedBrokers, top5BuyBrokers, top5SellBrokers, brokerNetStats.topTekToks]);

  const topTekTokBrokerNames = useMemo(
    () => brokerNetStats.topTekToks.map(t => t.broker),
    [brokerNetStats.topTekToks]
  );

  const tektokMetadata = useMemo(
    () => Object.fromEntries(
      brokerNetStats.topTekToks.map(t => [
        t.broker,
        {
          tooltip: t.algos,
          netStatus: t.netLot === undefined ? undefined : t.netLot > 0 ? 'Net Buy' : t.netLot < 0 ? 'Net Sell' : 'Net Zero'
        }
      ])
    ),
    [brokerNetStats.topTekToks]
  );

  // Stable broker-name array for TradingViewChart's effect deps.
  // forcing the chart's main useEffect to re-run (recreate chart, re-fitContent)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const visibleBrokerKey = useMemo(() => visibleBrokers.slice().sort().join(','), [visibleBrokers]);

  // Auto-select default top 5 buy + sell brokers when data arrives and user hasn't chosen manually
  useEffect(() => {
    if (isTrial) {
      // For trial users, force top 5 buy + sell
      setBrokerSelectionMode(prev => {
        if (prev.top5buy && prev.top5sell && !prev.top5tektok && !prev.custom) return prev;
        return {
          top5buy: true,
          top5sell: true,
          top5tektok: false,
          custom: false
        };
      });

      // Force selected brokers to union of Top 5 Buyers and Top 5 Sellers
      if (brokerNetStats?.topBuyers && brokerNetStats?.topSellers) {
        const topBuy = brokerNetStats.topBuyers
          .map(({ broker }) => broker)
          .filter((broker): broker is string => Boolean(broker))
          .slice(0, 5);

        const topSell = brokerNetStats.topSellers
          .map(({ broker }) => broker)
          .filter((broker): broker is string => Boolean(broker))
          .slice(0, 5);

        const combined = Array.from(new Set([...topBuy, ...topSell]));
        
        const isDifferent = combined.length !== selectedBrokers.length || 
                            !combined.every(b => selectedBrokers.includes(b));
        if (isDifferent && combined.length > 0) {
          setSelectedBrokers(combined);
        }
      }
    }
  }, [isTrial, brokerNetStats, selectedBrokers]);

  // Set default visibility: all selected brokers are visible by default
  useEffect(() => {
    if (!brokerNetStats || brokerNetStats.topBuyers.length === 0 || brokerNetStats.topSellers.length === 0) {
      return;
    }

    const localTop5BuyBrokers = brokerNetStats.topBuyers.slice(0, 5).map(item => item.broker);
    const localTop5SellBrokers = brokerNetStats.topSellers.slice(0, 5).map(item => item.broker);
    const hasTop5Buy = localTop5BuyBrokers.every(broker => selectedBrokers.includes(broker));
    const hasTop5Sell = localTop5SellBrokers.every(broker => selectedBrokers.includes(broker));

    if (hasTop5Buy && hasTop5Sell && brokerSelectionMode.top5buy && brokerSelectionMode.top5sell) {
      setBrokerVisibility((prev) => {
        const updated = { ...prev };
        let changed = false;

        selectedBrokers.forEach((broker) => {
          if (updated[broker] === false || updated[broker] === undefined) {
            updated[broker] = true;
            changed = true;
          }
        });

        return changed ? updated : prev;
      });
    }
  }, [brokerNetStats, selectedBrokers, brokerSelectionMode]);

  useEffect(() => {
    if (!brokerSummaryData.length || !startDate || !endDate) return;

    const topBuy = brokerSelectionMode.top5buy
      ? brokerNetStats.topBuyers
          .map(({ broker }) => broker)
          .filter((broker): broker is string => Boolean(broker))
          .slice(0, 5)
      : [];

    const topSell = brokerSelectionMode.top5sell
      ? brokerNetStats.topSellers
          .map(({ broker }) => broker)
          .filter((broker): broker is string => Boolean(broker))
          .slice(0, 5)
      : [];

    const topTekTok = brokerSelectionMode.top5tektok
      ? (brokerNetStats.topTekToks || [])
          .map(({ broker }) => broker)
          .filter((broker): broker is string => Boolean(broker))
          .slice(0, 5)
      : [];

    const combinedDefaults = Array.from(new Set([...topBuy, ...topSell, ...topTekTok]));
    setDefaultBrokers(combinedDefaults);

    if (combinedDefaults.length > 0) {
      setSelectedBrokers(prev => {
        const union = Array.from(new Set([...prev, ...combinedDefaults]));
        if (union.length === prev.length && union.every(b => prev.includes(b))) {
          return prev;
        }
        return union;
      });
    }
  }, [
    brokerSummaryData,
    startDate,
    endDate,
    hasUserSelectedBrokers,
    selectedBrokers,
    brokerSelectionMode,
  ]);


  // Generate unique colors for brokers (dark colors with high contrast for white text)
  // Ensures each broker gets a unique, highly contrasting color
  const generateUniqueBrokerColor = (broker: string | undefined | null, allBrokers: string[], topBuyers: string[] = [], topSellers: string[] = [], topTektoks: string[] = []): string => {
    // Handle undefined/null broker
    if (!broker || typeof broker !== 'string') {
      return 'hsl(0, 0%, 35%)'; // Return dark gray color for invalid broker
    }

    // Check if broker is in Top 5 Buyers
    const topBuyerIndex = topBuyers.indexOf(broker);
    if (topBuyerIndex !== -1 && topBuyerIndex < TOP_BUYER_COLORS.length) {
      return TOP_BUYER_COLORS[topBuyerIndex] as string;
    }

    // Check if broker is in Top 5 Sellers - use the same color palette as buyers
    const topSellerIndex = topSellers.indexOf(broker);
    if (topSellerIndex !== -1 && topSellerIndex < TOP_BUYER_COLORS.length) {
      return TOP_BUYER_COLORS[topSellerIndex] as string;
    }

    // Check if broker is in Top TekToks - use the same color palette but FADED (80% opacity)
    const topTektokIndex = topTektoks.indexOf(broker);
    if (topTektokIndex !== -1 && topTektokIndex < TOP_BUYER_COLORS.length) {
      const baseColor = TOP_BUYER_COLORS[topTektokIndex] ?? '';
      const colorWithoutAlpha = baseColor.startsWith('#') ? baseColor.slice(0, 7) : baseColor;
      return `${colorWithoutAlpha}cc`;
    }

    const sortedBrokers = [...new Set(allBrokers)].sort();
    const brokerIndex = sortedBrokers.indexOf(broker);

    // Create a hash from broker code for more random distribution
    const brokerHash = broker.split('').reduce((acc, char, idx) => {
      return acc + char.charCodeAt(0) * (idx + 1);
    }, 0);

    if (brokerIndex === -1) {
      // Fallback for unknown brokers
      const hue = brokerHash % 360;
      return `hsl(${hue}, 75%, 30%)`; // Darker for better text contrast
    }

    // Use both brokerIndex and brokerHash to ensure unique colors
    // This combination ensures different brokers get different colors even if they're close in index

    // Calculate hue with larger variation to ensure contrast
    // Distribute hues evenly across 360 degrees with minimum spacing
    const totalBrokers = sortedBrokers.length;
    const baseHueStep = 360 / Math.max(totalBrokers, 1);
    const baseHue = (brokerIndex * baseHueStep) % 360;

    // Add significant variation from hash to differentiate brokers with similar indices
    // This ensures brokers get unique colors even if they're adjacent in the sorted list
    const hashHueVariation = (brokerHash % 40) - 20; // -20 to +20 degrees variation
    const finalHue = (baseHue + hashHueVariation + 360) % 360;

    // Use hash to create more variation in saturation
    // Wider saturation range (65-90%) for better distinction
    const satHash = brokerHash % 26; // 0-25
    const saturation = 65 + satHash; // 65-90% saturation

    // Use hash to create more variation in lightness
    // Wider lightness range (22-35%) for better distinction
    const lightHash = (brokerHash * 7) % 14; // 0-13
    const lightness = 22 + lightHash; // 22-35% lightness (dark enough for white text)

    return `hsl(${Math.round(finalHue)}, ${saturation}%, ${lightness}%)`;
  };

  // Generate Top Brokers data by date from brokerSummaryData (from top_broker API)
  // OPTIMIZED: Use Map for grouping and pre-compute broker colors
  const topBrokersData = useMemo(() => {
    if (!brokerSummaryData || brokerSummaryData.length === 0) return [];

    // Get top 5 buy and top 5 sell brokers for coloring
    const localBuyBrokers = brokerNetStats.topBuyers.slice(0, 5).map(item => item.broker);
    const localSellBrokers = brokerNetStats.topSellers.slice(0, 5).map(item => item.broker);

    // OPTIMIZED: Get all unique brokers once and pre-compute colors
    const allBrokers = [...new Set(brokerSummaryData.map(r => r.broker).filter((b): b is string => Boolean(b) && typeof b === 'string'))];
    const brokerColorCache = new Map<string, string>();
    const getBrokerColor = (broker: string) => {
      if (!brokerColorCache.has(broker)) {
        brokerColorCache.set(broker, generateUniqueBrokerColor(broker, allBrokers, localBuyBrokers, localSellBrokers, topTekTokBrokers));
      }
      return brokerColorCache.get(broker)!;
    };

    // Helper to determine text color
    const getTextColor = (bgColor: string) => {
      const c = bgColor.toUpperCase();
      // User Request: Yellow, Green, Orange should have Black text. Blue should be White.
      if (['#FFFF00', '#00FF00', '#FFA500'].includes(c)) {
        return '#000000';
      }
      return '#FFFFFF';
    };

    // OPTIMIZED: Use Map for O(1) grouping instead of object with repeated checks
    const dataByDate = new Map<string, any[]>();
    brokerSummaryData.forEach(record => {
      const date = record.date || record.time;
      if (date) {
        if (!dataByDate.has(date)) {
          dataByDate.set(date, []);
        }
        dataByDate.get(date)!.push(record);
      }
    });

    // Get unique dates and sort them
    const dates = Array.from(dataByDate.keys()).sort();

    // OPTIMIZED: Pre-filter and sort brokers once per date
    return dates.map(date => {
      const brokersForDate = dataByDate.get(date) || [];
      const limit = brokersForDate.length;

      // Sort by NetSellVol descending and slice
      const sortedBrokers = brokersForDate
        .filter(record => record.broker && typeof record.broker === 'string')
        // User requested to use Lot/Volume for ranking in this dashboard
        .sort((a, b) => (b.NetSellVol || 0) - (a.NetSellVol || 0))
        .slice(0, limit)
        .map((record) => {
          const broker = record.broker || record.BrokerCode || '';
          const bgColor = getBrokerColor(broker);
          return {
            broker,
            volume: record.TotalVol || 0,
            netFlow: (record.NetSellVol || 0) / 100, // Use NetSellVol and convert to Lot
            color: bgColor,
            textColor: getTextColor(bgColor)
          };
        });

      return {
        date,
        topBrokers: sortedBrokers
      };
    });
  }, [brokerSummaryData, brokerNetStats, topTekTokBrokers]);

  // Sort and filter brokers for dropdown (sorted by top brokers by date on last date)
  const {
    recommendedBrokers: _recommendedBrokers,
    otherBrokers,
    filteredBrokers,
    totalOtherBrokersCount,
  } = useMemo(() => {
    const searchTerm = debouncedBrokerSearch.toLowerCase();

    // Get top 5 buy and top 5 sell brokers
    const filterTop5BuyBrokers = brokerNetStats.topBuyers.slice(0, 5).map(item => item.broker);
    const filterTop5SellBrokers = brokerNetStats.topSellers.slice(0, 5).map(item => item.broker);
    const top5BuySellSet = new Set([...filterTop5BuyBrokers, ...filterTop5SellBrokers]);

    // Get top brokers order from last date
    const lastDateData = topBrokersData.length > 0 ? topBrokersData[topBrokersData.length - 1] : null;
    const topBrokersOrderMap = new Map<string, number>();
    if (lastDateData && lastDateData.topBrokers) {
      lastDateData.topBrokers.forEach((brokerData, index) => {
        topBrokersOrderMap.set(brokerData.broker, index);
      });
    }



    const recommended = defaultBrokers
      .filter((broker) => availableBrokersForStock.includes(broker))
      .filter((broker) => broker.toLowerCase().includes(searchTerm));

    // Sort recommended brokers: "ALL" first, then Top 5 Buy/Sell (in their original order), then by top brokers order from last date
    const sortedRecommended = [...recommended].sort((a, b) => {
      // "ALL" always comes first
      if (a === 'ALL') return -1;
      if (b === 'ALL') return 1;

      const aIsTop5 = top5BuySellSet.has(a);
      const bIsTop5 = top5BuySellSet.has(b);

      // Top 5 Buy/Sell brokers always come after "ALL"
      if (aIsTop5 && !bIsTop5) return -1;
      if (!aIsTop5 && bIsTop5) return 1;

      // If both are Top 5, maintain their original order (top 5 buy first, then top 5 sell)
      if (aIsTop5 && bIsTop5) {
        const aIndex = filterTop5BuyBrokers.indexOf(a) !== -1 ? filterTop5BuyBrokers.indexOf(a) : filterTop5SellBrokers.indexOf(a) + 5;
        const bIndex = filterTop5BuyBrokers.indexOf(b) !== -1 ? filterTop5BuyBrokers.indexOf(b) : filterTop5SellBrokers.indexOf(b) + 5;
        return aIndex - bIndex;
      }

      // For non-Top 5 brokers, sort by top brokers order from last date
      const aOrder = topBrokersOrderMap.has(a) ? topBrokersOrderMap.get(a)! : Infinity;
      const bOrder = topBrokersOrderMap.has(b) ? topBrokersOrderMap.get(b)! : Infinity;

      // If both are in top brokers, sort by their order
      if (aOrder !== Infinity && bOrder !== Infinity) {
        return aOrder - bOrder;
      }

      // If only one is in top brokers, it comes first
      if (aOrder !== Infinity) return -1;
      if (bOrder !== Infinity) return 1;

      // If neither is in top brokers, maintain original order
      return 0;
    });

    const others = availableBrokersForStock
      .filter((broker) => broker !== 'ALL')
      .filter((broker) => broker.toLowerCase().includes(searchTerm));

    const sortedOthers = [...others].sort((a, b) => a.localeCompare(b));

    // Ensure "ALL" is first in recommended brokers if it exists
    const sortedRecommendedWithAll = [...sortedRecommended].sort((a, b) => {
      if (a === 'ALL') return -1;
      if (b === 'ALL') return 1;
      return 0;
    });

    // Display all brokers (no limit) - ensure all brokers are shown in dropdown
    // Combine and ensure "ALL" is first
    const allBrokers = [...sortedRecommendedWithAll, ...sortedOthers];
    const allIndex = allBrokers.indexOf('ALL');
    if (allIndex > 0) {
      // Move "ALL" to first position
      allBrokers.splice(allIndex, 1);
      allBrokers.unshift('ALL');
    }

    return {
      recommendedBrokers: sortedRecommendedWithAll,
      otherBrokers: sortedOthers,
      filteredBrokers: allBrokers,
      totalOtherBrokersCount: sortedOthers.length,
    };
  }, [availableBrokersForStock, defaultBrokers, debouncedBrokerSearch, brokerNetStats, topBrokersData]);

  // Calculate the currently highlighted stock value based on index
  const highlightedStockValue = useMemo(() => {
    if (highlightedStockIndex === -1) return null;

    // For windowed display logic (previously limited to 10 for flat list), 
    // we now use the full list relative to the window, but let's stick to the visible items concept if needed on scrolling?
    // Actually, for navigation we should probably allow navigating the whole filtered list
    // But keeping it simple with the *visible* or *top* items as per previous implementation logic?
    // The previous implementation used .slice(0, 10) which matched the dropdown suggestion limit if it was limited.
    // However, the dropdown implementation seems to show 'visibleStocksList' which is windowed.
    // Let's rely on the list that corresponds to 'visible...' but maybe we should allow scrolling?
    // For now, let's map index directly to the source list (filtered or all)
    // AND we must ensure scrolling follows index if we want perfect UX. 
    // But for "highlight value", just grabbing from the correct list is enough.

    // Note: Previous logic was: (tickerInput === '' ? availableStocks... : filteredStocks).slice(0, 10)
    // This implies it only allowed navigating top 10.
    // Let's allow navigating visible set (ITEMS_PER_PAGE).
    // Or just the slice that is rendered.

    // Be careful: highlightedStockIndex is the index *within the visible window*? 
    // OR is it the index *in the total list*?
    // The previous code used .slice(0, 10). So clearly it was designed for a short list.
    // But the UI renders `visibleAllStocksList` which uses `stockScrollOffset`.
    // If I press down 15 times, index becomes 14. 
    // If we assume index is absolute, we need to ensure scroll follows.
    // For this task, strict requirement is Left/Right navigation. 
    // Let's assume index is relative to the currently rendered view or top X?

    // Wait, the previous logic was:
    // const suggestions = (...).slice(0, 10);
    // return suggestions[highlightedStockIndex] || null;

    // This means it ONLY supported 10 items.
    // Let's stick to supporting the rendered items for now to be safe.

    // Use the same list source as the render uses:
    const listToUse = activeColumn === 'stocks'
      ? (tickerInput === '' ? visibleAllStocksList : visibleStocksList)
      : (tickerInput === '' ? visibleAllSectorsList : visibleSectorsList);

    return listToUse[highlightedStockIndex] || null;
  }, [highlightedStockIndex, tickerInput, activeColumn, visibleAllStocksList, visibleStocksList, visibleAllSectorsList, visibleSectorsList]);

  // Windowed recommended brokers list â€” same as recommendedBrokers (no windowing needed)
  // Note: use recommendedBrokers directly in JSX

  // Windowed other brokers list - show all loaded items up to current scroll page
  const visibleOtherBrokers = useMemo(() => {
    const end = brokerScrollOffset + ITEMS_PER_PAGE;
    return otherBrokers.slice(0, end);
  }, [otherBrokers, brokerScrollOffset]);

  return (
    <div className={`flex flex-col ${isDashboard ? 'h-full' : 'h-screen'} overflow-hidden ${onlyShowInventoryChart ? '' : 'space-y-0'}`}>

      {/* Controls */}
      {!hideControls && (
        <div className="shrink-0 w-full z-50">
          <div className="bg-card border-b border-[#3a4252] px-4 py-1.5 shadow-lg">
            <div className="flex flex-col md:flex-row md:flex-wrap items-stretch md:items-center gap-3 md:gap-6">
              {/* Ticker Selection */}
              {!disableTickerSelection && (
                <div className="flex flex-col md:flex-row md:items-center gap-2 w-full md:w-auto">
                  <label className="text-sm font-medium whitespace-nowrap">Ticker:</label>
                  <div className="relative flex-1 md:flex-none" ref={dropdownRef}>
                    <div className="w-full sm:w-32 h-9 px-3 flex items-center justify-between bg-background border border-input rounded-md text-sm text-foreground hover:border-primary/50 focus-within:border-primary/50 transition-colors">
                      <Search className="w-3.5 h-3.5 text-muted-foreground mr-2 flex-shrink-0" />
                      <input
                        type="text"
                        autoCapitalize="characters"
                        className="w-full bg-transparent text-sm text-foreground focus:outline-none placeholder:text-muted-foreground/50 truncate uppercase"
                        placeholder="Select ticker..."
                        value={showStockSuggestions
                          ? tickerInput.toUpperCase()
                          : (selectedTicker ? formatStockDisplayName(selectedTicker).toUpperCase() : '')}
                        onChange={(e) => {
                          handleStockInputChange(e.target.value);
                          setHighlightedStockIndex(0);
                          setActiveColumn('stocks');
                          setShowStockSuggestions(true);
                        }}
                        onKeyDown={(e) => {
                          if (!showStockSuggestions) return;
                          const listToUse = tickerInput === ''
                            ? (activeColumn === 'sectors' ? visibleAllSectorsList : visibleAllStocksList)
                            : (activeColumn === 'sectors' ? visibleSectorsList : visibleStocksList);
                          const total = listToUse.length;
                          if (e.key === 'ArrowDown') {
                            e.preventDefault();
                            setHighlightedStockIndex(prev => (prev < total - 1 ? prev + 1 : 0));
                          } else if (e.key === 'ArrowUp') {
                            e.preventDefault();
                            setHighlightedStockIndex(prev => (prev > 0 ? prev - 1 : total - 1));
                          } else if (e.key === 'Enter') {
                            e.preventDefault();
                            const highlighted = highlightedStockValue || (listToUse[0]);
                            if (highlighted) {
                              setSelectedTicker(highlighted);
                              setTickerInput('');
                              setShowStockSuggestions(false);
                            }
                          } else if (e.key === 'Escape') {
                            setShowStockSuggestions(false);
                          }
                        }}
                        onFocus={() => {
                          // Pre-populate with current selection so user can edit from it
                          const initVal = selectedTicker
                            ? (selectedTicker.startsWith('[SECTOR] ') ? selectedTicker.replace('[SECTOR] ', '') : selectedTicker)
                            : '';
                          setTickerInput(initVal.toUpperCase());
                          setShowStockSuggestions(true);
                          setHighlightedStockIndex(0);
                          setActiveColumn('stocks');
                        }}
                      />
                      <ChevronDown className="w-3 h-3 text-muted-foreground opacity-50 ml-1 flex-shrink-0 cursor-pointer" onClick={() => {
                        setShowStockSuggestions(!showStockSuggestions);
                        if (!showStockSuggestions) {
                          setHighlightedStockIndex(0);
                          setActiveColumn('stocks');
                        }
                      }} />
                    </div>
                    {showStockSuggestions && (
                      <div className="absolute top-full left-0 mt-1 bg-popover border border-[#3a4252] rounded-md shadow-lg z-50 max-h-96 overflow-hidden flex flex-col w-[340px]">
                        {availableStocks.length === 0 ? (
                          <div className="px-3 py-[2.06px] text-sm text-muted-foreground flex items-center">
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            Loading stocks...
                          </div>
                        ) : (
                          <>
                            {/* Search Input (Removed) */}
                            <div className="flex flex-row h-full max-h-80 overflow-hidden">
                            {/* Left column: Stocks */}
                            <div
                              className="w-[140px] shrink-0 border-r border-[#3a4252] overflow-y-auto"
                              onScroll={(e) => {
                                const target = e.target as HTMLElement;
                                const scrollBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
                                if (scrollBottom < 100 && stockScrollOffset + ITEMS_PER_PAGE < (tickerInput === '' ? allStocksList.length : filteredStocksList.length)) {
                                  setStockScrollOffset(prev => prev + ITEMS_PER_PAGE);
                                }
                              }}
                            >
                              {tickerInput === '' ? (
                                <>
                                  <div className="px-3 py-[2.06px] text-xs text-muted-foreground border-b border-[#3a4252] sticky top-0 bg-popover">
                                    Stocks ({allStocksList.length})
                                  </div>
                                  {visibleAllStocksList.map(stock => {
                                    const isHighlighted = stock === highlightedStockValue;
                                    const isSelected = stock === selectedTicker;
                                    return (
                                      <div
                                        key={stock}
                                        onClick={() => handleStockSelect(stock)}
                                        className={`px-3 py-[2.06px] cursor-pointer text-sm flex justify-between items-center ${isSelected ? 'text-primary font-medium bg-accent/50' : ''} ${isHighlighted ? 'bg-accent text-accent-foreground' : ''}`}
                                      >
                                        <span>{stock}</span>
                                        {isSelected && <span className="text-primary text-xs">✔</span>}
                                      </div>
                                    );
                                  })}
                                  {stockScrollOffset + ITEMS_PER_PAGE < allStocksList.length && (
                                    <div className="px-3 py-2 text-xs text-muted-foreground text-center">
                                      Loading more... ({Math.min(stockScrollOffset + ITEMS_PER_PAGE, allStocksList.length)} / {allStocksList.length})
                                    </div>
                                  )}
                                </>
                              ) : filteredStocksList.length > 0 ? (
                                <>
                                  <div className="px-3 py-[2.06px] text-xs text-muted-foreground border-b border-[#3a4252] sticky top-0 bg-popover">
                                    Stocks ({filteredStocksList.length})
                                  </div>
                                  {visibleStocksList.map(stock => {
                                    const isHighlighted = stock === highlightedStockValue;
                                    const isSelected = stock === selectedTicker;
                                    return (
                                      <div
                                        key={stock}
                                        onClick={() => handleStockSelect(stock)}
                                        className={`px-3 py-[2.06px] cursor-pointer text-sm flex justify-between items-center ${isSelected ? 'text-primary font-medium bg-accent/50' : ''} ${isHighlighted ? 'bg-accent text-accent-foreground' : ''}`}
                                      >
                                        <span>{stock}</span>
                                        {isSelected && <span className="text-primary text-xs">✔</span>}
                                      </div>
                                    );
                                  })}
                                  {stockScrollOffset + ITEMS_PER_PAGE < filteredStocksList.length && (
                                    <div className="px-3 py-2 text-xs text-muted-foreground text-center">
                                      Loading more... ({Math.min(stockScrollOffset + ITEMS_PER_PAGE, filteredStocksList.length)} / {filteredStocksList.length})
                                    </div>
                                  )}
                                </>
                              ) : (
                                <div className="px-3 py-[2.06px] text-xs text-muted-foreground border-b border-[#3a4252] sticky top-0 bg-popover">
                                  Stocks (0)
                                </div>
                              )}
                            </div>
                            {/* Right column: Sectors */}
                            <div
                              className="flex-1 overflow-y-auto"
                              onScroll={(e) => {
                                const target = e.target as HTMLElement;
                                const scrollBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
                                if (scrollBottom < 100 && sectorScrollOffset + ITEMS_PER_PAGE < (tickerInput === '' ? allSectorsList.length : filteredSectorsList.length)) {
                                  setSectorScrollOffset(prev => prev + ITEMS_PER_PAGE);
                                }
                              }}
                            >
                              {tickerInput === '' ? (
                                <>
                                  <div className="px-3 py-[2.06px] text-xs text-muted-foreground border-b border-[#3a4252] sticky top-0 bg-popover">
                                    Sectors ({allSectorsList.length})
                                  </div>
                                  {visibleAllSectorsList.map(stock => {
                                    const isHighlighted = stock === highlightedStockValue;
                                    const isSelected = stock === selectedTicker;
                                    return (
                                      <div
                                        key={stock}
                                        onClick={() => handleStockSelect(stock)}
                                        className={`px-3 py-[2.06px] cursor-pointer text-sm flex justify-between items-center ${isSelected ? 'text-primary font-medium bg-accent/50' : ''} ${isHighlighted ? 'bg-accent text-accent-foreground' : ''}`}
                                      >
                                        <span>{formatStockDisplayName(stock)}</span>
                                        {isSelected && <span className="text-primary text-xs">✔</span>}
                                      </div>
                                    );
                                  })}
                                  {sectorScrollOffset + ITEMS_PER_PAGE < allSectorsList.length && (
                                    <div className="px-3 py-2 text-xs text-muted-foreground text-center">
                                      Loading more... ({Math.min(sectorScrollOffset + ITEMS_PER_PAGE, allSectorsList.length)} / {allSectorsList.length})
                                    </div>
                                  )}
                                </>
                              ) : filteredSectorsList.length > 0 ? (
                                <>
                                  <div className="px-3 py-[2.06px] text-xs text-muted-foreground border-b border-[#3a4252] sticky top-0 bg-popover">
                                    Sectors ({filteredSectorsList.length})
                                  </div>
                                  {visibleSectorsList.map(stock => {
                                    const isHighlighted = stock === highlightedStockValue;
                                    const isSelected = stock === selectedTicker;
                                    return (
                                      <div
                                        key={stock}
                                        onClick={() => handleStockSelect(stock)}
                                        className={`px-3 py-[2.06px] cursor-pointer text-sm flex justify-between items-center ${isSelected ? 'text-primary font-medium bg-accent/50' : ''} ${isHighlighted ? 'bg-accent text-accent-foreground' : ''}`}
                                      >
                                        <span>{formatStockDisplayName(stock)}</span>
                                        {isSelected && <span className="text-primary text-xs">✔</span>}
                                      </div>
                                    );
                                  })}
                                  {sectorScrollOffset + ITEMS_PER_PAGE < filteredSectorsList.length && (
                                    <div className="px-3 py-2 text-xs text-muted-foreground text-center">
                                      Loading more... ({Math.min(sectorScrollOffset + ITEMS_PER_PAGE, filteredSectorsList.length)} / {filteredSectorsList.length})
                                    </div>
                                  )}
                                </>
                              ) : (
                                <div className="px-3 py-[2.06px] text-xs text-muted-foreground border-b border-[#3a4252] sticky top-0 bg-popover">
                                  Sectors (0)
                                </div>
                              )}
                            </div>
                          </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {/* Broker Selection - Multi-select with chips */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto">
                <label className="text-sm font-medium whitespace-nowrap">Broker:</label>
                <div className="relative flex-1 sm:flex-none w-full sm:w-auto">
                  <div
                    className={`w-full sm:w-32 h-9 px-3 flex items-center justify-between bg-background border border-input rounded-md text-sm text-foreground cursor-pointer hover:bg-accent/50 transition-colors ${(!getActualTicker || isLoadingBrokersForStock) ? 'opacity-50 pointer-events-none' : ''}`}
                    onMouseDown={(e) => {
                      if (isTrial) {
                        e.stopPropagation();
                        showToast({
                          type: 'warning',
                          title: 'Fitur Terbatas',
                          message: 'Dalam masa free trial, Anda tidak dapat memilih broker spesifik.'
                        });
                        return;
                      }
                      if (getActualTicker && !isLoadingBrokersForStock) {
                        e.stopPropagation();
                        setShowStockSuggestions(false);
                        setBrokerScrollOffset(0);
                        setShowBrokerSuggestions(prev => !prev);
                      }
                    }}
                  >
                    <span className="truncate flex-1 min-w-0">
                      {(() => {
                        if (isLoadingBrokersForStock) return "Loading...";
                        if (!getActualTicker) return "Select ticker";
                        const quickSelectLabels: string[] = [];
                        if (brokerSelectionMode.top5buy) quickSelectLabels.push('Top 5 Buy');
                        if (brokerSelectionMode.top5sell) quickSelectLabels.push('Top 5 Sell');
                        if (brokerSelectionMode.top5tektok) quickSelectLabels.push('Top TekTok');
                        if (quickSelectLabels.length > 0) return quickSelectLabels.join(' | ');
                        if (selectedBrokers.length > 0) return selectedBrokers.join(' | ');
                        return 'All';
                      })()}
                    </span>
                    {getActualTicker && (brokerSelectionMode.top5buy || brokerSelectionMode.top5sell || brokerSelectionMode.top5tektok || selectedBrokers.length > 0) && (
                      <button
                        type="button"
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          setSelectedBrokers([]);
                          setBrokerSelectionMode({ top5buy: false, top5sell: false, top5tektok: false, custom: false });
                          setBrokerVisibility({});
                          setHasUserSelectedBrokers(false);
                          setShouldFetchData(false);
                        }}
                        className="text-muted-foreground hover:text-destructive transition-colors ml-1 flex-shrink-0 text-xs leading-none"
                      >×</button>
                    )}
                    <ChevronDown className="w-3 h-3 text-muted-foreground opacity-50 ml-1 flex-shrink-0" />
                  </div>
                  {showBrokerSuggestions && getActualTicker && !isLoadingBrokersForStock && (
                    <div id="broker-suggestions" role="listbox" className="broker-dropdown-container absolute top-full left-0 mt-1 bg-popover border border-[#3a4252] rounded-md shadow-lg z-[100] max-h-96 overflow-hidden flex flex-col w-auto min-w-[230px]" onMouseDown={(e) => e.stopPropagation()}>
                      {availableBrokersForStock.length === 0 ? (
                        <div className="px-3 py-[2.06px] text-sm text-muted-foreground flex items-center">
                          {isLoadingBrokersForStock ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin mr-2" />
                              Loading brokers...
                            </>
                          ) : (
                            'No brokers available'
                          )}
                        </div>
                      ) : (
                        <>
                        {/* Search Input */}
                        <div className="p-2 border-b border-[#3a4252]">
                          <div className="relative">
                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                            <input
                              type="text"
                              className="w-full h-8 pl-7 pr-2 bg-background border border-[#3a4252] rounded text-sm text-foreground focus:outline-none focus:border-primary/50"
                              placeholder="Search broker..."
                              value={brokerSearch}
                              onChange={(e) => handleBrokerSearchChange(e)}
                              onKeyDown={handleBrokerKeyDown}
                              onClick={(e) => e.stopPropagation()}
                              autoFocus
                            />
                          </div>
                        </div>
                        <div className="flex flex-row h-full max-h-80 overflow-hidden">
                          {/* Left column: Brokers */}
                          <div
                            className="w-[80px] shrink-0 border-r border-[#3a4252] overflow-y-auto"
                            onScroll={(e) => {
                              const target = e.target as HTMLElement;
                              const scrollBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
                              if (scrollBottom < 100 && brokerScrollOffset + ITEMS_PER_PAGE < otherBrokers.length) {
                                setBrokerScrollOffset(prev => prev + ITEMS_PER_PAGE);
                              }
                            }}
                          >
                            {filteredBrokers.length === 0 ? (
                              <div className="px-3 py-[2.06px] text-sm text-muted-foreground">
                                {debouncedBrokerSearch !== '' ? `No brokers found matching "${debouncedBrokerSearch}"` : `No brokers available for ${getActualTicker || 'selected ticker'}`}
                              </div>
                            ) : (
                              <>
                                {otherBrokers.length > 0 && (
                                  <>
                                    <div className="px-3 py-[2.06px] text-xs text-muted-foreground border-b border-[#3a4252]">
                                      {`Brokers (${totalOtherBrokersCount})`}
                                    </div>
                                    {visibleOtherBrokers.map((broker, index) => {
                                      const hasQuickSelect = brokerNetStats.topBuyers.length > 0 && debouncedBrokerSearch === '';
                                      const quickSelectCount = hasQuickSelect ? 3 : 0;
                                      const globalIndex = quickSelectCount + index;
                                      const isSelected = selectedBrokers.includes(broker);
                                      return (
                                        <div
                                          key={`option-${broker}`}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (isSelected) {
                                              removeBroker(broker);
                                            } else {
                                              handleBrokerSelect(broker);
                                            }
                                          }}
                                          onMouseDown={(e) => e.stopPropagation()}
                                          tabIndex={0}
                                          role="option"
                                          aria-selected={isSelected}
                                          className={`px-3 py-[2.06px] cursor-pointer text-sm flex justify-between items-center ${globalIndex === highlightedBrokerIndex ? 'bg-accent' : ''} ${isSelected ? 'bg-accent/50' : ''}`}
                                          onMouseEnter={() => handleBrokerMouseEnter(globalIndex)}
                                        >
                                          <span className={getBrokerColorClass(broker).className} style={{ color: getBrokerColorClass(broker).color }}>{broker}</span>
                                          {isSelected && <span className="text-primary text-xs">✔</span>}
                                        </div>
                                      );
                                    })}
                                    {brokerScrollOffset + ITEMS_PER_PAGE < otherBrokers.length && (
                                      <div className="px-3 py-2 text-xs text-muted-foreground text-center">
                                        Loading more... ({Math.min(brokerScrollOffset + ITEMS_PER_PAGE, otherBrokers.length)} / {otherBrokers.length})
                                      </div>
                                    )}
                                  </>
                                )}
                              </>
                            )}
                          </div>
                          {/* Right column: Quick Select - Always visible */}
                          <div className="w-[150px] shrink-0 overflow-y-auto">
                            <div className="px-3 py-[2.06px] text-xs text-muted-foreground border-b border-[#3a4252] sticky top-0 bg-popover flex justify-between items-center">
                              <span>Quick Select</span>
                              {(brokerSelectionMode.top5buy || brokerSelectionMode.top5sell || brokerSelectionMode.top5tektok || selectedBrokers.length > 0) && (
                                <button type="button" onMouseDown={(e) => { e.stopPropagation(); setSelectedBrokers([]); setBrokerSelectionMode({ top5buy: false, top5sell: false, top5tektok: false, custom: false }); setBrokerVisibility({}); setHasUserSelectedBrokers(false); setShouldFetchData(false); }} className="text-destructive hover:text-destructive/80 font-medium">Clear</button>
                              )}
                            </div>
                            {brokerSearch === '' && (
                              <>
                                <div
                                  onClick={() => {
                                    const isCurrentlyActive = brokerSelectionMode.top5buy;
                                    // Deactivation always allowed; activation requires data
                                    if (!isCurrentlyActive && brokerNetStats.topBuyers.length === 0) return;
                                    const top5Buy = brokerNetStats.topBuyers.slice(0, 5).map(item => item.broker);
                                    if (isCurrentlyActive) {
                                      setSelectedBrokers(prev => prev.filter(b => !top5Buy.includes(b)));
                                      setBrokerSelectionMode(prev => ({ ...prev, top5buy: false }));
                                    } else {
                                      setSelectedBrokers(prev => Array.from(new Set([...prev, ...top5Buy])));
                                      setBrokerSelectionMode(prev => ({ ...prev, top5buy: true }));
                                    }
                                    setHasUserSelectedBrokers(false);
                                    setBrokerSearch('');
                                    setShouldFetchData(false);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      if (brokerNetStats.topBuyers.length === 0) {
                                        return;
                                      }
                                      const top5Buy = brokerNetStats.topBuyers.slice(0, 5).map(item => item.broker);
                                      const isCurrentlyActive = brokerSelectionMode.top5buy;

                                      if (isCurrentlyActive) {
                                        setSelectedBrokers(prev => prev.filter(b => !top5Buy.includes(b)));
                                        setBrokerSelectionMode(prev => ({ ...prev, top5buy: false }));
                                      } else {
                                        setSelectedBrokers(prev => {
                                          const newSet = new Set([...prev, ...top5Buy]);
                                          return Array.from(newSet);
                                        });
                                        setBrokerSelectionMode(prev => ({ ...prev, top5buy: true }));
                                      }
                                      setHasUserSelectedBrokers(false);
                                      setBrokerSearch('');
                                      // Reset shouldFetchData to prevent auto-reload until Show is clicked
                                      setShouldFetchData(false);
                                    }
                                  }}
                                  tabIndex={brokerNetStats.topBuyers.length > 0 ? 0 : -1}
                                  role="option"
                                  aria-selected={brokerSelectionMode.top5buy}
                                  aria-disabled={brokerNetStats.topBuyers.length === 0}
                                  className={`px-3 py-[2.06px] text-sm font-medium flex justify-between items-center ${brokerNetStats.topBuyers.length === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-muted cursor-pointer'} ${highlightedBrokerIndex === 0 ? 'bg-accent' : ''} ${brokerSelectionMode.top5buy ? 'bg-primary/20' : ''}`}
                                >
                                  <span>Top 5 Buy {brokerNetStats.topBuyers.length === 0 && !isLoadingBrokerData && !isDataReady && '(Click Show to load)'}</span>
                                  {brokerSelectionMode.top5buy && <span className="text-primary text-xs">✔</span>}
                                </div>
                                <div
                                  onClick={() => {
                                    const isCurrentlyActive = brokerSelectionMode.top5sell;
                                    if (!isCurrentlyActive && brokerNetStats.topSellers.length === 0) return;
                                    const top5Sell = brokerNetStats.topSellers.slice(0, 5).map(item => item.broker);
                                    if (isCurrentlyActive) {
                                      setSelectedBrokers(prev => prev.filter(b => !top5Sell.includes(b)));
                                      setBrokerSelectionMode(prev => ({ ...prev, top5sell: false }));
                                    } else {
                                      setSelectedBrokers(prev => Array.from(new Set([...prev, ...top5Sell])));
                                      setBrokerSelectionMode(prev => ({ ...prev, top5sell: true }));
                                    }
                                    setHasUserSelectedBrokers(false);
                                    setBrokerSearch('');
                                    setShouldFetchData(false);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      if (brokerNetStats.topSellers.length === 0) {
                                        return;
                                      }
                                      const top5Sell = brokerNetStats.topSellers.slice(0, 5).map(item => item.broker);
                                      const isCurrentlyActive = brokerSelectionMode.top5sell;

                                      if (isCurrentlyActive) {
                                        setSelectedBrokers(prev => prev.filter(b => !top5Sell.includes(b)));
                                        setBrokerSelectionMode(prev => ({ ...prev, top5sell: false }));
                                      } else {
                                        setSelectedBrokers(prev => {
                                          const newSet = new Set([...prev, ...top5Sell]);
                                          return Array.from(newSet);
                                        });
                                        setBrokerSelectionMode(prev => ({ ...prev, top5sell: true }));
                                      }
                                      setHasUserSelectedBrokers(false);
                                      setBrokerSearch('');
                                      // Reset shouldFetchData to prevent auto-reload until Show is clicked
                                      setShouldFetchData(false);
                                    }
                                  }}
                                  tabIndex={brokerNetStats.topSellers.length > 0 ? 0 : -1}
                                  role="option"
                                  aria-selected={brokerSelectionMode.top5sell}
                                  aria-disabled={brokerNetStats.topSellers.length === 0}
                                  className={`px-3 py-[2.06px] text-sm font-medium flex justify-between items-center ${brokerNetStats.topSellers.length === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-muted cursor-pointer'} ${highlightedBrokerIndex === 1 ? 'bg-accent' : ''} ${brokerSelectionMode.top5sell ? 'bg-primary/20' : ''}`}
                                >
                                  <span>Top 5 Sell {brokerNetStats.topSellers.length === 0 && !isLoadingBrokerData && !isDataReady && '(Click Show to load)'}</span>
                                  {brokerSelectionMode.top5sell && <span className="text-primary text-xs">✔</span>}
                                </div>
                                <div
                                  onClick={() => {
                                    const isCurrentlyActive = brokerSelectionMode.top5tektok;
                                    // Deactivation always allowed; activation requires data
                                    if (!isCurrentlyActive) {
                                      if (brokerNetStats.topTekToks.length === 0) {
                                        if (isDataReady && !brokerNetStats.isInventoryEligible) {
                                          showToast({
                                            type: 'info',
                                            title: 'Info',
                                            message: `Inventory ratio (${(brokerNetStats.inventoryRatio ?? 0).toFixed(1)}%) is above 35% threshold`
                                          });
                                        } else if (isDataReady) {
                                          showToast({
                                            type: 'info',
                                            title: 'Info',
                                            message: 'No brokers meet Tektok criteria (value > top 5 buy/sell threshold)'
                                          });
                                        }
                                        return;
                                      }
                                    }
                                    const topTekTok = brokerNetStats.topTekToks.map(item => item.broker);
                                    if (isCurrentlyActive) {
                                      setSelectedBrokers(prev => prev.filter(b => !topTekTok.includes(b)));
                                      setBrokerSelectionMode(prev => ({ ...prev, top5tektok: false }));
                                    } else {
                                      setSelectedBrokers(prev => Array.from(new Set([...prev, ...topTekTok])));
                                      setBrokerSelectionMode(prev => ({ ...prev, top5tektok: true }));
                                    }
                                    setHasUserSelectedBrokers(false);
                                    setBrokerSearch('');
                                    setShouldFetchData(false);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      if (brokerNetStats.topTekToks.length === 0) {
                                        return;
                                      }
                                      const topTekTok = brokerNetStats.topTekToks.map(item => item.broker);
                                      const allSelected = topTekTok.every(b => selectedBrokers.includes(b));
                                      
                                      if (allSelected) {
                                        setSelectedBrokers(prev => prev.filter(b => !topTekTok.includes(b)));
                                        setBrokerSelectionMode(prev => ({ ...prev, top5tektok: false }));
                                      } else {
                                        setSelectedBrokers(prev => {
                                          const newSet = new Set([...prev, ...topTekTok]);
                                          return Array.from(newSet);
                                        });
                                        setBrokerSelectionMode(prev => ({ ...prev, top5tektok: true }));
                                      }
                                      setHasUserSelectedBrokers(false);
                                      setBrokerSearch('');
                                      // Reset shouldFetchData to prevent auto-reload until Show is clicked
                                      setShouldFetchData(false);
                                    }
                                  }}
                                  tabIndex={brokerNetStats.topTekToks.length > 0 ? 0 : -1}
                                  role="option"
                                  aria-selected={brokerSelectionMode.top5tektok}
                                  aria-disabled={brokerNetStats.topTekToks.length === 0}
                                  className={`px-3 py-[2.06px] text-sm font-medium flex justify-between items-center ${brokerNetStats.topTekToks.length === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-muted cursor-pointer'} ${highlightedBrokerIndex === 2 ? 'bg-accent' : ''} ${brokerSelectionMode.top5tektok ? 'bg-primary/20' : ''}`}
                                >
                                  <span>Top TekTok {brokerNetStats.topTekToks.length === 0 && !isLoadingBrokerData && !isDataReady && '(Click Show to load)'}</span>
                                  {brokerSelectionMode.top5tektok && <span className="text-primary text-xs">✔</span>}
                                </div>
                              </>
                            )}
                            {brokerSearch !== '' && (
                              <div className="px-3 py-[2.06px] text-xs text-muted-foreground">
                                Quick Select unavailable while searching
                              </div>
                            )}
                          </div>
                        </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Date Range */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto">
                <label className="text-sm font-medium whitespace-nowrap">Date Range:</label>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <div
                    className="relative h-9 flex-1 sm:w-36 rounded-md border border-input bg-background cursor-pointer hover:bg-accent/50 transition-colors"
                    onClick={() => triggerDatePicker(startDateRef)}
                  >
                    <input
                      ref={startDateRef}
                      type="date"
                      value={formatDateForInput(startDate)}
                      onChange={(e) => {
                        const selectedDate = e.target.value;

                        // Validate minimum date (earliest Stockbit migration data)
                        if (selectedDate < MIN_DATE) {
                          showToast({
                            type: 'error',
                            title: 'Tanggal Tidak Valid',
                            message: `Tanggal minimum yang bisa dipilih adalah ${new Date(MIN_DATE).toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' })}`
                          });
                          if (startDateRef.current) {
                            startDateRef.current.value = formatDateForInput(startDate);
                          }
                          return;
                        }

                        setStartDate(selectedDate);
                        if (!endDate || new Date(selectedDate) > new Date(endDate)) {
                          setEndDate(selectedDate);
                        }
                      }}
                      max={endDate ? formatDateForInput(endDate) : (new Date().toISOString().split('T')[0] || '')}
                      min={MIN_DATE}
                      onKeyDown={(e) => e.preventDefault()}
                      onPaste={(e) => e.preventDefault()}
                      onInput={(e) => e.preventDefault()}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      style={{ caretColor: 'transparent' }}
                    />
                    <div className="flex items-center justify-between h-full px-3">
                      <span className="text-sm text-foreground">
                        {startDate ? new Date(startDate).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        }) : 'DD/MM/YYYY'}
                      </span>
                      <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    </div>
                  </div>
                  <span className="text-sm text-muted-foreground whitespace-nowrap hidden sm:inline">to</span>
                  <div
                    className="relative h-9 flex-1 sm:w-36 rounded-md border border-input bg-background cursor-pointer hover:bg-accent/50 transition-colors"
                    onClick={() => triggerDatePicker(endDateRef)}
                  >
                    <input
                      ref={endDateRef}
                      type="date"
                      value={formatDateForInput(endDate)}
                      onChange={(e) => {
                        const selectedDate = e.target.value;

                        if (selectedDate < MIN_DATE) {
                          showToast({
                            type: 'error',
                            title: 'Tanggal Tidak Valid',
                            message: `Tanggal minimum yang bisa dipilih adalah ${new Date(MIN_DATE).toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' })}`
                          });
                          if (endDateRef.current) {
                            endDateRef.current.value = formatDateForInput(endDate);
                          }
                          return;
                        }

                        setEndDate(selectedDate);
                      }}
                      min={formatDateForInput(startDate || MIN_DATE)}
                      max={new Date().toISOString().split('T')[0] ?? ''}
                      onKeyDown={(e) => e.preventDefault()}
                      onPaste={(e) => e.preventDefault()}
                      onInput={(e) => e.preventDefault()}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      style={{ caretColor: 'transparent' }}
                    />
                    <div className="flex items-center justify-between h-full px-3">
                      <span className="text-sm text-foreground">
                        {endDate ? new Date(endDate).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        }) : 'DD/MM/YYYY'}
                      </span>
                      <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    </div>
                  </div>
                </div>
              </div>

              {/* F/D Filter */}
              <div className="flex flex-col md:flex-row md:items-center gap-2 w-full md:w-auto">
                <label className="text-sm font-medium whitespace-nowrap">F/D:</label>
                <select
                  value={fdFilter}
                  onChange={(e) => {
                    setFdFilter(e.target.value as 'All' | 'Foreign' | 'Domestic');
                    // CRITICAL: Keep existing data visible - no auto-fetch, no hide charts
                    // User must click Show button to fetch new data
                  }}
                  className="h-9 px-3 border border-[#3a4252] rounded-md bg-background text-foreground text-sm w-full md:w-auto"
                >
                  <option value="All">All</option>
                  <option value="Foreign">Foreign</option>
                  <option value="Domestic">Domestic</option>
                </select>
              </div>

              {/* Board Filter */}
              <div className="flex flex-col md:flex-row md:items-center gap-2 w-full md:w-auto">
                <label className="text-sm font-medium whitespace-nowrap">Board:</label>
                <select
                  value={marketFilter}
                  onChange={(e) => {
                    setMarketFilter(e.target.value as 'RG' | 'TN' | 'NG' | '');
                    // CRITICAL: Keep existing data visible - no auto-fetch, no hide charts
                    // User must click Show button to fetch new data
                  }}
                  className="h-9 px-3 border border-[#3a4252] rounded-md bg-background text-foreground text-sm w-full md:w-auto"
                >
                  <option value="">All</option>
                  <option value="RG">RG</option>
                  <option value="TN">TN</option>
                  <option value="NG">NG</option>
                </select>
              </div>

              {/* Show Button */}
              <button
                onClick={() => {
                  console.log(`[DEBUG Action] Show button CLICKED for ticker: "${selectedTicker}" | range: ${startDate} to ${endDate} | brokers:`, selectedBrokers);
                  // Update displayed ticker & filters when Show button is clicked
                  setDisplayedTicker(selectedTicker);
                  setDisplayedFdFilter(fdFilter);
                  setDisplayedMarket(marketFilter);
                  // Set shouldFetchData to true to trigger data fetch
                  setShouldFetchData(true);
                  setIsDataReady(false);
                  // Set loading states immediately to show loading spinner right away
                  setIsLoadingData(true);
                  setIsLoadingBrokerData(true);
                }}
                disabled={isLoadingData || isLoadingBrokerData || selectedBrokers.length === 0 || !startDate || !endDate || !selectedTicker}
                className="h-9 px-4 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium whitespace-nowrap flex items-center justify-center w-full md:w-auto"
              >
                Show
              </button>
            </div>
          </div>
        </div>
      )}

      {(isDataReady || isLoadingData || isLoadingBrokerData) && (
        <div 
          className={`flex-1 min-h-0 w-full ${splitVisualization ? 'block pb-10' : 'flex flex-col overflow-hidden'}`} 
          style={{ overflowY: splitVisualization ? 'auto' : 'hidden' }}
        >
          {onlyShowInventoryChart ? (
          // Only show Broker Inventory Chart
          <Card className="rounded-none border-border/70 overflow-hidden shrink-0">
            <CardContent className="relative pt-4">
              {!isDataReady && (isLoadingData || isLoadingBrokerData) && (
                <div className="absolute inset-0 z-30 flex items-center justify-center bg-background rounded-lg">
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    <div className="text-xs text-muted-foreground">
                      Loading ticker and broker data...
                    </div>
                  </div>
                </div>
              )}
              <div className="flex flex-col lg:flex-row gap-4">
                <div className="flex-1 relative">
                  {selectedBrokers.length === 0 && !isLoadingData && !isLoadingBrokerData && (
                    <div className="flex items-center justify-center h-80 text-muted-foreground">
                      <div className="text-center">
                        <p>No brokers selected</p>
                        <p className="text-xs mt-2">Select brokers above to view cumulative net flow</p>
                      </div>
                    </div>
                  )}
                  {(isDataReady || isLoadingData || isLoadingBrokerData) && (
                    <InventoryChart
                      key="inventory-main"
                      inventoryData={inventoryData}
                      selectedBrokers={selectedBrokers}
                      displayBrokers={visibleBrokers}
                      className="h-[calc(100vh-185px)] w-full relative"
                      topBuyers={top5BuyBrokers}
                      topSellers={top5SellBrokers}
                      topTektoks={topTekTokBrokers}
                      brokerColorMap={brokerColorMap}
                      ticker={displayedTicker}
                      logicalRangeRef={chartLogicalRangeRef}
                    />
                  )}
                  {selectedBrokers.length > 0 && !isLoadingData && !isLoadingBrokerData && inventoryData.length > 0 && visibleBrokers.length === 0 && (
                    <div className="flex items-center justify-center h-80 text-muted-foreground">
                      <div className="text-center">
                        <p>No visible brokers</p>
                        <p className="text-xs mt-2">Enable broker visibility in the legend to view chart</p>
                      </div>
                    </div>
                  )}
                  {selectedBrokers.length > 0 && !isLoadingData && !isLoadingBrokerData && inventoryData.length === 0 && isDataReady && (
                    <div className="flex items-center justify-center h-80 text-muted-foreground">
                      <div className="text-center">
                        <p>No inventory data available for selected brokers</p>
                        <p className="text-xs mt-2">Data may not be available for the selected date range</p>
                      </div>
                    </div>
                  )}
                  {selectedBrokers.length > 0 && !isLoadingData && !isLoadingBrokerData && !isDataReady && (
                    <div className="flex items-center justify-center h-80 text-muted-foreground">
                      <div className="text-center">
                        <p>No data loaded</p>
                        <p className="text-xs mt-2">Click Show button to load data</p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="w-full lg:w-[200px] lg:min-w-[200px] shrink-0 flex flex-col gap-4">
                  {brokerSelectionMode.top5buy && top5BuyBrokers.length > 0 && (
                    <BrokerLegend
                      title="Top 5 Buyers"
                      brokers={top5BuyBrokers}
                      colorReferenceBrokers={selectedBrokers}
                      brokerVisibility={brokerVisibility}
                      onToggleVisibility={handleToggleBrokerVisibility}
                      onRemoveBroker={isTrial ? undefined : removeBroker}
                      onRemoveAll={isTrial ? undefined : removeAllTop5Buy}
                      topBuyers={top5BuyBrokers}
                      topSellers={top5SellBrokers}
                      tektokBrokerNames={brokerNetStats.topTekToks.map(t => t.broker)}
                       topTektoks={brokerNetStats.topTekToks}
                      brokerColorMap={brokerColorMap}
                        brokerMetadata={tektokMetadata}
                    />
                  )}
                  {brokerSelectionMode.top5sell && top5SellBrokers.length > 0 && (
                    <BrokerLegend
                      title="Top 5 Sellers"
                      brokers={top5SellBrokers}
                      colorReferenceBrokers={selectedBrokers}
                      brokerVisibility={brokerVisibility}
                      onToggleVisibility={handleToggleBrokerVisibility}
                      onRemoveBroker={isTrial ? undefined : removeBroker}
                      onRemoveAll={isTrial ? undefined : removeAllTop5Sell}
                      topBuyers={top5BuyBrokers}
                      topSellers={top5SellBrokers}
                      tektokBrokerNames={brokerNetStats.topTekToks.map(t => t.broker)}
                       topTektoks={brokerNetStats.topTekToks}
                      brokerColorMap={brokerColorMap}
                        brokerMetadata={tektokMetadata}
                    />
                  )}
                  {brokerSelectionMode.top5tektok && topTekTokBrokers.length > 0 && (
                    <BrokerLegend
                      title="Top Tektok"
                      brokers={topTekTokBrokers}
                      colorReferenceBrokers={selectedBrokers}
                      brokerVisibility={brokerVisibility}
                      onToggleVisibility={handleToggleBrokerVisibility}
                      onRemoveBroker={isTrial ? undefined : removeBroker}
                      onRemoveAll={isTrial ? undefined : removeAllTopTekTok}
                      topBuyers={top5BuyBrokers}
                      topSellers={top5SellBrokers}
                      topTektoks={brokerNetStats.topTekToks}
                      brokerColorMap={brokerColorMap}
                        brokerMetadata={tektokMetadata}
                    />
                  )}
                  {brokerSelectionMode.custom && customBrokers.length > 0 && (
                    <BrokerLegend
                      title="Selected Brokers"
                      brokers={customBrokers}
                      colorReferenceBrokers={selectedBrokers}
                      brokerVisibility={brokerVisibility}
                      onToggleVisibility={handleToggleBrokerVisibility}
                      onRemoveBroker={isTrial ? undefined : removeBroker}
                      onRemoveAll={isTrial ? undefined : removeAllCustom}
                      topBuyers={top5BuyBrokers}
                      topSellers={top5SellBrokers}
                      tektokBrokerNames={brokerNetStats.topTekToks.map(t => t.broker)}
                       topTektoks={brokerNetStats.topTekToks}
                      brokerColorMap={brokerColorMap}
                        brokerMetadata={tektokMetadata}
                    />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ) : splitVisualization ? (
          // Split View - Separate Charts
          <>
            {/* Price Chart */}
            <Card className="rounded-none overflow-hidden border-0 gap-0">
              <div className="bg-muted/50 px-4 py-1.5 flex flex-wrap items-center justify-between gap-1">
                <div className="flex items-center">
                  <h3 className="font-semibold text-sm uppercase tracking-wider text-foreground/90">{displayedTicker} Price Action</h3>
                </div>
                  <div className="flex items-center gap-2">
                  <span className="text-[12px] font-bold text-muted-foreground uppercase whitespace-nowrap">View:</span>
                  <div className="flex items-center gap-1 rounded-md p-0.5 bg-background/50 border border-border/50">
                    <Button
                      variant={!splitVisualization ? "default" : "ghost"}
                      onClick={() => setSplitVisualization(false)}
                      size="sm"
                      className="px-2 py-0 h-5 text-[9px] uppercase font-bold"
                    >
                      Combined
                    </Button>
                    <Button
                      variant={splitVisualization ? "default" : "ghost"}
                      onClick={() => setSplitVisualization(true)}
                      size="sm"
                      className="px-2 py-0 h-5 text-[9px] uppercase font-bold"
                    >
                      Split
                    </Button>
                  </div>
                </div>
              </div>
              <CardContent className="relative px-4 pt-0 pb-0 overflow-hidden">
                {!isDataReady && (isLoadingData || isLoadingBrokerData) && (
                  <div className="absolute inset-0 z-30 flex items-center justify-center bg-background rounded-lg">
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="w-6 h-6 animate-spin text-primary" />
                      <div className="text-xs text-muted-foreground">
                        Loading ticker and broker data...
                      </div>
                    </div>
                  </div>
                )}
                {isDataReady && candlestickData.length > 0 && (
                  <PriceChart candlestickData={candlestickData} className="h-[500px] min-h-[400px]" logicalRangeRef={chartLogicalRangeRef} />
                )}
                {!isLoadingData && !isLoadingBrokerData && candlestickData.length === 0 && (
                  <div className="flex items-center justify-center h-80 text-muted-foreground">
                    <div className="text-center">
                      <p>No price data available</p>
                      <p className="text-xs mt-2">Click Show button to load data</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Broker Inventory Chart */}
            <Card className="rounded-none overflow-hidden border-0 gap-0">
              <CardContent className="relative px-4 pt-4 pb-0 overflow-hidden">
                {!isDataReady && (isLoadingData || isLoadingBrokerData) && (
                  <div className="absolute inset-0 z-20 flex items-center justify-center bg-background rounded-lg">
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="w-6 h-6 animate-spin text-primary" />
                      <div className="text-xs text-muted-foreground">
                        Loading ticker and broker data...
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex flex-col lg:flex-row gap-4">
                  <div className="flex-1 relative">
                    {(isDataReady || isLoadingData || isLoadingBrokerData) && (
                      <InventoryChart
                        key="inventory-split"
                        inventoryData={inventoryData}
                        selectedBrokers={selectedBrokers}
                        displayBrokers={visibleBrokers}
                        topBuyers={top5BuyBrokers}
                        topSellers={top5SellBrokers}
                        topTektoks={topTekTokBrokers}
                        brokerSummaryData={brokerSummaryData}
                        brokerColorMap={brokerColorMap}
                        ticker={displayedTicker}
                        logicalRangeRef={chartLogicalRangeRef}
                        className="h-[500px] w-full relative"
                      />
                    )}
                    {selectedBrokers.length > 0 && !isLoadingData && inventoryData.length > 0 && visibleBrokers.length === 0 && (
                      <div className="flex items-center justify-center h-80 text-muted-foreground">
                        <div className="text-center">
                          <p>No visible brokers</p>
                          <p className="text-xs mt-2">Enable broker visibility in the legend to view chart</p>
                        </div>
                      </div>
                    )}
                    {selectedBrokers.length > 0 && !isLoadingData && inventoryData.length === 0 && isDataReady && (
                      <div className="flex items-center justify-center h-80 text-muted-foreground">
                        <div className="text-center">
                          <p>No inventory data available for selected brokers</p>
                          <p className="text-xs mt-2">Data may not be available for the selected date range</p>
                        </div>
                      </div>
                    )}
                    {selectedBrokers.length === 0 && (
                      <div className="flex items-center justify-center h-80 text-muted-foreground">
                        <div className="text-center">
                          <p>No brokers selected</p>
                          <p className="text-xs mt-2">Select brokers above to view cumulative net flow</p>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="w-full lg:w-[200px] lg:min-w-[200px] shrink-0 flex flex-col gap-2">
                    {brokerSelectionMode.top5buy && top5BuyBrokers.length > 0 && (
                      <BrokerLegend
                        title="Top 5 Buyers"
                        brokers={top5BuyBrokers}
                        colorReferenceBrokers={selectedBrokers}
                        brokerVisibility={brokerVisibility}
                        onToggleVisibility={handleToggleBrokerVisibility}
                        onRemoveBroker={isTrial ? undefined : removeBroker}
                        onRemoveAll={isTrial ? undefined : removeAllTop5Buy}
                        topBuyers={top5BuyBrokers}
                        topSellers={top5SellBrokers}
                        tektokBrokerNames={brokerNetStats.topTekToks.map(t => t.broker)}
                       topTektoks={brokerNetStats.topTekToks}
                        brokerColorMap={brokerColorMap}
                        brokerMetadata={tektokMetadata}
                      />
                    )}
                    {brokerSelectionMode.top5sell && top5SellBrokers.length > 0 && (
                      <BrokerLegend
                        title="Top 5 Sellers"
                        brokers={top5SellBrokers}
                        colorReferenceBrokers={selectedBrokers}
                        brokerVisibility={brokerVisibility}
                        onToggleVisibility={handleToggleBrokerVisibility}
                        onRemoveBroker={isTrial ? undefined : removeBroker}
                        onRemoveAll={isTrial ? undefined : removeAllTop5Sell}
                        topBuyers={top5BuyBrokers}
                        topSellers={top5SellBrokers}
                        tektokBrokerNames={brokerNetStats.topTekToks.map(t => t.broker)}
                       topTektoks={brokerNetStats.topTekToks}
                        brokerColorMap={brokerColorMap}
                        brokerMetadata={tektokMetadata}
                      />
                    )}
                    {brokerSelectionMode.top5tektok && topTekTokBrokers.length > 0 && (
                      <BrokerLegend
                        title="Top Tektok"
                        brokers={topTekTokBrokers}
                        colorReferenceBrokers={selectedBrokers}
                        brokerVisibility={brokerVisibility}
                        onToggleVisibility={handleToggleBrokerVisibility}
                        onRemoveBroker={isTrial ? undefined : removeBroker}
                        onRemoveAll={isTrial ? undefined : removeAllTopTekTok}
                        topBuyers={top5BuyBrokers}
                        topSellers={top5SellBrokers}
                        topTektoks={brokerNetStats.topTekToks}
                        brokerColorMap={brokerColorMap}
                        brokerMetadata={tektokMetadata}
                      />
                    )}
                    {brokerSelectionMode.custom && customBrokers.length > 0 && (
                      <BrokerLegend
                        title="Selected Brokers"
                        brokers={customBrokers}
                        colorReferenceBrokers={selectedBrokers}
                        brokerVisibility={brokerVisibility}
                        onToggleVisibility={handleToggleBrokerVisibility}
                        onRemoveBroker={isTrial ? undefined : removeBroker}
                        onRemoveAll={isTrial ? undefined : removeAllCustom}
                        topBuyers={top5BuyBrokers}
                        topSellers={top5SellBrokers}
                        tektokBrokerNames={brokerNetStats.topTekToks.map(t => t.broker)}
                       topTektoks={brokerNetStats.topTekToks}
                        brokerColorMap={brokerColorMap}
                        brokerMetadata={tektokMetadata}
                      />
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Volume Chart */}
            <Card className="rounded-none overflow-hidden border-0 gap-0">
              <div className="bg-muted/50 px-4 py-1.5">
                <h3 className="font-semibold text-sm uppercase tracking-wider text-foreground/90">Volume - {displayedTicker}</h3>
              </div>
              <CardContent className="relative px-4 pt-0 pb-0 overflow-hidden">
                {!isDataReady && (isLoadingData || isLoadingBrokerData) && (
                  <div className="absolute inset-0 z-30 flex items-center justify-center bg-background rounded-lg">
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="w-6 h-6 animate-spin text-primary" />
                      <div className="text-xs text-muted-foreground">
                        Loading ticker and broker data...
                      </div>
                    </div>
                  </div>
                )}
                {isDataReady && volumeDataForCharts.length > 0 && (
                  <VolumeChart volumeData={volumeDataForCharts} candlestickData={candlestickData} showLabel={true} logicalRangeRef={chartLogicalRangeRef} />
                )}
                {!isLoadingData && !isLoadingBrokerData && volumeDataForCharts.length === 0 && (
                  <div className="flex items-center justify-center h-80 text-muted-foreground">
                    <div className="text-center">
                      <p>No volume data available</p>
                      <p className="text-xs mt-2">Click Show button to load data</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        ) : (
          // Combined View - Original Layout (only render when isDataReady is true)
          <>
            {/* Main TradingView Chart */}
            <Card className="flex-1 flex flex-col rounded-none overflow-hidden border-0 gap-0">
              <div className="bg-muted/50 px-4 py-1.5 flex flex-wrap items-center justify-between gap-1">
                <div className="flex items-center">
                  <h3 className="font-semibold text-sm uppercase tracking-wider text-foreground/90">Inventory Analysis{displayedTicker ? ` - ${displayedTicker}` : ''}</h3>
                </div>
                <div className="flex items-center gap-1">
                  <label className="flex items-center gap-1.5 cursor-pointer select-none mr-4">
                    <input
                      type="checkbox"
                      checked={showTooltipDetails}
                      onChange={e => setShowTooltipDetails(e.target.checked)}
                      className="h-3 w-3 rounded-none border-border/70 bg-transparent accent-primary cursor-pointer"
                    />
                    <span className="text-[12px] font-bold text-muted-foreground uppercase whitespace-nowrap">
                      SHOW DETAILS
                    </span>
                  </label>
                  <span className="text-[12px] font-bold text-muted-foreground uppercase whitespace-nowrap">View:</span>
                  <div className="flex items-center gap-1 rounded-md p-0.5 bg-background/50 border border-border/50">
                    <Button
                      variant={!splitVisualization ? "default" : "ghost"}
                      onClick={() => setSplitVisualization(false)}
                      size="sm"
                      className="px-2 py-0 h-5 text-[9px] uppercase font-bold"
                    >
                      Combined
                    </Button>
                    <Button
                      variant={splitVisualization ? "default" : "ghost"}
                      onClick={() => setSplitVisualization(true)}
                      size="sm"
                      className="px-2 py-0 h-5 text-[9px] uppercase font-bold"
                    >
                      Split
                    </Button>
                  </div>
                </div>
              </div>
              <CardContent className="flex-1 relative px-4 pt-0 pb-0 overflow-hidden flex flex-col">
                {!isDataReady && (isLoadingData || isLoadingBrokerData) && (
                  <div className="absolute inset-0 z-30 flex items-center justify-center bg-background rounded-lg">
                    <div className="flex flex-col items-center gap-3">
                      <Loader2 className="w-8 h-8 animate-spin text-primary" />
                      <div className="text-sm text-muted-foreground">
                        Loading ticker and broker data...
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex-1 flex flex-col lg:flex-row gap-4 min-h-0">
                  <div className="flex-1 relative flex flex-col">

                    {(dataError || brokerDataError) && !isLoadingData && !isLoadingBrokerData && (
                      <div className="absolute inset-0 z-30 flex items-center justify-center bg-background rounded-lg">
                        <div className="flex flex-col items-center gap-3 text-center p-6 max-w-md">
                          <div className="text-4xl">âš ï¸</div>
                          <div className="text-sm text-muted-foreground">
                            {dataError || brokerDataError}
                          </div>
                          <p className="text-xs text-muted-foreground mt-2">
                            Tip: Try adjusting the date range. Data may not be available for all selected dates.
                          </p>
                          <Button
                            onClick={() => window.location.reload()}
                            variant="outline"
                            size="sm"
                          >
                            Retry
                          </Button>
                        </div>
                      </div>
                    )}

                    {isDataReady && selectedBrokers.length === 0 && !isLoadingData && (
                      <div className="flex items-center justify-center h-full text-muted-foreground">
                        <div className="text-center">
                          <p>No brokers selected</p>
                          <p className="text-xs mt-2">Select brokers above to view chart</p>
                        </div>
                      </div>
                    )}

                    {isDataReady && selectedBrokers.length > 0 && visibleBrokers.length === 0 && !isLoadingData && (
                      <div className="flex items-center justify-center h-full text-muted-foreground">
                        <div className="text-center">
                          <p>No visible brokers</p>
                          <p className="text-xs mt-2">Enable broker visibility in the legend to view chart</p>
                        </div>
                      </div>
                    )}

                    {(isDataReady || isLoadingData || isLoadingBrokerData) && (
                      <TradingViewChart
                        key="tradingview-main"
                        candlestickData={candlestickData}
                        inventoryData={inventoryData}
                        selectedBrokers={selectedBrokers}
                        displayBrokers={visibleBrokers}
                        title="Inventory Analysis"
                        volumeData={volumeDataForCharts}
                        ticker={displayedTicker}
                        className="h-full w-full relative"
                        topBuyers={top5BuyBrokers}
                        topSellers={top5SellBrokers}
                        topTektoks={topTekTokBrokerNames}
                        showDetails={showTooltipDetails}
                        brokerSummaryData={brokerSummaryData}
                        brokerColorMap={brokerColorMap}
                        logicalRangeRef={chartLogicalRangeRef}
                      />
                    )}
                  </div>
                  <div className="w-fit shrink-0 flex flex-col gap-2 min-h-0">
                    {brokerSelectionMode.top5buy && top5BuyBrokers.length > 0 && (
                      <BrokerLegend
                        title="Top 5 Buyers"
                        brokers={top5BuyBrokers}
                        colorReferenceBrokers={selectedBrokers}
                        brokerVisibility={brokerVisibility}
                        onToggleVisibility={handleToggleBrokerVisibility}
                        onRemoveBroker={isTrial ? undefined : removeBroker}
                        onRemoveAll={isTrial ? undefined : removeAllTop5Buy}
                        topBuyers={top5BuyBrokers}
                        topSellers={top5SellBrokers}
                        tektokBrokerNames={brokerNetStats.topTekToks.map(t => t.broker)}
                       topTektoks={brokerNetStats.topTekToks}
                        brokerColorMap={brokerColorMap}
                        brokerMetadata={tektokMetadata}
                      />
                    )}
                    {brokerSelectionMode.top5sell && top5SellBrokers.length > 0 && (
                      <BrokerLegend
                        title="Top 5 Sellers"
                        brokers={top5SellBrokers}
                        colorReferenceBrokers={selectedBrokers}
                        brokerVisibility={brokerVisibility}
                        onToggleVisibility={handleToggleBrokerVisibility}
                        onRemoveBroker={isTrial ? undefined : removeBroker}
                        onRemoveAll={isTrial ? undefined : removeAllTop5Sell}
                        topBuyers={top5BuyBrokers}
                        topSellers={top5SellBrokers}
                        tektokBrokerNames={brokerNetStats.topTekToks.map(t => t.broker)}
                       topTektoks={brokerNetStats.topTekToks}
                        brokerColorMap={brokerColorMap}
                        brokerMetadata={tektokMetadata}
                      />
                    )}
                    {brokerSelectionMode.top5tektok && topTekTokBrokers.length > 0 && (
                      <BrokerLegend
                        title="Top Tektok"
                        brokers={topTekTokBrokers}
                        colorReferenceBrokers={selectedBrokers}
                        brokerVisibility={brokerVisibility}
                        onToggleVisibility={handleToggleBrokerVisibility}
                        onRemoveBroker={isTrial ? undefined : removeBroker}
                        onRemoveAll={isTrial ? undefined : removeAllTopTekTok}
                        topBuyers={top5BuyBrokers}
                        topSellers={top5SellBrokers}
                        topTektoks={brokerNetStats.topTekToks}
                        brokerColorMap={brokerColorMap}
                        brokerMetadata={tektokMetadata}
                      />
                    )}
                    {brokerSelectionMode.custom && customBrokers.length > 0 && (
                      <BrokerLegend
                        title="Selected Brokers"
                        brokers={customBrokers}
                        colorReferenceBrokers={selectedBrokers}
                        brokerVisibility={brokerVisibility}
                        onToggleVisibility={handleToggleBrokerVisibility}
                        onRemoveBroker={isTrial ? undefined : removeBroker}
                        onRemoveAll={isTrial ? undefined : removeAllCustom}
                        topBuyers={top5BuyBrokers}
                        topSellers={top5SellBrokers}
                        tektokBrokerNames={brokerNetStats.topTekToks.map(t => t.broker)}
                       topTektoks={brokerNetStats.topTekToks}
                        brokerColorMap={brokerColorMap}
                        brokerMetadata={tektokMetadata}
                      />
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

          </>
        )}
      </div>
      )}
    </div>
  );
});
